<template>
  <section id="createOrder">
    <!-- Collapsed locked-image pill — teleports to the rail ABOVE the cart
         pill (App.vue) when the locked image is parked via the → toggle.
         Declared at the always-rendered root so it shows whether the locked
         image is the machine OR the assembly. Click to restore. -->
    <portal to="locked-image-pill"
            v-if="(inPlaceZoomLocked.machine || inPlaceZoomLocked.assembly) && imageCollapsed">
      <div class="locked-image-pill"
           role="button" tabindex="0"
           @click="expandLockedImage"
           @keydown.enter.space.prevent="expandLockedImage"
           title="Show locked image"
           aria-label="Show locked image">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             aria-hidden="true">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21 15 16 10 5 21"/>
        </svg>
      </div>
    </portal>
    <div class="container">
      <div class="row create-order-row">
        <div class="col-12 mb-4 mb-lg-0">
          <!-- No company selected: hide the Search-for-Part form (company /
               machine / part picker) and show the shared select-a-company
               prompt instead. A part search is meaningless without a company,
               so there's nothing to show until one is chosen. -->
          <select-company-prompt
            v-if="!companySelected"
            :user-name="currentUser && currentUser.firstName ? currentUser.firstName : 'Admin'" />
          <b-form v-if="companySelected" id="orderForm" class="row create-order-form" @submit.prevent="createOrder">
            <!-- ===== HEADER =====
                 Title + brief value prop. Tells users what they
                 can do here without making them parse the form
                 first. -->
            <div class="col-12">
              <h1 class="create-order-title">Search for Part</h1>
            </div>

            <!-- ===== OFF-ACCOUNT HEADS-UP BANNER =====
                 Non-blocking notice shown when the part the user
                 searched for lives on a machine that isn't set up
                 on their company's account (the machine matcher in
                 applyPendingHeaderMachine found no company machine
                 by name). Ordering is NOT disabled — this is purely
                 informational with a contact-us nudge. Dismissible
                 via the ×. Amber palette matches the in-page
                 wayfinding notice (.inline-wear-more-hint). -->
            <div class="col-12"
                 v-if="headerOffAccount">
              <div class="header-offaccount-banner" role="status">
                <span class="header-offaccount-icon" aria-hidden="true">!</span>
                <span class="header-offaccount-text">
                  Heads up —
                  <strong>{{ headerOffAccountCode }}</strong>
                  is on <strong>{{ headerOffAccountMachine }}</strong>,
                  which isn't set up on your account — so it can't be
                  added to an order yet.
                  <a href="#" @click.prevent="openPartsInquiryModal('machine')">Contact us</a>
                  to add the machine to your account, then you can order
                  its parts.
                </span>
                <button type="button"
                        class="header-offaccount-close"
                        aria-label="Dismiss"
                        @click="headerOffAccount = false">
                  ×
                </button>
              </div>
            </div>

            <!-- ===== STEP 1: ORDER CONTEXT =====
                 Company + PO Number. The two pieces of info that
                 every order needs regardless of which path the
                 user takes (frequently ordered, machine browse,
                 or new equipment). -->
            <!-- ===== STEP 1: ORDER CONTEXT =====
                 Company is now the only required up-front
                 input. PO Number is captured per-part via the
                 picker modal when the user clicks ADD —
                 matching how Frequently Ordered works. This
                 means a single cart can have multiple POs
                 across different parts without forcing the
                 user to commit to a single PO at the start. -->
            <!-- ===== COMPLETED STEPS NAV =====
                 Steps the user has finished (or started) collapse into
                 clickable numbered circles here; the active step stays
                 expanded below. Clicking a circle re-expands that step. -->
            <div class="col-12 completed-steps-row" v-if="stepCircles.length">
              <span class="completed-steps-label">Steps:</span>
              <button v-for="n in stepCircles" :key="'csnav-' + n"
                      type="button"
                      class="completed-step-circle"
                      :class="isStepDone(n) ? 'is-done' : 'is-pending'"
                      :title="circleTitle(n)"
                      @click="goToStep(n)">{{ n }}</button>
              <!-- "Show wear parts only" toggle removed per request: ALL parts now
                   show by default (configuredWear defaults false). Narrowing is done
                   by the "Show discounted wear parts only" filter beside the part filter. -->
            </div>

            <div class="col-12 form-step" v-show="currentStep === 1">
              <div class="form-step-header">
                <h3 class="form-step-title">Choose Company</h3>
              </div>

              <div class="row form-step-body">
                <div class="col-12">
                  <label for="CompanySelect">Company</label>
                  <div
                    :class="[
  'ui fluid search selection dropdown',
  { disabled: true }
]"
                    id="CompanySelect"
                  >
                    <input
                      type="hidden"
                      name="company"
                      v-model="companyId"
                      id="company"
                    />
                    <div class="default text">Select your company</div>
                    <i class="dropdown icon"></i>
                    <div class="menu">
                      <div
                        class="item"
                        v-for="company in companies"
                        :key="company.value"
                        v-bind:data-value="company.value"
                      >
                        {{ company.text }}
                      </div>
                    </div>
                  </div>

                  <div class="form-helper-text" v-if="disabled">
                    Locked while a cart is active.
                    <a href="/cart">Review</a> or
                    <a href="#" @click.prevent="showDeleteModal()">clear it</a>
                    to switch.
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== STEP 2: FIND YOUR MACHINE =====
                 Just the machine select + optional machine
                 image. The wear-parts toggle moved up into
                 Step 3's header so it lives next to the part
                 list it filters. -->
            <div class="col-12 form-step" v-show="currentStep === 2">
              <div class="form-step-header">
                <h3 class="form-step-title">Find Your Machine</h3>
              </div>

              <div class="row form-step-body">
                <div class="col-12">
                  <label for="MachineSelect">Machine</label>
                  <div
                    class="ui fluid search selection dropdown"
                    :class="[{ error: machineError }]"
                    id="MachineSelect"
                  >
                    <input
                      type="hidden"
                      name="machine"
                      v-model="machineId"
                      id="Machine"
                      @change="onMachineChange"
                      required
                    />
                    <div class="default text">Select your machine</div>
                    <i class="dropdown icon"></i>
                    <div class="menu">
                      <div
                        class="item"
                        v-for="machine in machines"
                        :key="machine.value"
                        v-bind:data-value="machine.value"
                        required
                      >
                        {{ machine.text }}
                      </div>
                      <!-- ===== "Don't see your machine?" footer =====
                           Sits below the data items as a non-selectable
                           help block. Both buttons (call + email) live
                           here so they're discoverable in context — the
                           moment a user can't find their machine, the
                           help is one click away.

                           data-no-select on the wrapper isn't a Semantic
                           UI native attribute, but combined with
                           click.stop on each interactive child it stops
                           the dropdown from absorbing the click as a
                           value-pick. -->
                      <div class="dropdown-footer-help" data-no-select="true"
                           @click.stop>
                        <div class="dropdown-footer-divider"></div>
                        <div class="dropdown-footer-content">
                          <div class="dropdown-footer-title">
                            <span class="dropdown-footer-icon">?</span>
                            <strong>Don't see your machine?</strong>
                          </div>
                          <p class="dropdown-footer-blurb">
                            Ordering for new or recently-purchased equipment that
                            isn't on your account yet? Our parts team can help.
                          </p>
                          <div class="dropdown-footer-actions">
                            <button type="button"
                                    class="btn btn-sm dropdown-footer-action"
                                    @click.stop.prevent="openPartsInquiryModal('machine')">
                              Click Here to Email Parts Team -OR- call us at 1-800-278-3353
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-12 mt-3"
                     v-if="machineId && (machineImage || machineImageLoading)">
                  <!-- Machine image — only renders if the
                       machine actually has an imagePath on
                       file. While the lookup is in flight,
                       show a spinner instead of jumping the
                       layout. -->
                  <portal to="locked-image-panel" :disabled="!(inPlaceZoomLocked.machine && !imageCollapsed)">
                  <div class="machine-image-wrap"
                       :class="{ 'is-zoom-locked': inPlaceZoomLocked.machine, 'lock-collapsed': inPlaceZoomLocked.machine && imageCollapsed }"
                       :style="{
                         '--lock-top': lockedStickyTop + 'px',
                         '--lock-left': lockedStickyLeft + 'px',
                         '--lock-width': lockedStickyWidth + 'px'
                       }"
                       @mousedown="onLockedMouseDown('machine', $event)">
                    <div v-if="inPlaceZoomLocked.machine" class="locked-toolbar">
                      <button class="locked-close-btn"
                              type="button"
                              title="Close locked image"
                              @click="onCloseLock('machine')"
                              @mousedown.stop>×</button>
                      <button class="locked-collapse-btn"
                              type="button"
                              title="Collapse image to the side"
                              @click="collapseLockedImage('machine')"
                              @mousedown.stop>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
                             aria-hidden="true"><polyline points="9 6 15 12 9 18"/></svg>
                      </button>
                    </div>
                    <div v-if="machineImageLoading" class="machine-image-loader">
                      <b-spinner small variant="secondary"></b-spinner>
                      <span>Loading image…</span>
                    </div>
                    <img
                      v-else-if="machineImage"
                      :src="machineImage"
                      class="machine-preview-img"
                      :style="{ transformOrigin: inPlaceZoomOrigin.machine, transform: inPlaceZoomLocked.machine ? ('scale(' + lockedZoom + ')') : null }"
                      @mousemove="inPlaceZoomMove('machine', $event)"
                      @wheel="onLockedWheel('machine', $event)"
                      @click="onImageClick('machine', machineImage)"
                      @dblclick.stop.prevent="openImageModal(machineImage)"
                      :title="inPlaceZoomLocked.machine
                        ? 'Drag to pan inside · Double-click + drag to move panel · Click × to close'
                        : 'Hover to zoom · Single-click to lock zoom in place · Double-click to open in popup'"
                      draggable="false"
                      alt="Selected machine"
                    />
                    <div v-if="inPlaceZoomLocked.machine" class="locked-zoom-controls">
                      <button type="button" class="locked-zoom-btn" title="Zoom out"
                              @click.stop="lockedZoomOut" @mousedown.stop>&minus;</button>
                      <button type="button" class="locked-zoom-btn" title="Zoom in"
                              @click.stop="lockedZoomIn" @mousedown.stop>+</button>
                    </div>
                  </div>
                  </portal>
                </div>
              </div>
            </div>

            <!-- ===== STEP 3: FIND YOUR PART =====
                 Renders only when a machine is selected.
                 Replaces the old navigation to
                 /orders/shopwearparts.

                 Header carries the wear-parts toggle on the
                 right — moved up from Step 2 so the filter
                 lives next to the list it actually filters.

                 Two-step inline UX:
                   1. Pick an assembly from the dropdown
                      (populated from
                      getSelectItemsByCompanyMachine).
                   2. Sub-assemblies + wear parts accordion
                      loads for that assembly via
                      getAssembliesWithWearParts.

                 ADD on each part feeds addToCartClick — the
                 same gate Frequently Ordered uses — so the
                 combined Add-to-Cart modal, dual staging tray,
                 debounced cart sync, and header counter emit
                 all work without new plumbing. Destination + PO
                 + quantity are captured per-add so users can
                 mix multiple POs (and order/quote destinations)
                 in one session. -->
            <div class="col-12 form-step"
                 v-if="showInlineWearParts"
                 v-show="currentStep === 3">
              <div class="form-step-header">
                <h3 class="form-step-title">Find Your Part</h3>
              </div>

              <div class="form-step-body inline-wear-stepbody">
                <!-- Use v-show rather than v-if here. v-if
                     would toggle this <p> in/out of the DOM
                     when the user flips the wear-parts
                     checkbox — and because the surrounding
                     siblings (incl. two divs that share the
                     class .inline-wear-picker) aren't keyed,
                     Vue's diff can patch the assembly
                     dropdown's DOM node into a different
                     position, blowing away Semantic UI's
                     event bindings on it. v-show keeps the
                     node in place and just hides it via CSS,
                     so the diff sees no structural change. -->
                <!-- Assembly picker — driven by
                     wearAssemblyOptions. Built as a Semantic
                     UI dropdown (rather than a native
                     <select>) so the "Don't see your
                     assembly?" help footer can live INSIDE
                     the menu — matching the Machine
                     dropdown's pattern exactly. The previous
                     native-<select> implementation couldn't
                     host arbitrary HTML inside <option>
                     elements, so the help block had to live
                     below it; now it sits flush at the
                     bottom of the open menu like users
                     expect.

                     Explicit `key` here disambiguates this
                     picker from the sub-assembly picker
                     below — both share the class
                     .inline-wear-picker, and unkeyed Vue
                     diffs can confuse them. -->
                <div class="inline-wear-picker-row">
                <div class="inline-wear-picker"
                     key="assembly-picker">
                  <label for="WearAssemblySelect"
                         class="inline-wear-picker-label">
                    Choose an Assembly
                  </label>
                  <div class="ui fluid search selection dropdown inline-wear-assembly-dropdown"
                       :class="{ disabled: wearAssembliesLoading }"
                       id="WearAssemblySelect">
                    <input type="hidden"
                           name="wearAssembly"
                           id="WearAssembly"
                           v-model="selectedWearAssemblyId"
                           @change="onWearAssemblyChange" />
                    <div class="default text">
                      {{ wearAssembliesLoading ? 'Loading assemblies…' : '— Select an assembly —' }}
                    </div>
                    <i class="dropdown icon"></i>
                    <div class="menu">
                      <!-- data-text keeps Semantic's SELECTED display text clean (just the
                           name), even though each item also carries an at-a-glance drawing
                           icon: a picture glyph when the assembly has a drawing, a faded one
                           when it doesn't — so the customer knows before selecting. -->
                      <div
                        class="item"
                        v-for="opt in wearAssemblyOptions"
                        :key="opt.value"
                        v-bind:data-value="opt.value"
                        v-bind:data-text="opt.text">
                        <span class="wear-asm-opt-text">{{ opt.text }}</span>
                        <span class="wear-asm-drawing-ico"
                              :class="opt.image ? 'has-drawing' : 'no-drawing'"
                              :title="opt.image ? 'Has a parts drawing' : 'No parts drawing'"
                              aria-hidden="true">🖼</span>
                      </div>
                      <!-- ===== "Don't see your assembly?" footer =====
                           Mirrors the Machine dropdown's
                           footer. Sits below the data items
                           as a non-selectable help block.
                           data-no-select + click.stop on the
                           interactive children stops Semantic
                           UI from absorbing the click as a
                           value-pick. Only renders once the
                           assembly list has finished loading
                           AND has at least one item — the
                           empty state below the picker handles
                           the "no assemblies on this machine"
                           case separately. -->
                      <div class="dropdown-footer-help" data-no-select="true"
                           v-if="!wearAssembliesLoading && wearAssemblyOptions.length > 0"
                           @click.stop>
                        <div class="dropdown-footer-divider"></div>
                        <div class="dropdown-footer-content">
                          <div class="dropdown-footer-title">
                            <span class="dropdown-footer-icon">?</span>
                            <strong>Don't see your assembly?</strong>
                          </div>
                          <p class="dropdown-footer-blurb">
                            Looking for an assembly that isn't on this machine
                            yet? Our parts team can help.
                          </p>
                          <div class="dropdown-footer-actions">
                            <button type="button"
                                    class="btn btn-sm dropdown-footer-action"
                                    @click.stop.prevent="openPartsInquiryModal('assembly')">
                              Click Here to Email Parts Team -OR- call us at 1-800-278-3353
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Sub-Assembly picker — 2nd column of the picker row.
                     Filters the accordion below to a single sub-assembly.
                     Options come from wearAssemblies (each entry is itself
                     a sub-assembly). Picking one auto-expands its card so
                     the user goes straight to the parts. Only appears once
                     an assembly is picked; until then "Choose an Assembly"
                     fills the row. -->
                <div class="inline-wear-picker"
                     key="subassembly-picker"
                     v-if="selectedWearAssemblyId && !wearPartsLoading && wearAssemblies.length > 0">
                  <label for="inline-wear-subassembly"
                         class="inline-wear-picker-label">
                    Jump to Sub-Assembly
                  </label>
                  <select id="inline-wear-subassembly"
                          class="inline-wear-picker-select"
                          v-model="selectedSubAssemblyId">
                    <option :value="null">— All sub-assemblies —</option>
                    <option v-for="sa in wearAssemblies"
                            :key="'sa-opt-' + sa.assemblyId"
                            :value="sa.assemblyId">
                      {{ sa.assemblyName }}
                    </option>
                  </select>
                </div>
                </div>

                <!-- Two-column row mirroring the assembly/sub-assembly picker row
                     above: col 1 = the part filter (same width as "Choose an
                     Assembly"); col 2 = the "Show discounted wear parts only" toggle,
                     left-aligned under "Jump to Sub-Assembly" (off by default). -->
                <div class="part-filter-row" v-if="showWearPartsList">
                  <div class="part-filter-wrap part-filter-wrap-block">
                    <span class="part-filter-icon" aria-hidden="true">🔍</span>
                    <input type="text"
                           class="part-filter-input"
                           v-model="partFilter"
                           placeholder="Filter by name, part code, item #, or price…"
                           aria-label="Filter parts by name, code, item, or price" />
                    <button v-if="partFilter"
                            type="button"
                            class="part-filter-clear"
                            @click="partFilter = ''"
                            title="Clear filter">×</button>
                  </div>
                  <div class="part-filter-discount-col">
                    <b-form-checkbox
                      id="discountedOnly"
                      v-model="showDiscountedOnly"
                      name="discountedOnly"
                      :value="true"
                      :unchecked-value="false"
                      class="discount-only-toggle"
                    >
                      <span class="discount-only-label">Show discounted wear parts only</span>
                    </b-form-checkbox>
                  </div>
                </div>

                <!-- Assembly image — same pattern as Step 2:
                     spinner while loading, omitted entirely
                     when the assembly has no image on file. -->
                <portal to="locked-image-panel" :disabled="!(inPlaceZoomLocked.assembly && !imageCollapsed)">
                <div class="machine-image-wrap"
                     :class="{ 'is-zoom-locked': inPlaceZoomLocked.assembly, 'lock-collapsed': inPlaceZoomLocked.assembly && imageCollapsed }"
                     :style="{
                       '--lock-top': lockedStickyTop + 'px',
                       '--lock-left': lockedStickyLeft + 'px',
                       '--lock-width': lockedStickyWidth + 'px'
                     }"
                     @mousedown="onLockedMouseDown('assembly', $event)"
                     v-if="selectedWearAssemblyId && (assemblyImage || assemblyImageLoading)">
                  <div v-if="inPlaceZoomLocked.assembly" class="locked-toolbar">
                    <button class="locked-close-btn"
                            type="button"
                            title="Close locked image"
                            @click="onCloseLock('assembly')"
                            @mousedown.stop>×</button>
                    <button class="locked-collapse-btn"
                            type="button"
                            title="Collapse image to the side"
                            @click="collapseLockedImage('assembly')"
                            @mousedown.stop>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
                           aria-hidden="true"><polyline points="9 6 15 12 9 18"/></svg>
                    </button>
                  </div>
                  <div v-if="assemblyImageLoading" class="machine-image-loader">
                    <b-spinner small variant="secondary"></b-spinner>
                    <span>Loading image…</span>
                  </div>
                  <img
                    v-else-if="assemblyImage"
                    :src="assemblyImage"
                    ref="assemblyImg"
                    @load="onAssemblyImgLoad"
                    class="machine-preview-img"
                    :style="{ transformOrigin: inPlaceZoomOrigin.assembly, transform: inPlaceZoomLocked.assembly ? ('scale(' + lockedZoom + ')') : null }"
                    @mousemove="inPlaceZoomMove('assembly', $event)"
                    @wheel="onLockedWheel('assembly', $event)"
                    @click="onImageClick('assembly', assemblyImage)"
                    @dblclick.stop.prevent="openImageModal(assemblyImage)"
                    :title="inPlaceZoomLocked.assembly
                      ? 'Drag to pan inside · Double-click + drag to move panel · Click × to close'
                      : 'Hover to zoom · Single-click to lock zoom in place · Double-click to open in popup'"
                    draggable="false"
                    alt="Selected assembly"
                  />
                  <!-- Clickable balloon hotspots (pre-computed OCR map). The
                       layer is pointer-events:none and mirrors the img's box +
                       zoom transform; only the small hotspots capture clicks,
                       so hover-zoom on the rest of the image still works. -->
                  <div v-if="dockedBalloons.items.length"
                       class="callout-hotspot-layer"
                       :class="{ 'is-zoom-locked': inPlaceZoomLocked.assembly }"
                       :style="calloutLayerStyle()">
                    <button v-for="(b, bi) in dockedBalloons.items"
                            :key="'cb-' + bi + '-' + b.code"
                            type="button"
                            class="callout-hotspot"
                            :style="calloutHotspotStyle(b)"
                            :title="b.partCode ? ('Go to ' + b.partCode + ' (item ' + b.code + ')') : ('Show item ' + b.code)"
                            @click.stop.prevent="onBalloonClick(b)"
                            @mousedown.stop
                            @dblclick.stop.prevent></button>
                  </div>
                  <div v-if="inPlaceZoomLocked.assembly" class="locked-zoom-controls">
                    <button type="button" class="locked-zoom-btn" title="Zoom out"
                            @click.stop="lockedZoomOut" @mousedown.stop>&minus;</button>
                    <button type="button" class="locked-zoom-btn" title="Zoom in"
                            @click.stop="lockedZoomIn" @mousedown.stop>+</button>
                  </div>
                </div>
                </portal>

                <!-- (Sub-Assembly picker moved up into the picker row,
                     beside "Choose an Assembly".) -->

                <!-- States: empty picker, parts loading,
                     parts empty for this assembly, or the
                     accordion. -->
                <div class="inline-wear-empty"
                     v-if="!wearAssembliesLoading && wearAssemblyOptions.length === 0">
                  No assemblies found for this machine.
                </div>

                <div class="inline-wear-loader"
                     v-else-if="wearPartsLoading">
                  <b-spinner small variant="secondary"></b-spinner>
                  <span>Loading parts…</span>
                </div>

                <div class="inline-wear-empty"
                     v-else-if="showWearPartsList && wearAssemblies.length === 0">
                  No parts in this assembly.
                </div>

                <div class="inline-wear-list"
                     v-else-if="showWearPartsList">
                  <template v-for="(asm, idx) in displayedSubAssemblies">
                  <div class="inline-wear-card"
                       v-show="!partsFilterActive || filteredParts(asm).length"
                       :key="'inline-wear-' + asm.assemblyId">
                    <button type="button"
                            class="inline-wear-card-header"
                            @click="toggleInlineWearAssembly(asm.assemblyId)">
                      <span class="inline-wear-chevron"
                            :class="{ 'is-expanded': isInlineWearAssemblyExpanded(asm.assemblyId) }"
                            aria-hidden="true">
                        {{ isInlineWearAssemblyExpanded(asm.assemblyId) ? '−' : '+' }}
                      </span>
                      <span class="inline-wear-card-name">
                        {{ asm.assemblyName }}
                      </span>
                      <span class="inline-wear-card-count">
                        {{ filteredParts(asm).length }}
                        {{ filteredParts(asm).length === 1 ? 'part' : 'parts' }}
                      </span>
                    </button>
                    <ul class="inline-wear-parts"
                        v-show="isInlineWearAssemblyExpanded(asm.assemblyId) || partsFilterActive">
                      <li class="inline-wear-part"
                          :class="{ 'is-header-highlight': isHeaderHighlightPart(part) }"
                          :data-part-code="part.code"
                          :data-part-name="part.description"
                          :data-part-price="partPriceText(part)"
                          v-for="part in filteredParts(asm)"
                          :key="'inline-part-' + (part.partId || part.id)">
                        <div class="inline-wear-part-thumb"
                             :class="{ 'is-clickable': partThumb(part), 'is-crop': !!partCalloutFrac(part) }"
                             @click="dockDiagramForPart(part)"
                             :style="partCalloutFrac(part) ? partThumbCropStyle(part) : null"
                             :title="partThumb(part) ? 'Click to view this part on the assembly diagram' : null">
                          <!-- When OCR located this part's balloon, the thumb is a zoomed
                               CROP of the assembly drawing at that balloon (background via
                               partThumbCropStyle). Otherwise the whole drawing; if there's
                               no drawing at all, a generic placeholder. -->
                          <img v-if="partThumb(part) && !partCalloutFrac(part)"
                               :src="partThumb(part)"
                               :alt="(part.code || 'Part') + ' image'"
                               loading="lazy" />
                          <svg v-if="!partThumb(part)"
                               class="inline-wear-part-thumb-ph"
                               viewBox="0 0 24 24"
                               aria-hidden="true">
                            <path d="M12 2 3 7v10l9 5 9-5V7l-9-5zm0 2.31 6.5 3.61L12 11.5 5.5 7.92 12 4.31zM5 9.42l6 3.33v6.94l-6-3.33V9.42zm14 0v6.94l-6 3.33v-6.94l6-3.33z"/>
                          </svg>
                        </div>
                        <div class="inline-wear-part-main">
                          <div class="inline-wear-part-code-row">
                            <span class="inline-wear-part-code">
                              {{ part.code }}
                            </span>
                            <span class="inline-wear-discount-chip"
                                  v-if="isWearEligible(part)">
                              2% Discount
                            </span>
                          </div>
                          <div class="inline-wear-part-desc">
                            {{ part.description }}
                          </div>
                          <div class="inline-wear-part-price">
                            {{ part.price | currency }}
                            <span class="inline-wear-part-qpm"
                                  v-if="part.quantityPerMachine">
                              · QTY/MACH: {{ part.quantityPerMachine }}
                            </span>
                          </div>
                        </div>
                        <!-- Parts.Item (assembly callout/item no. e.g. 60A) — shown
                             between the part info and the Add button. Already returned
                             by the API (AssemblyPart.Item); only rendered when present. -->
                        <!-- Item # box. For admins it's CLICK-TO-EDIT in place: the
                             value becomes an input (the box design is kept) — type to
                             change it or blank it to remove. Saves on Enter/blur; Esc
                             cancels. Non-admins click it to show it on the diagram. -->
                        <div v-if="part.item || (isMasterUser && addItemFor === partDbId(part))"
                             class="inline-wear-part-item"
                             :class="{ 'is-editing': isMasterUser && addItemFor === partDbId(part), 'is-admin-edit': isMasterUser }"
                             :role="isMasterUser ? null : 'button'"
                             @click="onItemBoxClick(part)"
                             @dblclick.prevent="onItemBoxDblClick(part)"
                             :title="isMasterUser
                               ? 'Click to show on the diagram · double-click to edit the item # (admin)'
                               : ('Show item ' + part.item + ' on the assembly diagram')">
                          <span class="inline-wear-part-item-label">Item</span>
                          <input v-if="isMasterUser && addItemFor === partDbId(part)"
                                 ref="addItemInput" v-model="addItemCode"
                                 class="inline-wear-part-item-val item-val-input"
                                 maxlength="12" placeholder="(blank)"
                                 @click.stop
                                 @keydown.enter.prevent="saveAddItem(part)"
                                 @keydown.esc.prevent="cancelAddItem"
                                 @blur="saveAddItem(part)" />
                          <span v-else class="inline-wear-part-item-val">{{ part.item }}</span>
                          <span v-if="isMasterUser && addItemFor === partDbId(part) && addItemError"
                                class="additem-error" :title="addItemError">!</span>
                        </div>
                        <!-- No item # + admin: add one (saves to the catalog so the
                             part can link to its diagram balloon). -->
                        <div v-else-if="isMasterUser && partDbId(part)" class="inline-wear-additem-wrap">
                          <button type="button" class="inline-wear-part-additem"
                                  @click.stop="startAddItem(part)"
                                  title="Add an item # for this part (admin — saves to the catalog)">+</button>
                        </div>
                        <a href="#"
                           class="inline-wear-part-add"
                           :class="{
                             'is-selected': isPartSelected({
                               partId: part.partId || part.id,
                               partCode: part.code
                             }),
                             disabled: addingInsightPart
                           }"
                           @click.prevent="addInlineWearPart(part, asm)"
                           :title="isPartSelected({ partId: part.partId || part.id, partCode: part.code })
                                   ? 'In your cart — click to update it or also add it to an order/quote'
                                   : 'Add this part to your cart'">
                          <!-- Always a CART icon (no pencil) — clicking an in-cart part opens
                               the modal to update it OR also add it to the other destination.
                               The 'is-selected' style on the <a> shows the in-cart state. -->
                          <svg class="cart-icon-svg" xmlns="http://www.w3.org/2000/svg"
                               viewBox="0 0 576 512" width="15" height="15" aria-hidden="true">
                            <path fill="currentColor" d="M0 24C0 10.7 10.7 0 24 0H69.5c22 0 41.5 12.8 50.6 32h411c26.3 0 45.5 25 38.6 50.4l-41 152.3c-8.5 31.4-37 53.3-69.5 53.3H170.7l5.4 28.5c2.2 11.3 12.1 19.5 23.6 19.5H488c13.3 0 24 10.7 24 24s-10.7 24-24 24H199.7c-34.6 0-64.3-24.6-70.7-58.5L77.4 54.5c-.7-3.8-4-6.5-7.9-6.5H24C10.7 48 0 37.3 0 24zM128 464a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm336-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z"/>
                          </svg>
                        </a>
                      </li>
                    </ul>
                  </div>
                  <!-- Wayfinding hint: when there's more than
                       one sub-assembly, the first is expanded
                       and the rest are collapsed below. This
                       banner sits right after the first card
                       so users can't miss that there are more
                       categories to explore. -->
                  <div class="inline-wear-more-hint"
                       v-if="idx === 0 && (partsFilterActive ? filterHiddenCategories > 0 : displayedSubAssemblies.length > 1)"
                       :key="'hint-' + asm.assemblyId">
                    <template v-if="partsFilterActive">
                      There {{ filterHiddenCategories === 1 ? 'is' : 'are' }}
                      {{ filterHiddenCategories }}
                      {{ filterHiddenCategories === 1 ? 'category' : 'categories' }}
                      of parts below.
                      <a href="#" class="inline-wear-more-hint-clear"
                         @click.prevent="partFilter = ''; showDiscountedOnly = false">Click here</a>
                      to clear your filter and view.
                    </template>
                    <template v-else>
                      <span aria-hidden="true">↓</span>
                      {{ displayedSubAssemblies.length - 1 }}
                      more {{ displayedSubAssemblies.length - 1 === 1 ? 'category' : 'categories' }}
                      of parts below — click <strong>+</strong> on any header to expand
                    </template>
                  </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- The "Don't see your machine?" call-to-action
                 used to live here as a standalone yellow box.
                 It moved up into the machine dropdown's footer
                 so the help is presented in context — at the
                 moment the user discovers their machine isn't
                 listed, not at the bottom of the form. The
                 modal it opens is defined further down. -->

          </b-form>
        </div>
         </div>
      </div>

    <!-- ===== ADD TO CART MODAL =====
         One combined modal for the Search page. Replaces the
         poPickerModal + poExplainerModal pair from Create-Order /
         Create-RFQ. The user picks a destination (Order or Quote),
         enters a PO Number / Quote Reference, sets the quantity,
         and confirms — same modal flow whether they're queueing
         a brand-new part or editing one already in the tray.

         In edit mode, the modal is pre-filled with the part's
         current destination, PO, and qty, and a Remove link is
         shown in the footer. Cross-destination moves (Order ↔ Quote)
         aren't supported here — the user removes from one side and
         re-adds to the other. -->
    <b-modal id="addToCartModal"
             v-model="showAddToCartModal"
             title="Add to Cart"
             :ok-title="addToCartOkTitle"
             ok-variant="primary"
             cancel-title="Cancel"
             cancel-variant="outline-dark"
             :ok-disabled="!canConfirm"
             size="md"
             centered
             hide-header-close
             no-close-on-backdrop
             no-close-on-esc
             no-enforce-focus
             modal-class="add-to-cart-modal"
             @ok="onAddToCartConfirm"
             @cancel="onAddToCartCancel"
             @shown="onAddToCartShown">
      <div class="add-to-cart-body">
        <p class="add-to-cart-lede" v-if="pendingPart">
          {{ pickerMode === 'edit' ? 'Updating' : 'Adding' }}
          <strong class="add-to-cart-partcode">{{ pendingPart.partCode }}</strong>
          <span v-if="pendingPart.description">
            ({{ pendingPart.description }})
          </span>
          to your cart.
        </p>

        <!-- ===== DESTINATION TOGGLE =====
             Two pill buttons. Hidden in edit mode — moving a queued
             part between Order and Quote isn't supported in v1, so
             the toggle would be confusing.

             In add mode when there are existing staging items in
             EITHER queue, pendingDestination starts as null so the
             user must explicitly pick before the PO/Qty section
             reveals. When both queues are empty, addToCartClick
             pre-sets it to 'order' and the rest of the modal is
             visible immediately. -->
        <div>
          <label class="add-to-cart-section-label">Destination</label>
          <div class="add-to-cart-destination">
            <button type="button"
                    class="add-to-cart-dest-btn"
                    :class="{ 'is-active': pendingDestination === 'order' }"
                    @click="onPickDestination('order')">
              <span class="add-to-cart-dest-title">Order<span v-if="pendingInOrder" class="add-to-cart-dest-badge">✓ in cart</span></span>
              <span class="add-to-cart-dest-sub">{{ pendingInOrder ? 'Update your order line' : 'Add to your active order' }}</span>
            </button>
            <button type="button"
                    class="add-to-cart-dest-btn"
                    :class="{ 'is-active': pendingDestination === 'quote' }"
                    @click="onPickDestination('quote')">
              <span class="add-to-cart-dest-title">Quote<span v-if="pendingInQuote" class="add-to-cart-dest-badge">✓ in cart</span></span>
              <span class="add-to-cart-dest-sub">{{ pendingInQuote ? 'Update your quote line' : 'Add to a price quote' }}</span>
            </button>
          </div>
        </div>

        <!-- ===== EVERYTHING BELOW HIDES UNTIL DESTINATION IS PICKED =====
             Progressive disclosure: until pendingDestination has a
             value, the PO/Qty section stays hidden. This keeps the
             modal from asking for a PO before the user has decided
             whether they want to attach the part to an Order or a
             Quote — which they're choosing right above. -->
        <template v-if="pendingDestination">

          <!-- ===== EXISTING PO PICKER =====
               Add mode only. When the chosen destination already
               has staging groups, list them as actionable buttons
               so the user can attach the part to an existing PO
               with one click instead of retyping. The "+ New" button
               reveals the input for a brand-new PO Number. Edit mode
               skips this — the picker is purely for new adds. -->
          <template v-if="pickerMode !== 'edit' && currentDestinationGroups.length > 0">
            <label class="add-to-cart-section-label">
              {{ pendingDestination === 'quote'
                 ? 'Add to existing quote or start new'
                 : 'Add to existing order or start new' }}
            </label>
            <div class="add-to-cart-po-picker">
              <button type="button"
                      class="add-to-cart-po-pick-btn"
                      v-for="g in currentDestinationGroups"
                      :key="'pick-' + g.poNumber"
                      :class="{ 'is-active': !useNewPo && pendingPoNumber === g.poNumber }"
                      @click="onPickExistingPo(g.poNumber)">
                <span class="add-to-cart-po-pick-btn-num">
                  {{ pendingDestination === 'quote' ? 'Quote' : 'Order' }} #{{ g.orderNumber }}
                </span>
                <span class="add-to-cart-po-pick-btn-meta">
                  {{ pendingDestination === 'quote' ? 'Ref' : 'PO' }}:
                  <strong>{{ g.poNumber }}</strong>
                  · {{ g.parts.length }} {{ g.parts.length === 1 ? 'part' : 'parts' }}
                </span>
              </button>
              <button type="button"
                      class="add-to-cart-po-pick-btn add-to-cart-po-pick-btn-new"
                      :class="{ 'is-active': useNewPo }"
                      @click="onPickNewPo()">
                <span class="add-to-cart-po-pick-btn-num">
                  + New {{ pendingDestination === 'quote' ? 'Quote Reference' : 'PO Number' }}
                </span>
              </button>
            </div>
          </template>

          <!-- ===== PO NUMBER / QUOTE REFERENCE INPUT =====
               Visible when:
                 - edit mode (always — user is updating the existing PO)
                 - add mode AND no existing groups (first add of this destination)
                 - add mode AND user clicked "+ New" in the picker above
               Hidden when an existing PO is selected from the picker. -->
          <template v-if="pickerMode === 'edit' || currentDestinationGroups.length === 0 || useNewPo">
            <label for="addToCartPoInput" class="add-to-cart-section-label">
              {{ pendingDestination === 'quote' ? 'Quote Reference' : 'PO Number' }}
            </label>
            <input id="addToCartPoInput"
                   type="text"
                   v-model="pendingPoNumber"
                   :placeholder="pendingDestination === 'quote'
                     ? 'Enter Quote Reference'
                     : 'Enter PO Number'"
                   class="po-modal-input"
                   autocomplete="off"
                   @keyup.enter="onAddToCartConfirm" />
            <div class="add-to-cart-helper">
              Your reference for tracking this
              {{ pendingDestination === 'quote' ? 'quote' : 'order' }}.
            </div>
          </template>

          <!-- ===== QUANTITY ===== -->
          <label for="addToCartQtyInput" class="add-to-cart-section-label">
            Quantity
          </label>
          <input id="addToCartQtyInput"
                 type="number"
                 min="1"
                 step="1"
                 v-model.number="pendingQuantity"
                 class="po-modal-input add-to-cart-qty-input"
                 @keyup.enter="onAddToCartConfirm" />

          <!-- ===== REMOVE LINK (edit mode only) =====
               Replaces the old picker's Remove option. Pulls the part
               out of whichever queue currently holds it. -->
          <div class="add-to-cart-remove" v-if="pickerMode === 'edit'">
            <a href="#" @click.prevent="onAddToCartRemove">
              ✕ Remove this part from cart
            </a>
          </div>
        </template>
      </div>
    </b-modal>

    <!-- ===== MACHINE IMAGE ZOOM MODAL =====
         Same modal + zoom controls as ShopWearParts.vue.
         Mouse wheel zooms; click+drag pans when zoomed in. -->
    <div
      v-if="showImageModal"
      class="custom-image-modal"
      @click="closeImageModal"
    >
      <div class="custom-image-modal-inner" @click.stop>
        <button
          type="button"
          class="custom-image-close"
          @click="closeImageModal"
          aria-label="Close"
        >
          ×
        </button>

        <div
          class="diagram-zoom-wrap"
          :class="{ 'is-balloon-editing': balloonEditMode }"
          @wheel.prevent="handleWheel"
          @mousedown="startDrag"
          @click="onModalAddClick"
          @dblclick.stop.prevent="onModalDblClick"
        >
          <img
            :src="modalImage"
            ref="modalImg"
            class="imagepreview"
            :style="imageTransformStyle"
            @load="measureModalImg"
            @dragstart.prevent
          />
          <!-- Balloons in the zoom modal: clickable (normal) OR editable (admin
               edit mode). Only for the ASSEMBLY image; the layer mirrors the
               image's pan/zoom transform so markers track it at any zoom. -->
          <div v-if="(dockedBalloons.items.length || balloonEditMode) && modalImage === assemblyImage"
               class="callout-hotspot-layer modal-callout-layer"
               :class="{ 'is-editing': balloonEditMode }"
               :style="modalCalloutLayerStyle()">
            <template v-if="!balloonEditMode">
              <button v-for="(b, bi) in dockedBalloons.items"
                      :key="'mcb-' + bi + '-' + b.code"
                      type="button"
                      class="callout-hotspot"
                      :style="calloutHotspotStyle(b, modalImgBox)"
                      :title="b.partCode ? ('Go to ' + b.partCode + ' (item ' + b.code + ')') : ('Show item ' + b.code)"
                      @click.stop.prevent="onModalBalloonClick(b)"
                      @mousedown.stop></button>
            </template>
            <template v-else>
              <!-- Every bubble is editable: drag to move, the corner handle to
                   resize, select then rename (code field in the bar) or delete. -->
              <div v-for="(b, ai) in editBalloons" :key="'mea-' + ai"
                   class="callout-edit-admin" :class="{ 'is-selected': editSelectedIdx === ai }"
                   :style="editMarkerStyle(b)"
                   @mousedown.stop.prevent="startMarkerDrag(ai)"
                   @click.stop="editSelectedIdx = ai">
                <span class="cea-code">{{ b.code || '?' }}</span>
                <button v-if="editSelectedIdx === ai" type="button" class="cea-resize"
                        @mousedown.stop.prevent="startMarkerResize(ai)" title="Drag to resize">&#9707;</button>
              </div>
            </template>
          </div>
        </div>

        <div class="custom-image-controls">
          <!-- editing aids (hint + selected-bubble code) on their own line -->
          <div v-if="balloonEditMode" class="cic-editrow">
            <span class="bee-hint">Click empty image to add · drag any bubble to move · &#9707; resizes · select to rename/delete · scroll/＋ to zoom</span>
            <span v-if="editSelectedIdx >= 0 && editBalloons[editSelectedIdx]" class="bee-sel">
              <input ref="editCodeInput" v-model="editBalloons[editSelectedIdx].code"
                     class="bee-code-input" placeholder="code e.g. 1RCV"
                     @input="editDirty = true"
                     @keydown.enter.prevent="editSelectedIdx = -1" />
              <button type="button" class="bee-del" @click="removeEditBalloon(editSelectedIdx)"
                      title="Delete this bubble">&#10005;</button>
            </span>
            <span v-if="editError" class="bee-error">{{ editError }}</span>
          </div>
          <!-- one row of buttons -->
          <div class="cic-btnrow">
            <button type="button" class="btn btn-secondary" @click="zoomOut">−</button>
            <button type="button" class="btn btn-secondary" @click="zoomIn">+</button>
            <button type="button" class="btn btn-outline-secondary" @click="resetZoom">Reset</button>
            <template v-if="balloonEditMode">
              <button type="button" class="btn btn-success" :disabled="editSaving || !editDirty"
                      @click="saveBalloonEdit">{{ editSaving ? "Saving…" : "Save bubbles" }}</button>
              <button type="button" class="btn btn-outline-secondary" :disabled="editSaving"
                      @click="cancelBalloonEdit">Cancel</button>
            </template>
            <template v-else>
              <button v-if="isMasterUser && modalImage === assemblyImage" type="button"
                      class="btn btn-danger" @click="startBalloonEdit">&#9998; Edit bubbles</button>
              <button v-if="isMasterUser && reportTarget" type="button"
                      class="btn btn-warning" title="Report this chart as incorrect to the Help Desk"
                      @click="openReportChartModal">&#9888; Report chart</button>
              <button type="button" class="btn btn-primary" @click="closeImageModal">Close</button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== PARTS INQUIRY MODAL =====
         Opened from the "Don't see your machine?" footer in
         the machine dropdown. Mirrors the bug-report form
         pattern (description + optional file), plus customer
         contact fields so the parts team can reply.

         Submits to FeApiService.submitPartsInquiry which
         POSTs to /Email/parts-inquiry as multipart/form-data.
         Server hardcodes the recipient as parts@jcsteele.com;
         the customer's email goes into Reply-To so the team
         can answer directly. -->
    <b-modal id="parts-inquiry-modal"
             v-model="showPartsInquiryModal"
             title="Email the Parts Team"
             size="lg"
             centered
             :ok-title="partsInquirySending ? 'Sending…' : 'Email Inquiry or call 1-800-278-3353'"
             ok-variant="primary"
             cancel-title="Cancel"
             cancel-variant="outline-secondary"
             :ok-disabled="partsInquirySending || !partsInquiryFormValid"
             @ok="submitPartsInquiry"
             @hidden="resetPartsInquiryForm">

      <p class="parts-inquiry-intro">
        Tell us about the equipment you need parts for. We'll get back
        to you at the email you provide.
      </p>

      <div v-if="partsInquirySuccess" class="ui visible success message">
        <div class="header">Inquiry sent.</div>
        <p>Our parts team will follow up by email shortly.</p>
      </div>

      <div v-if="partsInquiryError" class="ui visible error message">
        <div class="header">Could not send your inquiry.</div>
        <p>{{ partsInquiryError }}</p>
      </div>

      <div v-if="!partsInquirySuccess" class="parts-inquiry-form">
        <div class="form-row">
          <div class="col-md-6">
            <label for="partsInquiryName">
              Your Name <span class="text-danger">*</span>
            </label>
            <input id="partsInquiryName"
                   type="text"
                   class="form-control"
                   maxlength="120"
                   v-model="partsInquiry.customerName" />
          </div>
          <div class="col-md-6">
            <label for="partsInquiryEmail">
              Reply Email <span class="text-danger">*</span>
            </label>
            <input id="partsInquiryEmail"
                   type="email"
                   class="form-control"
                   maxlength="200"
                   v-model="partsInquiry.customerEmail" />
          </div>
        </div>

        <div class="form-row mt-3">
          <div class="col-md-6">
            <label for="partsInquiryCompany">Company</label>
            <input id="partsInquiryCompany"
                   type="text"
                   class="form-control"
                   maxlength="200"
                   v-model="partsInquiry.customerCompany" />
          </div>
          <div class="col-md-6">
            <label for="partsInquiryMachine">
              Machine / Equipment Description
            </label>
            <input id="partsInquiryMachine"
                   type="text"
                   class="form-control"
                   maxlength="200"
                   placeholder="e.g. 90A Extruder, serial 12345"
                   v-model="partsInquiry.machineDescription" />
          </div>
        </div>

        <div class="form-row mt-3">
          <div class="col-12">
            <label for="partsInquiryDescription">
              What can we help you with?
              <span class="text-danger">*</span>
            </label>
            <textarea id="partsInquiryDescription"
                      class="form-control"
                      rows="6"
                      maxlength="2000"
                      placeholder="Tell us what parts you need or what's going on with your equipment."
                      v-model="partsInquiry.description"></textarea>
          </div>
        </div>

        <div class="form-row mt-3">
          <div class="col-12">
            <label for="partsInquiryFile">
              Attach a photo (optional)
            </label>
            <input id="partsInquiryFile"
                   type="file"
                   ref="partsInquiryFile"
                   accept=".jpg,.jpeg,.png"
                   @change="onPartsInquiryFileChange" />
            <div v-if="partsInquiryFileError" class="text-danger mt-1">
              {{ partsInquiryFileError }}
            </div>
            <small class="form-text text-muted">
              JPG or PNG, max 1 MB. Helpful for nameplates / serial tags.
            </small>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- ===== REPORT INCORRECT CHART MODAL (Master-only) =====
         Opened from the fullscreen chart viewer (⚠ toolbar button or
         double-click on the chart). Snapshots the viewer's context at
         open time (openReportChartModal) — the fullscreen viewer is
         closed first so this b-modal isn't buried under its z-index.
         Submits JSON to /Email/report-chart (FeApiService.reportChart);
         the server validates the assembly, builds the chart-page +
         upload links itself, and emails helpdesk@jcsteele.com. -->
    <b-modal id="report-chart-modal"
             v-model="showReportChartModal"
             title="Report incorrect chart"
             centered
             :ok-title="reportChartSending ? 'Sending…' : 'Send to Helpdesk'"
             ok-variant="danger"
             cancel-title="Cancel"
             cancel-variant="outline-secondary"
             :ok-disabled="reportChartSending || reportChartSuccess"
             @ok="submitReportChart"
             @hidden="resetReportChart">

      <div v-if="reportChartSuccess" class="ui visible success message">
        <div class="header">Report sent.</div>
        <p>The Help Desk received the chart details and a link to upload a corrected version.</p>
      </div>

      <div v-if="reportChartError" class="ui visible error message">
        <div class="header">Could not send the report.</div>
        <p>{{ reportChartError }}</p>
      </div>

      <template v-if="!reportChartSuccess && reportChartSnapshot">
        <p class="report-chart-intro">
          This sends the chart below to the Help Desk with a link to upload a corrected version.
        </p>
        <ul class="report-chart-ctx">
          <li v-if="reportChartSnapshot.machineName"><b>Machine:</b> {{ reportChartSnapshot.machineName }}</li>
          <li v-if="reportChartSnapshot.assemblyName"><b>Assembly:</b> {{ reportChartSnapshot.assemblyName }}</li>
          <li v-if="reportChartSnapshot.subAssemblyName"><b>Sub-Assembly:</b> {{ reportChartSnapshot.subAssemblyName }}</li>
          <li v-if="reportChartSnapshot.partFilter"><b>Part filtered on:</b> {{ reportChartSnapshot.partFilter }}</li>
        </ul>
        <label for="reportChartNote">What's wrong with this chart? (optional)</label>
        <textarea id="reportChartNote"
                  class="form-control"
                  rows="4"
                  maxlength="2000"
                  placeholder="e.g. Item 5L points at the wrong housing; the chart is for the older model."
                  v-model="reportChartNote"></textarea>
      </template>
    </b-modal>
  </section>
</template>

<script>
/* ============================================================================
 * CONTRIBUTOR NOTES -- header-search hydration feature
 * ============================================================================
 *
 * THIS FILE:  Index_-_Search.vue  (/search)
 * SIBLINGS:   Create_-_Order.vue  (/orders/create)
 *             Create_-_RFQ.vue  (/quotes/createrfq)
 *
 * These three files share a "header-search hydration" feature where a part
 * suggestion clicked in the floating header search bar (Head.vue's
 * selectOption) propagates context -- code, machineName, assembly,
 * allParts=true -- into whichever page the user lands on. Changes to one
 * file almost always need parallel changes to the other two. The shared
 * pattern is grep-able: search any of these tokens and you'll find it
 * mirrored across all three:
 *
 *   - Template:   class="header-search-result" v-if="headerSearch.active"
 *                 (banner sits at top of .insights-panel)
 *   - CSS:        .header-search-result* in scoped <style>
 *   - data():     headerSearch + pendingHeaderMachineName / AssemblyName /
 *                 SubAssemblyName + wearPartIds
 *   - computed:   wearPartIdSet
 *   - methods:    applyHeaderSearchQuery, applyPendingHeaderMachine,
 *                 applyHeaderAssemblyDeepSearch, _floatHeaderSearchPartToTop,
 *                 clearHeaderSearch, loadWearPartIds, isWearEligible
 *   - watchers:   machines, wearAssemblyOptions, $route
 *   - mounted():  applyHeaderSearchQuery() then loadWearPartIds()
 *   - Loaders:    loadInlineWearAssemblyOptions, loadInlineWearPartsForAssembly,
 *                 loadInlineAllPartsForAssembly each have hydration blocks
 *
 * --- ARRIVAL PATHS ---
 *   1. Direct: URL query params (?code=...&machineName=...&assembly=...&allParts=true)
 *   2. Cross-page nav: localStorage.headerSearchParams (set by any of the
 *      three pages when applyHeaderSearchQuery successfully reads URL params,
 *      or by Head.vue itself).
 *   First line of applyHeaderSearchQuery: prefer URL, fall back to
 *   localStorage. Source is logged ("source: URL" vs "source: localStorage").
 *
 * --- KNOWN RACES (DO NOT REGRESS) ---
 *
 *   [R1] mounted()'s 600ms setTimeout calls loadInlineWearAssemblyOptions
 *        when companyId+machineId are set. Race-GUARDED with
 *        `!self.headerSearch.active`. Why: when header-search is the
 *        arrival path, the machineId watcher (set by applyPendingHeader-
 *        Machine) has ALREADY triggered the load. A second call bumps
 *        wearLoadKey, which silently invalidates the in-flight parts
 *        response from loadInlineAllPartsForAssembly / loadInlineWearParts-
 *        ForAssembly (their stale-check is `if (key !== this.wearLoadKey)
 *        return;`). Symptom: parts list never populates, log shows
 *        "[...] inline wear: stale key, ignoring response".
 *
 *   [R2] applyHeaderAssemblyDeepSearch's stale-check captures machineId,
 *        NOT wearLoadKey. Why: wearLoadKey gets bumped by intra-page
 *        bootstrap chatter (other loaders firing). machineId changes only
 *        on actual user-driven machine switches, which is the only signal
 *        we genuinely want to abort on. Diagnostic log fires when the
 *        stale-check trips: "deep search response for parent X dropped --
 *        machineId changed from N to M".
 *
 *   [R3] applyHeaderSearchQuery has NO modify-flow guard here because
 *      /search has no modify flow. The two Create files DO have
 *      this guard -- don't copy this method's prelude verbatim into
 *      a Create file without adding the pendingModify* check.
 *
 *   [R4] wearAssemblyOptions watcher re-fires applyHeaderAssemblyDeepSearch
 *        as a safety net. With [R1] and [R2] in place, this watcher won't
 *        actually re-fire (single load, single deep-search). But it's there
 *        to catch edge cases where wearAssemblyOptions populates after the
 *        first deep-search attempt aborted. If you see duplicate "deep
 *        search starting" log lines, something else is reassigning
 *        wearAssemblyOptions -- investigate before silencing.
 *
 * --- API ENDPOINTS ---
 *   apiService.getWearPartIds()
 *     -> GET /api/wear/GetWearPartIds. Flat list of wear-eligible PartIds.
 *        Used by isWearEligible(part) for the "2% Discount" chip.
 *        Source-of-truth: WearController.GetWearPartIds (filters Active,
 *        FK > 0, returns Distinct PartIds across ALL companies -- wear-
 *        eligibility is a per-PART attribute, not per-customer).
 *   apiService.getSelectItemsByCompanyMachine({ id })
 *     -> top-level wearAssemblyOptions, list of { value, text }
 *   apiService.getAssembliesWithWearParts({ assemblyId, companyMachineId })
 *     -> wear sub-assemblies (used when configuredWear=true)
 *   apiService.getAssemblyWithParts({ id })
 *     -> all-parts sub-assemblies (used when configuredWear=false)
 *
 * --- VUE 2 REACTIVITY GOTCHAS ---
 *   - Set mutations are NOT tracked. Use Array + computed object-map (see
 *     wearPartIds + wearPartIdSet for the canonical pattern in this file).
 *   - Vue.set / this.$set is needed when adding NEW keys to existing
 *     objects; plain assignment to a new key won't trigger reactivity.
 *     Used in the loader hydration blocks for expandedWearAssemblies.
 *   - vuetable-2 v2.0.0-beta.4 doesn't render __slot: fields correctly;
 *     use 1.7.5 with slot/slot-scope syntax (project-wide constraint).
 *
 * --- LOG PREFIX CONVENTION ---
 *   Search.vue           uses [Search]
 *   Create_-_Order.vue   uses [Create]
 *   Create_-_RFQ.vue     uses [Create RFQ]
 *   This file uses [Search]. Maintain the prefix when porting code
 *   between files.
 *
 * --- EDITING NOTES (Claude-specific, but applies to anyone) ---
 *
 *   When applying multi-step edits via a Python script, SAVE AFTER EACH
 *   step, not all at the end. sys.exit on a late failed step otherwise
 *   discards all in-memory mutations from earlier successful steps.
 *   (Bitten twice in this codebase -- once on the banner template, once
 *   on the discount chip. Pattern that works:
 *     def patch(path, old, new, label):
 *         with open(path, 'rb') as f: s = f.read().decode()
 *         # ...mutate...
 *         with open(path, 'wb') as f: f.write(s.encode())
 *   Slow but correct.)
 *
 *   s.count(substring) is a substring search, not a line search. A 4-space-
 *   indented `this.applyHeaderSearchQuery();` (mounted call) is a substring
 *   of an 8-space-indented `        this.applyHeaderSearchQuery();` ($route
 *   watcher call), so count returns 2 even though they're "different lines".
 *   Use multi-line anchors with surrounding context when disambiguating.
 *
 *   When porting a method from one file to another, the log prefix needs
 *   retagging. Search -> Create: s/[Search]/[Create]/g.
 *   Create -> RFQ: s/[Create]/[Create RFQ]/g (be careful, [Create RFQ]
 *   contains [Create] as a substring; do the longer replace first, OR
 *   make sure the source file has only [Create] before the swap).
 *
 * ============================================================================
 */

import { mapGetters } from "vuex";
import { recordSearch } from "@/assets/js/activity";
import FeApiService from "@/assets/js/FeApiService";
import { downloadPdf } from "@/assets/js/helper";
import { locateCallout, findCalloutBox, extractBalloons } from "@/assets/js/calloutLocator";
import SelectCompanyPrompt from "@/components/Common/SelectCompanyPrompt.vue";
const apiService = new FeApiService();

import "expose-loader?$!expose-loader?jQuery!jquery";
import "semantic-ui-dropdown/dropdown.js";

import Vue from "vue";
import Vue2Filters from "vue2-filters";

Vue.use(Vue2Filters);

export default {
  name: "CreateOrder",
  components: { SelectCompanyPrompt },
  computed: {
    // Signed-in user from Vuex (same source as the dashboard) so the
    // select-a-company prompt can greet them by first name.
    ...mapGetters({ currentUser: "currentUser" }),
    // Whether a company is currently selected. Derived from companyId,
    // which is hydrated on mount from localStorage.companyId and kept in
    // sync by the existing companyId watcher (it fires on every header
    // company change), so this computed re-evaluates reactively without
    // any extra polling.
    companySelected() { return !!this.companyId; },
    // ===== STEP ACCORDION =====
    // Completion of each step: 1 = company chosen, 2 = machine chosen,
    // 3 = an assembly chosen in the part picker.
    step1Done() { return !!this.companyId; },
    step2Done() { return !!this.machineId; },
    step3Done() { return !!this.selectedWearAssemblyId; },
    // Which step is the expanded "full block". Manual / auto-advance wins
    // (expandedStep); otherwise derive the first incomplete step so the
    // right one is open on load (incl. URL-hydrated visits).
    currentStep() {
      if (this.expandedStep) return this.expandedStep;
      if (!this.companyId) return 1;
      if (!this.machineId) return 2;
      return 3;
    },
    // Circles shown in the "Completed Steps" row — every step that's
    // reachable (its prerequisites are met) EXCEPT the one currently
    // expanded. Clicking a circle expands that step.
    navCircles() {
      var reachable = [1];
      if (this.companyId) reachable.push(2);
      if (this.machineId) reachable.push(3);
      return reachable.filter((n) => n !== this.currentStep);
    },
    // Every reachable step shown as a circle in the top row — INCLUDING the
    // current one (styled is-current), so all step numbers sit together.
    stepCircles() {
      var reachable = [1];
      if (this.companyId) reachable.push(2);
      if (this.machineId) reachable.push(3);
      return reachable;
    },
    // Is ANY part filter narrowing the list right now — the text filter OR the
    // "Show discounted wear parts only" toggle? Drives card visibility, sub-assembly
    // auto-expand, and the "clear filter" banner so both filters behave the same.
    partsFilterActive() {
      return !!this.partFilter || this.showDiscountedOnly;
    },
    // Categories hidden because no parts match the active filter — drives the
    // "There are X categories below, clear to view" banner.
    filterHiddenCategories() {
      if (!this.partsFilterActive) return 0;
      var self = this;
      return (this.displayedSubAssemblies || []).filter(function (a) {
        return (a.parts || []).length && !self.filteredParts(a).length;
      }).length;
    },
    // Display names of each selection, for the circle hover tooltips.
    companyDisplayName() {
      var c = (this.companies || []).find((x) => String(x.value) === String(this.companyId));
      return c ? c.text : "";
    },
    machineDisplayName() {
      var m = (this.machines || []).find((x) => String(x.value) === String(this.machineId));
      return m ? m.text : "";
    },
    assemblyDisplayName() {
      var a = (this.wearAssemblyOptions || []).find((x) => String(x.value) === String(this.selectedWearAssemblyId));
      return a ? a.text : "";
    },
    // Transform style for the zoom modal image — combines pan (pointX/Y)
    // and scale into a single CSS transform. Mirrors ShopWearParts.vue.
    imageTransformStyle() {
      return {
        transform: `translate(${this.pointX}px, ${this.pointY}px) scale(${this.scale})`,
        transformOrigin: "center center",
      };
    },
    // Sub-assembly filter — when selectedSubAssemblyId is set,
    // narrow the accordion to that single sub-assembly. Null
    // selection shows the full list. Defensive: if the picker
    // value points to a sub-assembly that no longer exists in
    // wearAssemblies (e.g. machine swap raced ahead), fall
    // back to the full list.
    // Clickable balloon hotspots for the currently-docked assembly diagram:
    // each displayed part whose Item maps (via the pre-computed OCR map) to a
    // balloon on THIS image, with the balloon's native-pixel box + the target
    // part. Drives the overlay layer; clicking one highlights/scrolls the part.
    dockedBalloons() {
      var empty = { entry: null, items: [] };
      if (!this.calloutMap || !this.assemblyImage) return empty;
      var path = this.imagePathFromUrl(this.assemblyImage);
      if (!path) return empty;
      var entry = this.calloutMap[path];
      var ov = this.calloutOverrides && this.calloutOverrides[path];
      // No OCR entry but an override exists (a brand-new drawing annotated from
      // scratch): synthesize an empty entry from the override's reference dims.
      if ((!entry || !entry.w) && ov && ov.w) entry = { w: ov.w, h: ov.h, t: [] };
      if (!entry || !entry.w) return empty;
      var self = this;
      var raw;
      if (ov && ov.managed) {
        // Admin FULLY manages this image's bubbles — the override IS the set, so
        // the OCR map is ignored for it. Scale from the override's ref dims into
        // entry space (handles a later OCR re-scan at different dims).
        var sx = entry.w / (ov.w || entry.w), sy = entry.h / (ov.h || entry.h);
        raw = (ov.balloons || []).map(function (ab) {
          return { code: self._normCode(ab.code), box: { x: ab.x * sx, y: ab.y * sy, w: ab.w * sx, h: ab.h * sy } };
        }).filter(function (b) { return b.code; });
      } else {
        // Un-edited image: bubbles come straight from the OCR map.
        raw = (entry.t && entry.t.length) ? extractBalloons(entry) : [];
      }
      // partCode is filled when the part is in the currently-loaded list; null
      // balloons are still clickable and get revealed (filter off / expand) on click.
      var items = raw.map(function (b) {
        var hit = self._findPartByItem(b.code);
        return { box: b.box, code: b.code, partCode: hit ? hit.part.code : null };
      });
      return { entry: entry, items: items };
    },
    // Master/admin detection — copied verbatim from AccountSetupWidget (the
    // proven gate) so the balloon editor shows for exactly the same users.
    isMasterUser() {
      var u = this.currentUser || {};
      if (u.isAdmin === true || u.isMaster === true) return true;
      var matches = function (val) {
        if (!val) return false;
        var s = typeof val === "string" ? val : (val && val.name) || (val && val.role) || "";
        var low = String(s).toLowerCase();
        return low === "master" || low === "admin";
      };
      if (matches(u.role)) return true;
      if (Array.isArray(u.roles) && u.roles.some(matches)) return true;
      return false;
    },
    // Which chart the fullscreen viewer is showing, resolved to the entity a
    // "Report incorrect chart" can act on. Every chart is an Assemblies row
    // (sub-assemblies included); the machine slot has no backing image on
    // this stack (Machine has no ImagePath) so it can never appear here.
    // Sub-assembly is checked BEFORE assembly in case both hold the same
    // blob URL; thumbnails (dockDiagramForPart) resolve via reverse lookup
    // over assemblyThumbs, which is keyed by the sub-assembly's Assembly.Id.
    // Null → the report button/dblclick stay hidden/inert.
    reportTarget() {
      if (!this.showImageModal || !this.modalImage) return null;
      var self = this;
      if (this.subAssemblyImage && this.modalImage === this.subAssemblyImage && this.selectedSubAssemblyId) {
        var sub = (this.wearAssemblies || []).find(function (sa) {
          return String(sa.assemblyId) === String(self.selectedSubAssemblyId);
        });
        return { type: "subassembly", id: Number(this.selectedSubAssemblyId), subName: sub ? sub.assemblyName : "" };
      }
      if (this.assemblyImage && this.modalImage === this.assemblyImage && this.selectedWearAssemblyId) {
        return { type: "assembly", id: Number(this.selectedWearAssemblyId), subName: "" };
      }
      var thumbId = Object.keys(this.assemblyThumbs || {}).find(function (k) {
        return self.assemblyThumbs[k] === self.modalImage;
      });
      if (thumbId) {
        var row = (this.wearAssemblies || []).find(function (sa) {
          return String(sa.assemblyId) === String(thumbId);
        });
        return { type: "subassembly", id: Number(thumbId), subName: row ? row.assemblyName : "" };
      }
      return null;
    },
    displayedSubAssemblies() {
      if (!this.selectedSubAssemblyId) return this.wearAssemblies;
      var match = (this.wearAssemblies || []).filter(function (sa) {
        return String(sa.assemblyId) === String(this.selectedSubAssemblyId);
      }, this);
      return match.length > 0 ? match : this.wearAssemblies;
    },
    // Display label for the insights period toggle — mirrors the
    // dashboard's labeling so users see the same wording in both
    // places.
    insightsPeriodLabel() {
      return this.insightsPeriod === "ytd" ? "YTD" : "All Time";
    },
    // Sliced view of the topParts list, starting at 5 and growing in
    // 5-row chunks each time "See more" is clicked. Defensive against
    // a missing/null list — empty array fallback so the template
    // never sees `undefined`. Slices from the *filtered* list so the
    // search box and pagination cooperate cleanly.
    displayedTopParts() {
      return this.filteredTopParts.slice(0, this.displayLimit);
    },
    // True when there are still hidden rows the user could expand to —
    // again computed against the filtered list.
    canShowMore() {
      return this.filteredTopParts.length > this.displayLimit;
    },
    // History search — case-insensitive substring match against
    // partCode, description, and contextDescription. Empty search
    // returns the full list unchanged so behavior is identical to
    // before the search box was added when nothing's typed.
    filteredTopParts() {
      var all = (this.insights && this.insights.topParts) || [];
      var term = (this.searchTerm || "").trim().toLowerCase();
      if (!term) return all;
      return all.filter(function (p) {
        return (
          (p.partCode && p.partCode.toLowerCase().indexOf(term) !== -1) ||
          (p.description && p.description.toLowerCase().indexOf(term) !== -1) ||
          (p.contextDescription &&
            p.contextDescription.toLowerCase().indexOf(term) !== -1)
        );
      });
    },
    // Group selected parts by PO Number, in stable order of first
    // appearance. The first PO encountered becomes Order #1, the
    // second Order #2, and so on. Drives the tray rendering and the
    // picker modal's existing-order options.
    groupedSelections() {
      var groups = {};
      var poOrder = [];
      (this.selectedParts || []).forEach(function (p) {
        var po = p.poNumber || "(no PO)";
        if (!groups[po]) {
          groups[po] = { poNumber: po, parts: [] };
          poOrder.push(po);
        }
        groups[po].parts.push(p);
      });
      return poOrder.map(function (po, i) {
        return {
          orderNumber: i + 1,
          poNumber: groups[po].poNumber,
          parts: groups[po].parts,
        };
      });
    },
    // Total count across all order groups — used by the Continue
    // button label.
    selectedPartsCount() {
      return (this.selectedParts || []).length;
    },
    // Mirror of groupedSelections but for the quote queue. Same
    // shape — { orderNumber, poNumber, parts[] } — so the staging
    // tray's quote section can render with the same template logic
    // as the order section.
    groupedQuoteSelections() {
      var groups = {};
      var order = [];
      (this.selectedPartsQuote || []).forEach(function (p) {
        var po = p.poNumber || "(no PO)";
        if (!groups[po]) {
          groups[po] = { poNumber: po, parts: [] };
          order.push(po);
        }
        groups[po].parts.push(p);
      });
      return order.map(function (po, i) {
        return {
          orderNumber: i + 1,
          poNumber: groups[po].poNumber,
          parts: groups[po].parts,
        };
      });
    },
    selectedQuotePartsCount() {
      return (this.selectedPartsQuote || []).length;
    },
    // Modal save button label adapts to the action — "Update Cart"
    // when editing an existing entry vs "Add to Cart" for a brand-
    // new add. Helpful disambiguation when the modal serves both
    // flows.
    addToCartOkTitle() {
      return this.pickerMode === "edit" ? "Update Cart" : "Add to Cart";
    },
    // Is the part in each destination's queue? Drives the tile labels so the user
    // can see where it already is ("✓ In your order" vs "Add to your order").
    pendingInOrder() { return !!this.findPartInDestination(this.pendingPart, "order"); },
    pendingInQuote() { return !!this.findPartInDestination(this.pendingPart, "quote"); },
    // When the customer arrived from an intent nav link (New Order / New Quote
    // pointing at /search?intent=order|quote), lock the add-to-cart modal to
    // that destination and hide the other option. null = normal (both shown).
    forcedDestination() {
      var intent = (
        (this.$route.query && this.$route.query.intent) || ""
      ).toString().toLowerCase();
      if (intent === "order") return "order";
      if (intent === "quote") return "quote";
      return null;
    },
    // The staging groups that apply to the currently chosen
    // destination. Drives the modal's PO picker — each group is
    // a one-click target for the user to attach the new part to.
    // Empty array when no destination is picked yet, or when the
    // chosen destination has no staging items (in which case the
    // modal falls through to the plain PO input).
    currentDestinationGroups() {
      if (!this.pendingDestination) return [];
      return this.pendingDestination === "quote"
        ? this.groupedQuoteSelections
        : this.groupedSelections;
    },
    // Drives :ok-disabled on the modal so the Add/Update button
    // can't be clicked until the user has supplied everything we
    // need. Edit mode skips the destination check (it's locked).
    canConfirm() {
      if (this.pickerMode !== "edit" && !this.pendingDestination) {
        return false;
      }
      var po = (this.pendingPoNumber || "").trim();
      if (!po) return false;
      var qty = Number(this.pendingQuantity);
      if (!isFinite(qty) || qty < 1) return false;
      return true;
    },

    // True when the queue has been modified since the last
    // successful saveCart (or sync). Drives the Continue button's
    // text and click behavior — when false, the cart already
    // mirrors the staging tray and the button just navigates;
    // when true, the button needs to fire saveCart again to
    // commit the new state.
    //
    // Differences detected: queue length, missing parts (new
    // additions not yet committed), PO changes, quantity edits.
    hasUnsavedChanges() {
      // No snapshot yet means nothing has been committed; if
      // there's anything queued, it's all unsaved.
      if (!this.committedSnapshot) {
        return this.selectedParts && this.selectedParts.length > 0;
      }
      if (this.selectedParts.length !== this.committedSnapshot.length) {
        return true;
      }
      var snapshot = this.committedSnapshot;
      for (var i = 0; i < this.selectedParts.length; i++) {
        var current = this.selectedParts[i];
        var match = snapshot.find(function (s) {
          return (
            s.partId === current.partId &&
            s.poNumber === current.poNumber
          );
        });
        if (!match) return true;
        if (Number(match.quantity) !== Number(current.quantity)) {
          return true;
        }
      }
      return false;
    },
    // Label for the bottom-of-panel commit button. When the queue
    // is fully committed to the cart already, swap to "Go to Cart"
    // so the user understands clicking it just navigates rather
    // than re-saving. Reverts to "Continue with N parts" the
    // moment they add or change anything.
    continueButtonText() {
      if (this.hasUnsavedChanges) {
        return this.selectedPartsCount === 1
          ? "Continue with 1 part"
          : "Continue with " + this.selectedPartsCount + " parts";
      }
      return "Go to Cart";
    },

    // ===== INLINE WEAR PANEL VISIBILITY =====
    // Panel renders only when the user has explicitly opted
    // into wear-only filtering AND has all the context the
    // API needs. Without companyId we can't load assemblies;
    // without machineId there's nothing to scope wear parts
    // to. Both must be set before the panel appears.
    // Step 3 panel visibility. Renders whenever a machine is
    // picked — the "Show wear parts only" toggle no longer
    // gates the whole panel, just whether wear-only filtering
    // applies inside it. companyId is also required because
    // the assembly options API needs both. The panel handles
    // empty states gracefully when the machine has no
    // assemblies on file.
    showInlineWearParts() {
      return (
        !!this.companyId &&
        !!this.machineId
      );
    },

    // Accordion of sub-assemblies + parts only renders once
    // the user picks an assembly from the inline dropdown.
    // Loading and empty states are still rendered above the
    // accordion regardless of this flag.
    showWearPartsList() {
      return !!this.selectedWearAssemblyId;
    },

    // ===== PARTS INQUIRY MODAL =====
    // Send button stays disabled until name/email/description
    // are non-empty AND the email roughly looks like an
    // email. Backend re-validates with [EmailAddress] data
    // annotation; this is just a lightweight UX gate so the
    // user doesn't blast a half-filled form at the server.
    partsInquiryFormValid() {
      var p = this.partsInquiry;
      if (!p.customerName || !p.customerName.trim()) return false;
      if (!p.customerEmail || !p.customerEmail.trim()) return false;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.customerEmail)) return false;
      if (!p.description || !p.description.trim()) return false;
      return true;
    },

    // ===== WEAR-ELIGIBILITY SET =====
    // Object-map projection of wearPartIds for O(1) lookup
    // inside isWearEligible(part). Recomputed only when
    // wearPartIds changes (single fetch on mount), so the
    // map-build cost is paid once per page load.
    //
    // Object.create(null) instead of {} so prototype keys
    // (toString, hasOwnProperty, etc.) can't false-positive
    // when a part ID happens to collide with one — a
    // theoretical concern with all-numeric IDs but free
    // protection.
    wearPartIdSet() {
      var s = Object.create(null);
      var arr = this.wearPartIds || [];
      for (var i = 0; i < arr.length; i++) {
        s[String(arr[i])] = true;
      }
      return s;
    },
  },
  mounted() {
    $(".ui.dropdown").dropdown({
      fullTextSearch: true,
      sortSelect: true,
      match: "text",
      // Force the menu to render below the field. Without this,
      // Semantic UI flips upward when the natural menu height
      // doesn't fit below — and since we removed the 60vh cap
      // (max-height: none on #MachineSelect .menu /
      // #WearAssemblySelect .menu), the menu can easily be tall
      // enough to overflow the top of the viewport instead of
      // the bottom, clipping the first several items. Forcing
      // downward keeps the top of the list anchored where the
      // user clicked; if it overflows the bottom, normal page
      // scroll covers it.
      direction: "downward",
    });
    delete localStorage.code;
    delete localStorage.description;
    if (localStorage.cartId && localStorage.cartId > 0) {
      this.disabled = true;
    } else {
      this.disabled = false;
    }

    if (localStorage.companyId) {
      this.companyId = localStorage.companyId;
      this.getMachinesByCompany();
      // Fire the insights fetch on initial load so the right-side
      // "Frequently Ordered" panel populates from the same data
      // source as the Account Dashboard.
      this.getInsights();
    }

    // Restore any in-progress queues from a previous visit to this
    // page. Runs after companyId is set so the helpers can verify
    // saved queues belong to the same company; if the user
    // switched companies between visits, saved queues are
    // discarded rather than restored against the wrong context.
    this.loadSelectionsFromStorage();
    this.loadSelectionsFromStorageQuote();

    // Cross-page sync: if there's an active order cart and/or
    // active quote cart linked to this user, re-derive the
    // corresponding staging queue from its server-side contents.
    // Cart is the source of truth across the Search page, the
    // Create-Order/Create-RFQ pages, and the cart pages — they
    // all read/write the same cart row, so each queue should
    // mirror its cart's current state at mount.
    this.syncFromActiveCart();
    this.syncFromActiveQuoteCart();

    // After hydration, validate that the linked carts still
    // exist with parts. Non-blocking — if validation finds drift
    // (cart deleted, emptied, or otherwise gone), it breaks the
    // local linkage. See validateLinkedCart for details.
    this.validateLinkedCart();
    this.validateLinkedQuoteCart();

    if (this.$route.params.fullOrder) {
      this.fullOrder = this.$route.params.fullOrder;
    }

    this.getCompanies();

    // ===== AUTO-ADD-TO-CART HAND-OFF =====
    // The Account dashboard (Top 5 Most Purchased Parts) and
    // any other entry point that wants to drop the user
    // straight into the Add-to-Cart modal redirects here with
    // `autoAddToCart=1` plus partId/code (and optionally
    // autoAddDesc) on the query string. Open the same modal
    // the page's own ADD buttons trigger, pre-filled with a
    // synthetic part object.
    //
    // CRITICAL ORDERING: this runs BEFORE applyModifyQuery
    // because applyModifyQuery calls
    // $router.replace({ query: {} }) which wipes EVERY query
    // param — including ours — when the URL has any modify*
    // param. The Account dashboard's smart-redirect URL
    // typically carries both modify* (for machine/assembly
    // pre-fill) AND autoAddToCart, so reading the query
    // first here is the only safe order. After
    // addToCartClick fires, applyAutoAddToCartQuery does its
    // own $router.replace cleanup, so applyModifyQuery's
    // cleanup is a no-op for the auto-add params.
    //
    // selectedParts / selectedPartsQuote have been restored
    // from storage above (loadSelectionsFromStorage*), so the
    // findPartInQueues check inside addToCartClick correctly
    // detects edit-vs-add mode. syncFromActiveCart may still
    // be in flight asynchronously — in the rare case it
    // overwrites selectedParts mid-modal, the user's confirm
    // still pushes onto whatever the queue contains at that
    // moment; the worst case is a duplicate detect that the
    // cart-sync layer dedupes server-side.
    this.applyAutoAddToCartQuery();

    // ===== MODIFY-FLOW HYDRATION =====
    // If the user arrived from the Modify Order modal on
    // Cart.vue, the URL carries `modify*` query params
    // identifying which (Order#, machine, assembly) combo
    // they want to keep adding parts to. Walk Steps 1-3
    // through their pre-fill chain. Runs after getCompanies
    // so the company list is at least in flight by the time
    // applyModifyQuery sets companyId (the company select
    // doesn't strictly need its options loaded for the
    // value to bind, just for the dropdown to show the
    // matching label). The existing syncFromActiveCart
    // call above rebuilds the staging tray from the cart's
    // server-side parts so the user sees what they
    // already added.
    this.applyModifyQuery();

    // ===== HEADER-SEARCH HYDRATION =====
    // The floating search bar in Head.vue routes part-
    // suggestion clicks here with code/machineName/assembly/
    // allParts on the query string. Read those, set the
    // banner state on the right column, and stash the
    // machine/assembly names for the loaders to pick up
    // (matching by text since the suggestion API returns
    // the display strings, not the IDs the form binds).
    //
    // Runs AFTER applyModifyQuery on purpose — that helper
    // does $router.replace({ query: {} }) to wipe the URL,
    // but only when modify* params are present. For a pure
    // header-search arrival (no modify*), applyModifyQuery
    // returns early without touching the URL, so our params
    // survive and we can read them here. If both flows hit
    // at once (extremely unlikely — modify is launched from
    // the cart, header search from any page), modify wins
    // and the header banner just doesn't render this load —
    // a benign degradation.
    this.applyHeaderSearchQuery();

    // Fetch the global wear-eligibility list. Powers the
    // "2% Discount" chip on inline parts. Single fetch per
    // page load; see loadWearPartIds for caching rationale.
    this.loadWearPartIds();

    // ===== INLINE WEAR PANEL BOOTSTRAP =====
    // Vue watchers only fire on CHANGES, not initial
    // values, so when the page mounts with localStorage
    // state already populated (e.g. user navigates back to
    // /orders/create from /cart with company+machine still
    // selected and toggle already on), the watchers stay
    // silent and the wear panel would never auto-populate.
    // Force a load here.
    //
    // Wrapped in a delay because machineId is set by a
    // Semantic UI dropdown which doesn't always fire its
    // change immediately on mount — same dynamic that
    // ShopWearParts.vue handles with a 1500ms retry.
    var self = this;
    setTimeout(function () {
      // Pull machineId from localStorage if v-model didn't
      // already pick it up — needed for the image load and
      // the assembly-options bootstrap below.
      //
      // Carve-out for header-search hydration: when the user
      // arrived via a part-suggestion click and we have a
      // pendingHeaderMachineName waiting for the machines
      // list, DON'T restore the stale localStorage value.
      // applyPendingHeaderMachine (called from
      // getMachinesByCompany's resolve) will set the correct
      // machineId once the list arrives. Restoring here
      // first would briefly load assemblies for the wrong
      // machine before being overridden, producing a visible
      // flicker in Step 3.
      // New Order / New Quote DURING a guide tour or build session → start the
      // search FRESH (don't carry the last machine), so the tour's "select your
      // machine" step lands on a blank dropdown. Scoped to tour/build so normal
      // users still keep their last machine when they click New Order.
      try {
        var _ni = self.$route && self.$route.query && self.$route.query.intent;
        var _nt = localStorage.getItem("jcs_tour_active") || localStorage.getItem("jcs_tour_build_session");
        if ((_ni === "order" || _ni === "quote") && _nt) {
          self.machineId = null;
          try { delete localStorage.machineId; } catch (e) {}
          try { delete localStorage.machineIdRFQ; } catch (e) {}
        }
      } catch (e) {}
      if (!self.machineId
          && !self.pendingHeaderMachineName
          && localStorage.machineId) {
        self.machineId = localStorage.machineId;
      }
      // Validate the just-restored machineId against the
      // (potentially-already-loaded) machines list. Without
      // this, a machineId tagged in localStorage from a
      // different company silently makes Step 2 look complete
      // and unconditionally reveals Step 3 even though the
      // dropdown's placeholder is showing. The earlier
      // validation hook in getMachinesByCompany only catches
      // the case where machineId is already set when the
      // machines list arrives — in this code path the
      // sequence is reversed (machines arrive first, then
      // machineId is restored 600ms later), so we re-run
      // the check here.
      self.validateMachineSelection();
      // Tell Semantic UI to redraw its dropdown label so the
      // restored machineId actually shows. Without this, the
      // value is bound but the dropdown reads
      // "Select your machine".
      self.syncMachineDropdownDisplay();
      // Load the machine image on initial mount if a
      // machine is already selected. Watcher only fires on
      // CHANGES, so without this an existing machineId
      // (from localStorage or a return-navigation) would
      // never get its image rendered.
      if (self.machineId) {
        self.loadMachineImage();
      }
      // Bootstrap the inline parts panel. Step 3 is now
      // visible whenever a machine is selected (regardless
      // of configuredWear), so always populate the
      // assembly options if we've got the context.
      //
      // Race-guard for header-search hydration: when the
      // user arrived via a header-search click, the
      // hydration chain (applyPendingHeaderMachine →
      // machineId watcher → loadInlineWearAssemblyOptions)
      // has ALREADY initiated a load. Firing a second one
      // here bumps wearLoadKey, which silently invalidates
      // the in-flight parts response (the one whose
      // selectedWearAssemblyId was set by the deep search),
      // killing the floated-part + sub-assembly hydration.
      // Skip the redundant load when header-search is the
      // arrival path; the watcher already covered it.
      if (self.companyId && self.machineId && !self.headerSearch.active) {
        self.loadInlineWearAssemblyOptions();
      }
    }, 600);

    // ===== Single docked image across the app =====
    // Only one docked diagram should show at a time. When another source
    // (e.g. the Top-5 widget) claims the dock, close ours.
    if (!Vue.prototype.$cartBus) {
      Vue.prototype.$cartBus = new Vue();
    }
    this._dockClaimHandler = (src) => {
      if (src !== "search") {
        this.onCloseLock("assembly");
        this.onCloseLock("machine");
      }
    };
    Vue.prototype.$cartBus.$on("dock-claimed", this._dockClaimHandler);

    // The header search's × (Head.vue clearSearch) → drop our active
    // header-search filter and reset, same as the banner ×.
    if (!Vue.prototype.$headerSearchBus) {
      Vue.prototype.$headerSearchBus = new Vue();
    }
    this._headerClearedHandler = () => { this.clearHeaderSearch(); };
    Vue.prototype.$headerSearchBus.$on("header-cleared", this._headerClearedHandler);

    // ===== Guide-tour preview context (admin builder) =====
    // The tour BUILDER asks us (a) to snapshot the current "Find Your Part"
    // selections so it can store them on a step, and (b) to RESTORE a step's
    // snapshot on "Preview / resize" so the right parts land on screen (and the
    // step's target element exists). Best-effort — the builder's auto-snap
    // (_watchForEditTarget) still covers anything we can't restore.
    this._tourCaptureHandler = () => { this._tourWriteSearchCtx(); };
    this._tourApplyHandler = (ctx) => { this.restoreSearchContext(ctx); };
    this.$root.$on("tour:capture-search-ctx", this._tourCaptureHandler);
    this.$root.$on("tour:apply-search-ctx", this._tourApplyHandler);
  },
  beforeDestroy() {
    if (Vue.prototype.$cartBus && this._dockClaimHandler) {
      Vue.prototype.$cartBus.$off("dock-claimed", this._dockClaimHandler);
    }
    if (Vue.prototype.$headerSearchBus && this._headerClearedHandler) {
      Vue.prototype.$headerSearchBus.$off("header-cleared", this._headerClearedHandler);
    }
    if (this._tourCaptureHandler) this.$root.$off("tour:capture-search-ctx", this._tourCaptureHandler);
    if (this._tourApplyHandler) this.$root.$off("tour:apply-search-ctx", this._tourApplyHandler);
  },
  data() {
    return {
      // Step accordion: which step's full block is expanded. null =
      // "follow completion" (the computed currentStep derives the first
      // incomplete step). Set explicitly when the user clicks a circle in
      // the "Completed Steps" row or when a step auto-advances.
      expandedStep: null,
      assembly: "",
      assemblies: [],
      assemblyId: "",
      machine: "",
      machines: [],
      machineId: "",
      company: "",
      companies: [],
      companyId: "",
      // Friendly display name of the selected company — populated
      // alongside the insights fetch so the panel can read "parts
      // <Company Name> orders most often" rather than a bare ID.
      companyName: "",
      poNumber: "",
      // ALL parts show by default now (the "Show wear parts only" toggle was removed);
      // false = the all-parts loader runs. Kept (vs deleted) because lots of load /
      // sub-assembly / saveCart logic still reads it — it's just pinned to "all parts".
      configuredWear: false,
      // "Show discounted wear parts only" filter (right of the part filter). Off by default;
      // when on, filteredParts() keeps only 2%-discount-eligible parts (isWearEligible).
      showDiscountedOnly: false,
      // ===== MODIFY-QUERY HYDRATION =====
      // Stash the assembly + sub-assembly IDs from the
      // ?modify* query params here while we wait for the
      // assembly options list to populate (loadInline-
      // WearAssemblyOptions). Once options arrive, the
      // loader checks these and applies them, then nulls
      // them out. Keeping it on `data` (not just a local
      // closure) means the loader can pick them up
      // regardless of how it was triggered (mounted, watcher,
      // or manual call).
      pendingModifyAssemblyId: null,
      pendingModifySubAssemblyId: null,

      // ===== HEADER-SEARCH HYDRATION =====
      // When the user clicks a part suggestion in the
      // floating header search bar (Head.vue's selectOption),
      // they get pushed to /search with ?code=…&machineName=
      // …&assembly=…&allParts=true on the query. We mirror
      // that into:
      //
      //   headerSearch          — display state for the banner
      //                           that renders at the top of
      //                           the right column. `active`
      //                           gates v-if on the banner.
      //   pendingHeaderMachine- — stashed until the machines
      //   Name                    list loads, then matched by
      //                           text against
      //                           machines[].text. Set by
      //                           applyHeaderSearchQuery,
      //                           consumed by
      //                           applyPendingHeaderMachine
      //                           (called from
      //                           getMachinesByCompany's
      //                           resolve and from the mounted
      //                           setTimeout).
      //   pendingHeaderAssembly — stashed until the assembly
      //   Name                    options load, then matched
      //                           by text against
      //                           wearAssemblyOptions[].text.
      //                           Consumed inside
      //                           loadInlineWearAssemblyOptions
      //                           after the existing
      //                           pendingModifyAssemblyId
      //                           block (modify wins on
      //                           collision).
      //
      // Mirrors the pendingModify* pattern above on purpose
      // — same lifecycle, same consumption sites, same null
      // semantics for "already applied or never set".
      headerSearch: {
        code: "",
        machineName: "",
        assembly: "",
        active: false,
      },
      pendingHeaderMachineName: null,
      pendingHeaderAssemblyName: null,

      // ===== HEADER-SEARCH MACHINE DEEP-LINK ID =====
      // ?machineSel= — the exact CompanyMachine.Id of the machine
      // to pre-select, in the SAME id space as the machine
      // dropdown's option values (Email Insights resolves it
      // server-side against the customer's account). When set
      // (non-zero), applyPendingHeaderMachine selects it id-first
      // and only falls back to the machineName cascade on a miss.
      // 0 = not supplied → name matching as before.
      pendingHeaderMachineId: 0,

      // ===== HEADER-SEARCH ASSEMBLY DISAMBIGUATION =====
      // The master Assembly id carried by the autocomplete
      // suggestion the user clicked (Head.vue forwards it as
      // ?assemblyId=). Many assemblies share the same NAME
      // across the catalog — and some duplicates live under the
      // SAME machine — so resolving purely by
      // pendingHeaderAssemblyName can land on the wrong sub-
      // assembly and bury the part the user wanted. When this is
      // set (non-zero), the two name-based resolvers (the
      // wearAssemblyOptions watcher and loadInlineWearAssembly-
      // Options' hydration block) PREFER the dropdown option
      // whose .value === this id over the name cascade; the deep
      // search short-circuits the same way. 0/null = "no id
      // supplied" (older links, free-typed terms) → fall back to
      // the existing name logic, so all prior behavior is
      // preserved. A Number; coerced via Number(...) || 0 on
      // read so an empty/garbage query value can't poison the
      // String(...)===String(...) comparisons downstream.
      // Cleared alongside pendingHeaderAssemblyName everywhere
      // that one is nulled.
      pendingHeaderAssemblyId: 0,

      // ===== HEADER-SEARCH DEEP-SEARCH STATE =====
      // The `assembly` param in the header-search URL is
      // sourced from the part-suggestion API, which returns
      // the SUB-assembly name (the leaf the user picked
      // from autocomplete) — e.g. "28PC WEDGE GROOVE LINER"
      // — not the top-level assembly that actually populates
      // wearAssemblyOptions (e.g. "HCV AUGERS AND LINERS").
      //
      // When the top-level match in loadInlineWearAssembly-
      // Options fails, we don't give up — the value is moved
      // here and applyHeaderAssemblyDeepSearch fans out
      // parallel API calls (one per top-level assembly) to
      // find which parent contains a sub-assembly with this
      // name. First hit wins; selectedWearAssemblyId is set
      // to the winning parent, and this name stays on the
      // queue for the parts-loader's sub-assembly hydration
      // block to consume.
      //
      // Cleared by:
      //   - the parts-loader hydration block, on successful
      //     sub-assembly selection
      //   - applyHeaderAssemblyDeepSearch, when no parent's
      //     sub-list contains the name (gives up cleanly)
      //   - clearHeaderSearch (banner ×)
      pendingHeaderSubAssemblyName: null,

      // ?subAssemblyId= — exact sub-assembly (child Assembly.Id)
      // deep-link companion to pendingHeaderSubAssemblyName. The
      // parts loaders' hydration blocks prefer a row whose
      // .assemblyId matches this before trying the name cascade
      // (sub-assembly names like "None" repeat across parents;
      // the id never does). Cleared wherever the name is.
      pendingHeaderSubAssemblyId: 0,

      // ===== HEADER-SEARCH HIGHLIGHT =====
      // The normalized code of the part that _floatHeaderSearch-
      // PartToTop surfaced for the current header search. The
      // parts list binds an `is-header-highlight` class on the
      // row whose code matches, giving the user an unmistakable
      // visual cue (brand-tinted background + left accent bar)
      // pointing at the exact part they searched for. Compared
      // case-insensitively in the template (see
      // isHeaderHighlightPart). Auto-clears after a few seconds
      // via _headerHighlightTimer, and is also cleared on a new
      // header search and by clearHeaderSearch.
      headerHighlightCode: "",

      // ===== OFF-ACCOUNT HEADS-UP BANNER =====
      // When the user searches a part whose MACHINE isn't set up
      // on the current company's account, applyPendingHeaderMachine
      // fails to resolve a company machine (matched by NAME — the
      // suggestion's machineId is a MASTER Machine id, a different
      // id space than the company's CompanyMachine ids, so id
      // matching would be wrong; see the name-matcher there). In
      // that case we flip this flag and show a NON-BLOCKING amber
      // banner: the user can still order, but is told the machine
      // isn't on their account and to contact us to add it.
      // headerOffAccountMachine / headerOffAccountCode carry the
      // labels for the message. Dismissible (× sets headerOffAccount
      // false); also reset whenever a new header search applies, on
      // a successful machine match, and by clearHeaderSearch.
      headerOffAccount: false,
      headerOffAccountMachine: "",
      headerOffAccountCode: "",

      // ===== WEAR-ELIGIBILITY LOOKUP =====
      // Flat array of PartIds (as strings) that are
      // registered in dbo.Wears. Loaded once on mount via
      // apiService.getWearPartIds (which calls
      // /api/wear/GetWearPartIds — see WearController.cs).
      // Powers the green "2% Discount" chip on the inline
      // parts list: the chip used to be gated on
      // `configuredWear`, which conflated "this part is
      // wear-eligible" (a per-part attribute) with "the
      // user has the wear-only toggle on" (a view filter).
      // Result: parts that ARE wear-eligible got no chip
      // when the user was browsing in all-parts mode.
      //
      // Stored as an array (rather than a Set) because
      // Vue 2 reactivity doesn't track Set mutations. The
      // wearPartIdSet computed wraps it in an object map
      // for O(1) lookups inside isWearEligible(part).
      //
      // Empty array on init means "not loaded yet" — the
      // chip simply won't render until the load completes,
      // which is the same UX as if the part isn't eligible.
      // The fetch is cheap (returns just IDs), so this
      // window is brief.
      wearPartIds: [],

      // searchTerm is unused now that the keyword-search panel is
      // gone, but keep the property so any external bindings (route
      // query helpers, etc.) don't blow up.
      searchTerm: "",
      companyError: false,
      machineError: false,
      fullOrder: "",
      disabled: false,

      // ===== INSIGHTS PANEL STATE =====
      // Mirrors the Account Dashboard's insights model so the same
      // getInsights() endpoint powers both. Only `topParts` is rendered
      // on this page; other fields are kept on the object so a future
      // expansion (e.g. spend totals) doesn't require a data shape
      // change. addingInsightPart prevents double-clicks during the
      // resolveTopPartContext round trip. displayLimit drives the
      // "See more" pagination — starts at 5 and increments by 5 with
      // each click, resetting whenever the YTD/All Time period flips
      // so a stale expanded view doesn't carry over.
      insights: {
        topParts: [],
      },
      insightsPeriod: "ytd",
      insightsLoading: false,
      addingInsightPart: false,
      displayLimit: 5,
      // History search — case-insensitive substring filter against
      // partCode, description, and contextDescription on
      // insights.topParts. Empty string = no filter (show all).
      searchTerm: "",

      // ===== MULTI-SELECT QUEUES =====
      // Two parallel queues — one per destination. The user picks
      // destination (Order vs Quote) per-part in the Add-to-Cart
      // modal, and the part lands in whichever queue they chose.
      // Each entry carries its own poNumber and quantity, so a
      // single queue can hold parts spread across multiple PO
      // groups.
      selectedParts: [],
      selectedPartsQuote: [],

      // ===== ADD-TO-CART MODAL STATE =====
      // Single combined modal replaces the picker + explainer pair
      // from Create-Order and Create-RFQ. The user picks a
      // destination, enters a PO/reference, and sets a quantity.
      //
      // pickerMode: 'add' for a brand-new part, 'edit' when the
      //   part is already in one of the queues. In edit mode the
      //   destination toggle is hidden (cross-destination moves
      //   aren't supported in v1) and a Remove link appears in
      //   the footer.
      //
      // pendingDestination: 'order' | 'quote' | null — drives
      //   label/helper text inside the modal and routes the
      //   confirm to the right queue. Starts as null in add mode
      //   when the staging tray already has items so the user
      //   must explicitly pick before the rest of the form
      //   reveals (progressive disclosure). When both queues are
      //   empty, pre-set to 'order' so the modal shows everything
      //   immediately — no ambiguity to resolve.
      //
      // pendingPoNumber / pendingQuantity: the modal's input
      //   values, pre-filled in edit mode.
      //
      // useNewPo: in add mode when the chosen destination has
      //   existing groups, the modal shows pickable buttons for
      //   each existing PO. Clicking "+ New" flips this to true
      //   and reveals the text input for a brand-new PO. Reset
      //   on every modal open and on destination change.
      showAddToCartModal: false,
      pickerMode: "add",
      pendingPart: null,
      pendingDestination: "order",
      pendingPoNumber: "",
      pendingQuantity: 1,
      useNewPo: false,

      // Snapshot of each queue's parts at the moment of the last
      // successful saveCart (or sync). Used to detect whether the
      // queue has been modified since — when it matches the current
      // queue exactly, the cart and staging tray are in sync.
      // Cleared when the corresponding cart is deleted.
      //
      // Each entry: { partId, poNumber, quantity }
      // null = nothing has been committed yet for this destination.
      committedSnapshot: null,
      committedSnapshotQuote: null,

      // Debounce + concurrency coordination for the staging tray
      // → cart sync. _syncTimer holds the pending setTimeout id
      // so successive scheduleSyncToCart calls collapse into one
      // fire. _syncDirty flips true when a change comes in
      // mid-flight; the in-flight sync's finally block re-fires
      // after resolving so the cart catches up.
      _syncTimer: null,
      _syncDirty: false,

      // ===== INLINE WEAR PARTS PANEL =====
      // When `configuredWear` is on AND a machine is picked,
      // a wear parts panel renders in the right column above
      // Frequently Ordered. Replaces the navigation to
      // /orders/shopwearparts entirely — users add wear
      // parts straight into the existing staging tray
      // without leaving Create.
      //
      // Two API calls drive it:
      //   1. getSelectItemsByCompanyMachine(machineId)
      //      → fills wearAssemblyOptions (the dropdown)
      //   2. getAssembliesWithWearParts(assemblyId,
      //      companyMachineId) → fills wearAssemblies (the
      //      accordion of sub-assemblies + their wear parts)
      // Same endpoints ShopWearParts.vue uses — no backend
      // changes needed.
      wearAssemblyOptions: [],
      selectedWearAssemblyId: null,
      wearAssemblies: [],
      wearAssembliesLoading: false,
      wearPartsLoading: false,
      // Map of {assemblyId: bool} so the user can collapse /
      // expand each sub-assembly card independently. Default
      // is everything expanded since the panel is small and
      // most users will want to see the parts immediately.
      expandedWearAssemblies: {},
      // Monotonic key — bumped every time loadWearAssemblyOptions
      // or loadWearPartsForAssembly fires. The async callbacks
      // compare the captured key against the current one and
      // bail if it's stale. Prevents race conditions when the
      // user switches machines or assemblies faster than the
      // network responds.
      // NOTE: must NOT be prefixed with `_` — Vue 2 reserves the
      // `_` and `$` prefixes for internal use and will not proxy
      // them onto the component instance, which would make
      // `this.wearLoadKey` permanently `undefined` and break
      // the staleness comparison (NaN !== NaN is always true,
      // so every response would be discarded).
      wearLoadKey: 0,

      // ===== MACHINE IMAGE =====
      // Loaded from getCompanyMachineDetails when a machine
      // is picked. Mirrors the canonical pattern used in
      // ShopWearParts.vue: image path is appended to the
      // Steele blob storage URL. Click-to-zoom uses the
      // same modal + pan/zoom controls as ShopWearParts so
      // the UX is consistent across pages.
      machineImage: null,
      machineImageUrl: "https://steeleimages.blob.core.windows.net/images/",
      machineImageLoading: false,
      // ===== PARTS INQUIRY MODAL =====
      // Drives the "Don't see your machine? Email Parts
      // Team" popup that opens from the machine dropdown
      // footer. Form fields back the modal inputs;
      // submission state flags drive the send button +
      // success/error banners.
      showPartsInquiryModal: false,
      partsInquirySending: false,
      partsInquirySuccess: false,
      partsInquiryError: "",
      partsInquiryFile: null,
      partsInquiryFileError: "",
      partsInquiry: {
        customerName: "",
        customerEmail: "",
        customerCompany: "",
        machineDescription: "",
        description: "",
      },
      // ===== REPORT INCORRECT CHART (Master-only) =====
      // Opened from the fullscreen chart viewer (⚠ button / dblclick).
      // reportChartSnapshot freezes the viewer's context at open time so
      // the report can't shift if the page state changes underneath the
      // dialog; the fullscreen viewer is closed before this modal shows.
      showReportChartModal: false,
      reportChartNote: "",
      reportChartSending: false,
      reportChartSuccess: false,
      reportChartError: "",
      reportChartSnapshot: null,
      // Assembly image — loaded when the user picks an
      // assembly in Step 3. Comes from getAssemblyDetails
      // (same call ShopWearParts.vue uses for its assembly
      // image). Reuses the same zoom modal as the machine
      // image — clicking either opens the same overlay.
      assemblyImage: null,
      assemblyImageLoading: false,
      // Step-3 part-list filter text (matches part code / item # / price).
      partFilter: "",
      // Admin: inline "add a missing item #" editor on a part row (saves to catalog).
      addItemFor: null,
      addItemCode: "",
      addItemSaving: false,
      addItemError: "",
      // ===== SUB-ASSEMBLY =====
      // Optional drill-down: when wearAssemblies (the
      // sub-assembly array for the picked top-level
      // assembly) has multiple entries, the user can pick
      // one from a "Jump to Sub-Assembly" dropdown to
      // collapse the accordion view to just that
      // sub-assembly. Null = show all.
      selectedSubAssemblyId: null,
      subAssemblyImage: null,
      subAssemblyImageLoading: false,
      // ===== PART-ROW ASSEMBLY THUMBNAILS =====
      // Map of assemblyId -> blob image URL (or null when the
      // assembly has no image on file / the fetch failed). Each
      // wear-part row shows its sub-assembly's picture as a small
      // left-hand icon, mirroring the Top-5 widget's thumbnail
      // style. Populated lazily by loadPartThumbnails() whenever
      // wearAssemblies changes; cached so re-renders never refetch.
      assemblyThumbs: {},
      // Pre-computed callout map (imagePath -> {w,h,t:[...]}) built offline by
      // the Azure OCR batch; lazy-loaded once, drives click-to-zoom on a part's
      // balloon. Null until first diagram interaction; {} if the asset is absent.
      calloutMap: null,
      // Admin-managed balloon overrides (imagePath -> { w, h, balloons:[{code,x,y,w,h}],
      // hidden:[code] }), merged over the OCR map in dockedBalloons so admin fixes are
      // clickable for ALL users. Loaded once via ensureCalloutOverrides().
      calloutOverrides: {},
      // ----- Master-only balloon editor (on the docked diagram) -----
      balloonEditMode: false,
      editBalloons: [],     // working admin balloons for the current image: { code, x, y, w, h } in entry px
      editHidden: [],       // OCR codes hidden for the current image
      editSelectedIdx: -1,
      editDirty: false,
      editSaving: false,
      editError: "",
      // Rendered (untransformed) box of the docked assembly <img> — offset +
      // client size within its wrap — measured at runtime so balloon hotspots
      // can be positioned over the object-fit:contain image. Re-measured on
      // load / resize / dock.
      assemblyImgBox: { cw: 0, ch: 0, ox: 0, oy: 0 },
      // Modal + zoom state — ported verbatim from ShopWearParts
      // so users see the same affordances on both pages.
      showImageModal: false,
      modalImage: "",
      // Measured base box of the zoom-modal image, for the clickable-balloon overlay.
      modalImgBox: { cw: 0, ch: 0, ox: 0, oy: 0 },

      // ===== IN-PLACE HOVER ZOOM =====
      // transform-origin string per image instance, updated by
      // inPlaceZoomMove() on mousemove. The CSS rule
      // `.machine-image-wrap:hover .machine-preview-img` applies
      // `transform: scale(...)`, and the inline transform-origin
      // (set from this map) controls WHICH point stays anchored,
      // so as the user moves the cursor the zoomed view "follows"
      // the cursor — classic Amazon/Etsy hover-magnify pattern.
      // Defaults to '50% 50%' so the very first hover (before
      // mousemove fires) zooms from the image center rather than
      // from a stale prior position.
      inPlaceZoomOrigin: {
        machine: "50% 50%",
        assembly: "50% 50%",
      },

      // ===== IN-PLACE LOCK STATE =====
      // Toggled by onImageClick after a single-click is confirmed
      // (i.e., after the 300ms double-click detection window
      // closes without a second click). When `true` for a given
      // key:
      //   - inPlaceZoomMove suppresses transform-origin updates
      //     (the lock holds the cursor's position at click time)
      //   - .machine-image-wrap.is-zoom-locked CSS kicks in:
      //     position:sticky so the wrap follows the viewport on
      //     scroll, plus a red border + crosshair cursor as a
      //     visual signal
      // Click again to unlock; double-click anywhere on the image
      // to open the full popup modal.
      inPlaceZoomLocked: {
        machine: false,
        assembly: false,
      },
      // True when the locked image is collapsed to its side pill (the →
      // toggle in the locked-panel toolbar). Click the pill to restore.
      imageCollapsed: false,
      // Locked-image zoom level (× scale), driven by the −/+ buttons on the
      // docked image. Default 4 matches the original fixed lock zoom.
      lockedZoom: 4,

      // ===== LOCKED-PANEL VIEWPORT GEOMETRY =====
      // Three pixel values that position the locked image
      // panel over the .insights-panel (Frequently Ordered)
      // container on the right side of the page. Set by
      // onImageClick on lock activation: it measures the
      // .insights-panel's bounding rect and stashes top,
      // left, and width here. The wrap reads them via inline
      // CSS custom properties (--lock-top / --lock-left /
      // --lock-width) so the fixed-positioned wrap adopts
      // the right column's geometry exactly — sits where
      // Frequently Ordered sits, same width.
      //
      // Defaults are intentionally non-destructive: if the
      // measurement fails (insights-panel not found, edge
      // case timing), CSS fallbacks (top:100px, left:auto,
      // width:auto) keep the panel visible somewhere
      // reasonable rather than vanishing offscreen.
      //
      // Single set of values shared between machine and
      // assembly locks — only one is locked at a time.
      lockedStickyTop: 100,
      lockedStickyLeft: 0,
      lockedStickyWidth: 0,
      scale: 1,
      minScale: 1,
      maxScale: 5,
      pointX: 0,
      pointY: 0,
      isDragging: false,
      startX: 0,
      startY: 0,

      clear: "",
    };
  },
  watch: {
    // Reset pagination on each new search so users always see the
    // first 5 matches of the filtered list, not a stale expanded
    // view from a previous filter.
    searchTerm() {
      this.displayLimit = 5;
    },

    // When the visible build step changes (e.g. clicking a "Completed
    // Steps" circle to revise), re-report the locked image to App.vue. If
    // the locked image's step is now hidden it measures 0 height and the
    // floating stack rises back to the top; when that step is shown again
    // the offset re-applies — keeping the cart's drop-below-image in sync
    // with whichever image is actually on screen.
    currentStep() {
      this._emitLockedImage();
    },

    // ===== PART-ROW THUMBNAILS =====
    // Whenever the wear-parts list changes (new assembly picked,
    // machine swap, header-search surfacing), fetch the picture for
    // every sub-assembly present so each part row can show it as a
    // left-hand icon. ensureAssemblyThumb() de-dupes + caches, so
    // re-fires are cheap.
    wearAssemblies() {
      this.loadPartThumbnails();
      // Load the callout map as soon as parts render so row thumbnails can crop
      // to each part's balloon without waiting for a diagram interaction.
      this.ensureCalloutMap();
      // A balloon was clicked for a part hidden by "wear parts only"; now that
      // the unfiltered parts have reloaded, finish revealing it (one-shot).
      if (this._pendingRevealItem) {
        var self = this, code = this._pendingRevealItem;
        this._pendingRevealItem = null;
        this.$nextTick(function () {
          var hit = self._findPartByItem(code);
          if (!hit) return;
          if (hit.asm && hit.asm.assemblyId != null) {
            self.$set(self.expandedWearAssemblies, hit.asm.assemblyId, true);
          }
          self.setHeaderHighlight(hit.part.code);
          self.$nextTick(function () { self._scrollToPartCode(hit.part.code); });
        });
      }
    },

    // ===== INLINE WEAR PANEL: assembly-options reload =====
    // Three inputs determine whether the dropdown should be
    // populated and what it should contain:
    //   - configuredWear: panel visibility gate
    //   - machineId: filter for assemblies (machine-specific)
    //   - companyId: filter for assemblies (company-specific)
    // Any change to any of these resets the picker and
    // re-loads.
    // Checking "Show discounted wear parts only" clears the text filter so the
    // discounted results show in full (not still narrowed by a leftover search).
    showDiscountedOnly(on) {
      if (on) this.partFilter = "";
    },
    configuredWear() {
      // Toggling the "Show wear parts only" checkbox doesn't
      // change the assembly options (the dropdown is the
      // full machine assembly list either way) — it only
      // changes which API gets called for the selected
      // assembly's parts. Reload parts in-place so the user
      // sees the new mode reflected immediately.
      if (this.selectedWearAssemblyId) {
        this.loadInlinePartsForSelectedAssembly();
      }
      // Defensive re-bind: if Vue's diff replaced the
      // assembly dropdown's DOM node when sibling v-ifs
      // around it changed, Semantic UI's event handlers
      // are now bound to a discarded node. Re-running
      // .dropdown() on the (current) #WearAssemblySelect
      // node restores click-to-open behavior. Idempotent
      // when the binding is already healthy.
      var self = this;
      this.$nextTick(function () {
        try {
          $("#WearAssemblySelect").dropdown({
            fullTextSearch: true,
            direction: "downward", // see main init — keep menu below the field
          });
          if (self.selectedWearAssemblyId) {
            $("#WearAssemblySelect").dropdown(
              "set selected",
              self.selectedWearAssemblyId
            );
          }
        } catch (e) {
          // Step 3 not in DOM — harmless.
        }
      });
    },
    machineId() {
      // Switching machines invalidates everything — the
      // assembly list, the picker selection, and the parts
      // accordion all need to reset. Then reload the
      // assembly options regardless of configuredWear, since
      // Step 3 is now always visible whenever a machine is
      // selected.
      this.resetInlineWearState();
      this.partFilter = "";   // new machine → clear the part filter so the new list shows in full
      if (this.machineId) {
        this.loadInlineWearAssemblyOptions();
        // Auto-advance the step accordion to the part picker.
        this.expandedStep = 3;
      }
      // Refresh the machine image alongside the wear panel.
      // No-ops cleanly when machineId is empty.
      this.loadMachineImage();
    },
    // Reload assemblies on company change — `companyId` has
    // its own existing watcher above for queue-stashing; we
    // hook the wear panel into that same change via a
    // separate handler. Vue allows multiple watchers when
    // they're on different keys, but here we share the same
    // key so we splice into the existing one below instead.

    // ===== INLINE WEAR PANEL: parts-accordion reload =====
    // When the user picks an assembly from the inline
    // dropdown, fetch its sub-assemblies + wear parts.
    selectedWearAssemblyId(newId) {
      // Switching the parent assembly resets the sub-
      // assembly picker — sub-assembly options come from
      // wearAssemblies which is about to be reloaded.
      this.partFilter = "";   // new assembly → clear the part filter so the new list shows in full
      this.selectedSubAssemblyId = null;
      this.subAssemblyImage = null;
      if (newId) {
        this.loadInlinePartsForSelectedAssembly();
        this.loadAssemblyImage(newId);
      } else {
        this.wearAssemblies = [];
        this.assemblyImage = null;
      }
    },

    // Sub-assembly drill-down. When the user picks one,
    // load its image and auto-expand its accordion card
    // (the user clearly wants to see those parts). Clearing
    // the picker (back to null) restores the all-sub-
    // assemblies view but doesn't change which cards are
    // expanded — that belongs to the user.
    selectedSubAssemblyId(newId) {
      if (newId) {
        this.loadSubAssemblyImage(newId);
        this.$set(this.expandedWearAssemblies, newId, true);
      } else {
        this.subAssemblyImage = null;
      }
    },
    // Persist the queue to localStorage on every change so it
    // survives navigation away and back via breadcrumbs (or any
    // other route change). Deep-watched because we mutate
    // individual queue rows (PO change, qty change) via $set —
    // shallow watch would miss those. Also schedules a debounced
    // cart sync so qty edits in the staging tray flow back to
    // the server cart instead of dying as local-only state.
    // scheduleSyncToCart bails cleanly when there's no linked
    // cart, so this is safe to fire on every mutation including
    // mount-time hydration.
    selectedParts: {
      deep: true,
      handler() {
        this.saveSelectionsToStorage();
        this.scheduleSyncToCart();
      },
    },
    // Mirror watcher for the quote queue. Same persist + debounced-
    // sync pattern as the order queue, just routed through the
    // quote-side helpers.
    selectedPartsQuote: {
      deep: true,
      handler() {
        this.saveSelectionsToStorageQuote();
        this.scheduleSyncToQuoteCart();
      },
    },
    // When the user switches companies, we DON'T wipe the queue —
    // we stash it under the outgoing company's slot and hydrate
    // whatever was previously saved under the incoming company.
    // This mirrors the cart's per-company persistence so an admin
    // shopping across multiple customers can jump between contexts
    // without losing in-progress selections in either.
    companyId(newId, oldId) {
      // Skip the initial empty → real-id assignment that happens
      // during mount-time hydration; that's handled by the explicit
      // loadSelectionsFromStorage call in mounted().
      if (!oldId || !newId || String(oldId) === String(newId)) return;

      // Persist the current queues under the OUTGOING company's keys
      // so the user can return to them later. Pass oldId explicitly
      // because this.companyId has already moved on to newId.
      if (this.selectedParts && this.selectedParts.length > 0) {
        this.saveSelectionsToStorage(oldId);
      }
      if (this.selectedPartsQuote && this.selectedPartsQuote.length > 0) {
        this.saveSelectionsToStorageQuote(oldId);
      }

      // Hydrate the queues for the INCOMING company. Helpers reset
      // to [] when no saved queue exists so we start clean rather
      // than inheriting the previous company's data.
      this.loadSelectionsFromStorage();
      this.loadSelectionsFromStorageQuote();

      // Inline wear panel — the assembly list is scoped to
      // (company, machine) so a company switch invalidates
      // it. Reset the picker selection and re-fetch options
      // if the panel is currently visible.
      this.resetInlineWearState();
      if (this.configuredWear === true) {
        this.loadInlineWearAssemblyOptions();
      }

      // Reload the company-scoped wear list that drives the
      // "2% Discount" chip so it reflects the INCOMING
      // company's own wear parts.
      this.loadWearPartIds();
    },

    // ===== HEADER-SEARCH HYDRATION SAFETY NETS =====
    // applyPendingHeaderMachine and the inline assembly
    // hydration block both run from inside their respective
    // loaders' .then() handlers, which IS the normal path.
    // These watchers exist to catch alternate code paths
    // that swap `machines` / `wearAssemblyOptions` without
    // going through getMachinesByCompany /
    // loadInlineWearAssemblyOptions — for example, if a
    // future refactor populates them from a different
    // source, or if Vue's reactivity batches things in an
    // order where the .then() hook fires before the array
    // assignment is reactive.
    //
    // Both are no-ops when there's nothing pending, so they
    // don't affect any non-header-search flow.
    machines() {
      if ((this.pendingHeaderMachineName || this.pendingHeaderMachineId) && this._machinesLoaded) {
        this.applyPendingHeaderMachine();
      }
    },
    wearAssemblyOptions() {
      // The inline block inside loadInlineWearAssemblyOptions
      // is the canonical consumer of pendingHeaderAssembly-
      // Name; this watcher only catches the rare case where
      // wearAssemblyOptions is set outside that path. We
      // can't reach back into that closure-scoped match
      // logic from here, so we re-resolve inline using the
      // same cascading strategy.
      if (!this.pendingHeaderAssemblyName) return;
      if (this.selectedWearAssemblyId) {
        // User (or modify flow) already picked something —
        // don't overwrite their choice.
        this.pendingHeaderAssemblyName = null;
        return;
      }
      var opts = this.wearAssemblyOptions || [];
      if (opts.length === 0) return;

      var target = String(this.pendingHeaderAssemblyName);
      var norm = function (s) {
        return String(s == null ? "" : s)
          .trim().toLowerCase().replace(/\s+/g, " ");
      };
      var normStrong = function (s) {
        return String(s == null ? "" : s)
          .toLowerCase().replace(/[^a-z0-9]/g, "");
      };
      var textOf = function (o) {
        return o.text || o.name || o.assemblyName || "";
      };
      var nT = norm(target);
      var nTS = normStrong(target);

      // ID-FIRST disambiguation. When the suggestion carried an
      // assemblyId, prefer the dropdown option whose .value is
      // that exact id — names are ambiguous (many assemblies
      // share one), but the id is unique. The option .value is
      // the assembly id, which is exactly what selectedWear-
      // AssemblyId expects. Only top-level options live in
      // `opts` here, so this hits when the picked assembly IS a
      // top-level one; otherwise we fall through to the name
      // cascade (which typically promotes to the deep search).
      var matchStrategy = null;
      var match = null;
      if (this.pendingHeaderAssemblyId) {
        var wantId = String(this.pendingHeaderAssemblyId);
        match = opts.find(function (o) {
          return String(o.value) === wantId;
        });
        if (match) matchStrategy = "id";
      }
      if (!match) {
        match = opts.find(function (o) { return norm(textOf(o)) === nT; })
          || opts.find(function (o) { return normStrong(textOf(o)) === nTS; })
          || opts.find(function (o) {
            var n = norm(textOf(o));
            return n && nT && (n.indexOf(nT) >= 0 || nT.indexOf(n) >= 0);
          });
        if (match) matchStrategy = "name";
      }

      if (match) {
        console.log(
          "[Search] header search (watcher): matched assembly",
          { value: match.value, text: textOf(match), via: matchStrategy }
        );
        this.selectedWearAssemblyId = match.value;
        this.$nextTick(function () {
          try {
            $("#WearAssemblySelect").dropdown(
              "set selected", match.value
            );
          } catch (e) {}
        });
      }
      this.pendingHeaderAssemblyName = null;
    },

    // ===== HEADER-SEARCH RE-ENTRY =====
    // When the user is already on /search and picks another
    // part from the floating header bar, Head.vue does
    // $router.push({ name: "Search", query: {...} }). The
    // route name doesn't change, so Vue keeps this component
    // alive — mounted() does NOT re-run, and our hydration
    // chain would silently miss the new params.
    //
    // This watcher catches that case: when any of the four
    // header-search params change between the previous and
    // current $route, re-run applyHeaderSearchQuery. Same
    // method that mounted uses, so the re-entry path and
    // the cold-load path stay in sync.
    //
    // Guard order:
    //   - path === path: cross-page navigations are mount's
    //     responsibility, not ours.
    //   - any of code/machineName/assembly/allParts changed:
    //     keeps us inert when the user touches OTHER query
    //     params (e.g. modify*, autoAddToCart) on the same
    //     page. Note we don't watch the deep value of
    //     to.query because Vue Router replaces the query
    //     object on every change — equality is stable on
    //     the four keys we care about.
    "$route": function (to, from) {
      if (!to || !from) return;
      if (to.path !== from.path) return;
      var tq = to.query || {};
      var fq = from.query || {};
      if (tq.code === fq.code
          && tq.machineName === fq.machineName
          && tq.assembly === fq.assembly
          && tq.allParts === fq.allParts) {
        return;
      }
      // If the user explicitly cleared the params (banner
      // ×), the new query has none of the four — let
      // clearHeaderSearch's state changes stand instead of
      // re-applying empty values.
      if (!tq.code && !tq.machineName && !tq.assembly) {
        return;
      }
      this.applyHeaderSearchQuery();
    },
  },
  methods: {
    // Expand a step from the "Completed Steps" circle row (or programmatically).
    goToStep(n) {
      this.expandedStep = n;
    },
    // Is a given step number complete? Drives the circle's done/pending style.
    isStepDone(n) {
      if (n === 1) return this.step1Done;
      if (n === 2) return this.step2Done;
      if (n === 3) return this.step3Done;
      return false;
    },
    // Hover tooltip for a "Completed Steps" circle — shows the selection
    // detail + "click to revise".
    circleTitle(n) {
      var rev = " — click to revise";
      if (n === 1) return "Company: " + (this.companyDisplayName || "(selected)") + rev;
      if (n === 2) return "Machine: " + (this.machineDisplayName || "(selected)") + rev;
      if (n === 3) return "Assembly: " + (this.assemblyDisplayName || "(selected)") + rev;
      return "Click to expand";
    },
    getCompanies() {
      apiService.getCompanyList().then((data) => {
        this.companies = data;

        if (localStorage.companyId) {
          this.companyId = localStorage.companyId;
          var companyId = this.companyId;
          // Find a friendly display name for the panel header. The
          // company list returns objects shaped like { value, text }
          // where `text` is "COMPANY NAME - (CODE)". Use the full
          // text — the panel sentence reads naturally with it.
          var match = (data || []).find(function (c) {
            return String(c.value) === String(companyId);
          });
          if (match) this.companyName = match.text;
          this.$nextTick(function () {
            setTimeout(function() {
              $("#CompanySelect").dropdown("set selected", companyId);
              $(".ui.dropdown").dropdown({ fullTextSearch: true, direction: "downward" });
            }, 500);
          });
          this.getMachinesByCompany();
        }
      });
    },
    downloadOrderPdf() {
      downloadPdf("order");
    },
    getMachineSelectedItems() {
      apiService.getMachinesList().then((data) => {
        this.machines = data;
        this._machinesLoaded = true;
        this.validateMachineSelection();
        this.syncMachineDropdownDisplay();
        // Mirror the hook in getMachinesByCompany — header-
        // search hydration must run as soon as the machines
        // list is in hand, regardless of which loader path
        // populated it.
        this.applyPendingHeaderMachine();
      });
    },
    getMachinesByCompany() {
      apiService
        .getMachinesByCompany({
          id: this.companyId,
        })
        .then((data) => {
          this.machines = data;
          this._machinesLoaded = true;
          this.validateMachineSelection();
          this.syncMachineDropdownDisplay();
          // If the user arrived via a header-search click,
          // the machine name was stashed in
          // pendingHeaderMachineName on mount. The machines
          // list is now in hand, so resolve the name → id
          // mapping and set machineId. Idempotent — bails
          // cleanly when there's nothing pending.
          this.applyPendingHeaderMachine();
        });
    },
    // Push the current machineId into the Semantic UI dropdown's
    // visible state. Vue's v-model on the hidden <input> sets
    // the bound value but Semantic UI doesn't observe that input
    // — it caches its visible label internally. So programmatic
    // machineId assignments (mount-time localStorage restore,
    // applyModifyQuery from Frequently Ordered handoff /
    // Add More Parts, deep-link reorder flow) update the value
    // but the dropdown still displays the placeholder
    // "Select your machine".
    //
    // Mirrors syncMachineDropdownDisplay in Create_-_Order.vue
    // and Create_-_RFQ.vue — same widget, same fix.
    syncMachineDropdownDisplay() {
      var self = this;
      this.$nextTick(function () {
        try {
          if (self.machineId
              && window.$
              && window.$("#MachineSelect").length) {
            window.$("#MachineSelect").dropdown(
              "set selected",
              String(self.machineId)
            );
          }
        } catch (e) {
          // Dropdown may not be initialized yet; fail silent.
        }
      });
    },
    // Verify the currently-bound machineId actually corresponds
    // to a machine in the loaded list. If it doesn't (e.g. the
    // value was restored from localStorage but belongs to a
    // different company, or to a machine that has since been
    // removed), clear it. Without this, Step 2's number circle
    // would flip to "complete" and Step 3 would unconditionally
    // render even though the dropdown's placeholder is showing
    // — `machineId` would be truthy but visibly nothing is
    // selected. The localStorage tag is cleared too so the
    // stale value doesn't keep getting re-restored on each
    // mount.
    //
    // Guarded against the "machines haven't loaded yet" case
    // via _machinesLoaded — without that guard, the mount-time
    // call (from the setTimeout that restores machineId from
    // localStorage) could fire before getMachinesByCompany
    // resolves and clear a perfectly valid machineId because
    // the find ran against an empty list. The matching hook
    // inside getMachinesByCompany picks up the validation
    // once machines actually arrive.
    validateMachineSelection() {
      if (!this.machineId) return;
      if (!this._machinesLoaded) return;
      var self = this;
      var match = (this.machines || []).find(function (m) {
        return String(m.value) === String(self.machineId);
      });
      if (!match) {
        this.machineId = "";
        try { delete localStorage.machineId; } catch (e) {}
      }
    },
    createOrder() {
      if (this.machineId && this.companyId) {
        // ===== TOGGLE ON (wear parts) =====
        // The inline wear parts panel (right column above
        // Frequently Ordered) handles part selection now.
        // The Continue button just routes to /cart so users
        // can review whatever they queued via the inline
        // ADD buttons. Skips the old navigation to
        // /orders/shopwearparts entirely.
        if (this.configuredWear == true) {
          // Persist the context so the cart page can resolve
          // any references back to (company, machine) the
          // user was working in.
          localStorage.machineId = this.machineId;
          localStorage.poNumber = this.poNumber;
          localStorage.configuredWear = this.configuredWear;
          localStorage.companyId = this.companyId;
          this.$router.push({ name: "Cart" });
          return;
        }

        // ===== TOGGLE OFF (full assembly browse) =====
        // Existing behavior — route to Choose Assembly.
        // Phase 2 will inline this flow as well.
        if (this.fullOrder) {
          this.$router.push({
            name: "Create Order - Choose Assembly",
            params: {
              number: this.poNumber,
              machineId: this.machineId,
              configuredWear: this.configuredWear,
              companyId: this.companyId,
              fullOrder: this.fullOrder,
            },
          });
        } else {
          this.$router.push({
            name: "Create Order - Choose Assembly",
            params: {
              number: this.poNumber,
              machineId: this.machineId,
              configuredWear: this.configuredWear,
              companyId: this.companyId,
            },
          });
        }

        localStorage.machineId = this.machineId;
        localStorage.poNumber = this.poNumber;
        localStorage.configuredWear = this.configuredWear;
        localStorage.companyId = this.companyId;
      } else {
        this.machineError = true;
        this.companyError = true;
        return false;
      }
    },
    // ===== INSIGHTS PANEL METHODS =====
    // Mirrors the Account Dashboard's insights logic so this page
    // benefits from any backend improvements there for free. Single
    // API endpoint (apiService.getInsights), single resolution flow
    // (apiService.resolveTopPartContext), single data shape on
    // `insights.topParts`.
    getInsights() {
      if (!this.companyId) return;
      this.insightsLoading = true;
      // Reset progressive disclosure — a fresh fetch should show
      // the first 5 rows again, not whatever expanded state was
      // left over from a previous load.
      this.displayLimit = 5;
      apiService
        .getInsights({
          companyId: this.companyId,
          allTime: this.insightsPeriod === "alltime",
          // Request a larger pool than the dashboard's default 5 so
          // the See more pagination has something to reveal. If the
          // backend ignores this param, the response shape is
          // unchanged (we still slice client-side via displayLimit).
          take: 50,
        })
        .then((data) => {
          this.insights = data || { topParts: [] };
        })
        .catch(() => {
          // Soft-fail to an empty list — the empty-state UI will
          // surface gracefully without a toast for what's really a
          // best-effort sidebar.
          this.insights = { topParts: [] };
        })
        .finally(() => {
          this.insightsLoading = false;
        });
    },
    switchInsightsPeriod(period) {
      if (this.insightsPeriod === period) return;
      this.insightsPeriod = period;
      // Reset on toggle so the user starts at 5 in the new period.
      this.displayLimit = 5;
      this.getInsights();
    },
    // Bump the visible row count by 5 — purely client-side, the
    // full topParts array is already in memory from the initial
    // getInsights() so no extra API call.
    showMoreParts() {
      this.displayLimit = this.displayLimit + 5;
    },
    // ===== MULTI-SELECT QUEUE =====
    // Each part in the queue carries its own poNumber AND quantity
    // so a single queue can hold parts spread across multiple Orders
    // with per-part quantities. The Add button on each row no longer
    // toggles directly — it opens either the picker (existing Orders
    // to choose from + "use a new PO" option) or the explainer
    // (first-time, or "new PO" pick) so the PO context is always
    // explicit.
    isPartSelected(part) {
      if (!part) return false;
      // True if this part is in EITHER queue (orders or quotes),
      // under any PO. Used by the ADD button in the part list to
      // flip its label to "Edit".
      var inOrder = this.selectedParts.some(function (p) {
        return p.partId === part.partId;
      });
      if (inOrder) return true;
      return this.selectedPartsQuote.some(function (p) {
        return p.partId === part.partId;
      });
    },
    // Helper — which queue (if any) currently holds this part.
    // Returns { destination, index, entry } or null. Used by
    // addToCartClick to pre-fill the modal in edit mode.
    findPartInQueues(part) {
      if (!part) return null;
      var oIdx = this.selectedParts.findIndex(function (p) {
        return p.partId === part.partId;
      });
      if (oIdx >= 0) {
        return {
          destination: "order",
          index: oIdx,
          entry: this.selectedParts[oIdx],
        };
      }
      var qIdx = this.selectedPartsQuote.findIndex(function (p) {
        return p.partId === part.partId;
      });
      if (qIdx >= 0) {
        return {
          destination: "quote",
          index: qIdx,
          entry: this.selectedPartsQuote[qIdx],
        };
      }
      return null;
    },
    // Like findPartInQueues but checks ONE specific destination's queue.
    findPartInDestination(part, dest) {
      if (!part) return null;
      var arr = dest === "quote" ? this.selectedPartsQuote : this.selectedParts;
      var idx = arr.findIndex(function (p) { return p.partId === part.partId; });
      return idx >= 0 ? { destination: dest, index: idx, entry: arr[idx] } : null;
    },
    // Point the modal at a destination and reflect whether the part is ALREADY
    // there: if so → EDIT that line (pre-filled); if not → ADD it here. This is what
    // lets a part live in BOTH the order and a quote — pick the other tile to add it.
    _applyDestinationState(dest) {
      var clean = dest === "quote" ? "quote" : "order";
      this.pendingDestination = clean;
      var existing = this.findPartInDestination(this.pendingPart, clean);
      if (existing) {
        this.pickerMode = "edit";
        this.pendingPoNumber = existing.entry.poNumber || "";
        this.pendingQuantity = Number(existing.entry.quantity) || 1;
        this.useNewPo = true;
      } else {
        this.pickerMode = "add";
        this.pendingPoNumber = "";
        this.pendingQuantity = this.defaultQuantityFor(this.pendingPart);
        var groups = clean === "quote" ? this.groupedQuoteSelections : this.groupedSelections;
        this.useNewPo = groups.length === 0;
      }
    },
    // Sensible default qty when a part is added — uses the historical
    // average per-order from insights data (totalQuantity / timesOrdered)
    // since "frequently ordered" parts are likely ordered in similar
    // quantities each time. Falls back to 1 if the math doesn't yield
    // a usable number.
    // Compute the default qty for a part being added to the
    // staging tray. Three signals, in priority order:
    //
    //   1. part.quantityPerMachine — engineering spec from the
    //      wear-parts catalog ("this assembly needs N of this
    //      part per machine"). Authoritative when present;
    //      that's the number on the product card the user
    //      just clicked Add on.
    //   2. totalQuantity / timesOrdered — Frequently Ordered
    //      insight, "the user typically orders this many at
    //      a time." Reasonable fallback for non-wear parts
    //      that have order history.
    //   3. 1 — when neither signal is available.
    //
    // The wear-parts panel was previously falling through to
    // the insights branch (which always returned 1 since
    // wear parts don't carry totalQuantity), so users had to
    // manually edit qty after every add. The cart-side
    // payload assembly already supports any qty value, so
    // surfacing the right default here is the only change
    // needed.
    defaultQuantityFor(part) {
      if (!part) return 1;
      var perMachine = Number(part.quantityPerMachine || part.QuantityPerMachine) || 0;
      if (perMachine > 0) return perMachine;
      var total = Number(part.totalQuantity) || 0;
      var times = Number(part.timesOrdered) || 1;
      if (total <= 0 || times <= 0) return 1;
      return Math.max(1, Math.round(total / times));
    },
    // ===== INLINE WEAR PARTS PANEL METHODS =====
    // These power the wear parts panel that appears in the
    // right column above Frequently Ordered when toggle is
    // on + machine selected. Replaces navigation to
    // /orders/shopwearparts entirely.

    // Reset all panel-local state. Called on toggle-off,
    // machine change, and company change so stale data
    // never bleeds into the next selection cycle. The
    // wearLoadKey bump invalidates any in-flight responses
    // — see loadInlineWearAssemblyOptions for how callers
    // check it.
    resetInlineWearState() {
      this.wearLoadKey++;
      this.wearAssemblyOptions = [];
      this.selectedWearAssemblyId = null;
      this.wearAssemblies = [];
      this.expandedWearAssemblies = {};
      this.wearAssembliesLoading = false;
      this.wearPartsLoading = false;
      this.assemblyImage = null;
      this.selectedSubAssemblyId = null;
      this.subAssemblyImage = null;
      // Clear Semantic UI's displayed text on the assembly
      // dropdown — setting selectedWearAssemblyId = null
      // syncs the hidden input via v-model but doesn't
      // touch the visible label. Wrapped because the
      // dropdown may not be mounted (Step 3 hidden when
      // wear-parts-only toggle is off, or panel not yet
      // rendered on first reset).
      try {
        $("#WearAssemblySelect").dropdown("clear");
      } catch (e) {
        // Dropdown not in DOM — harmless.
      }
    },

    // Populate the assembly dropdown for the current
    // machine. Uses the same endpoint the canonical
    // ShopWearParts.vue uses to populate its assembly picker
    // (apiService.getSelectItemsByCompanyMachine →
    // /assembly/GetSelectItemsByCompanyMachine/{machineId}).
    // Returns the FULL assembly list for the machine in
    // {value, text} shape — wear-eligibility filtering happens
    // downstream when the user picks an assembly and we call
    // getAssembliesWithWearParts to load the actual parts.
    //
    // Earlier wired this to apiService.getAssembliesByMachine,
    // which hits /wear/GetAssemblyByMachineAndCompany — wrong
    // endpoint (singular Assembly, returns empty/wrong shape
    // for this UI). The dropdown was always empty as a result.
    async loadInlineWearAssemblyOptions() {
      console.log("[Create] inline wear: loadInlineWearAssemblyOptions called", {
        companyId: this.companyId,
        machineId: this.machineId,
        configuredWear: this.configuredWear,
      });
      if (!this.machineId) {
        console.log("[Create] inline wear: bailing — missing machineId");
        return;
      }
      var key = ++this.wearLoadKey;
      this.wearAssembliesLoading = true;
      try {
        var data = await apiService.getSelectItemsByCompanyMachine({
          id: this.machineId,
        });
        console.log("[Create] inline wear: API response", { key, data });
        // Stale response — user changed the machine before
        // this resolved. Bail without touching state.
        if (key !== this.wearLoadKey) {
          console.log("[Create] inline wear: stale key, ignoring response");
          return;
        }
        // getSelectItemsByCompanyMachine returns {value, text}
        // pairs already, but tolerate alternate shapes in case
        // the backend ever changes serialization.
        var rows = Array.isArray(data) ? data : [];
        var opts = rows.map(function (row) {
          return {
            value: row.value || row.assemblyId || row.id,
            text: row.text || row.assemblyName || row.name,
            // Assembly drawing path (backend returns Image = Assembly.ImagePath);
            // empty/null = no drawing. Drives the at-a-glance drawing icon in the menu.
            image: row.image || row.Image || "",
          };
        }).filter(function (r) {
          return r.value && r.text;
        });
        console.log("[Create] inline wear: normalized options", opts);
        this.wearAssemblyOptions = opts;
        // Initialize / re-bind the Semantic UI dropdown now
        // that Vue has rendered the new <div class="item">
        // children. Calling .dropdown() is idempotent —
        // first run initializes, subsequent runs re-read
        // the menu DOM. The $nextTick wait is required
        // because the v-for items haven't entered the DOM
        // until the current tick finishes. Wrapped in
        // try/catch in case Step 3 isn't mounted yet (the
        // wear-parts-only toggle controls visibility).
        var self = this;
        this.$nextTick(function () {
          try {
            $("#WearAssemblySelect").dropdown({
              fullTextSearch: true,
              direction: "downward", // see main init — keep menu below the field
            });
          } catch (e) {
            // Step 3 may not be in the DOM — harmless,
            // dropdown will init when it appears.
          }
        });
        // Modify-flow hydration step 3 (Step 3 of the
        // create page, fittingly): if we arrived via the
        // Modify Order modal with a target assembly id,
        // pick it up now that options are available. The
        // selectedWearAssemblyId watcher will then trigger
        // the parts loader, which is itself the place
        // pendingModifySubAssemblyId gets consumed.
        if (this.pendingModifyAssemblyId) {
          var target = String(this.pendingModifyAssemblyId);
          var match = opts.find(function (o) {
            return String(o.value) === target;
          });
          if (match) {
            this.selectedWearAssemblyId = match.value;
            // Programmatic selection — Semantic UI's
            // displayed text won't follow the hidden input
            // change on its own, so nudge it.
            this.$nextTick(function () {
              try {
                $("#WearAssemblySelect").dropdown("set selected", match.value);
              } catch (e) {}
            });
          } else {
            console.warn(
              "[Create] inline wear: pendingModifyAssemblyId " + target +
              " did not match any loaded option; clearing"
            );
          }
          this.pendingModifyAssemblyId = null;
        }

        // Header-search hydration (parallels the modify-flow
        // block above). When the user clicked a part from the
        // floating header search, the assembly NAME (not id —
        // the suggestion API returns display strings, not the
        // machine-scoped assembly ids the form needs) was
        // stashed in pendingHeaderAssemblyName. Resolve it
        // against the freshly-loaded options now.
        //
        // Skipped when an assembly has already been selected
        // by either the modify-flow above OR by the user
        // clicking the dropdown between mount and this load
        // resolving — modify wins on collision; user input
        // always wins.
        //
        // Match strategy mirrors applyPendingHeaderMachine:
        // cascading exact → alphanumeric → substring, since
        // the suggestion API and the assembly-options
        // endpoint are different sources and historical
        // data drifts on whitespace, casing, and punctuation.
        // Verbose console logging surfaces what was tried
        // when no match lands, which is the only on-page
        // diagnostic the user has for this silent UX.
        if (this.pendingHeaderAssemblyName && !this.selectedWearAssemblyId) {
          var headerAsmTarget = String(this.pendingHeaderAssemblyName);

          var asmNorm = function (s) {
            return String(s == null ? "" : s)
              .trim().toLowerCase().replace(/\s+/g, " ");
          };
          var asmNormStrong = function (s) {
            return String(s == null ? "" : s)
              .toLowerCase().replace(/[^a-z0-9]/g, "");
          };
          var asmTextCandidate = function (o) {
            return o.text || o.name || o.assemblyName || "";
          };

          var nAsmTarget = asmNorm(headerAsmTarget);
          var nAsmTargetStrong = asmNormStrong(headerAsmTarget);

          console.log("[Search] header search: resolving assembly", {
            target: headerAsmTarget,
            normalized: nAsmTarget,
            optionCount: opts.length,
            sample: opts.slice(0, 8).map(function (o) {
              return {
                value: o.value,
                text: o.text,
                name: o.name,
                assemblyName: o.assemblyName,
              };
            }),
          });

          var asmMatch = null;
          var asmStrategy = null;

          // ID-FIRST disambiguation. The suggestion's assemblyId
          // is unique where its name is not (many assemblies
          // share a name, some under the same machine). If that
          // id matches a top-level option's .value, select it
          // outright and skip the ambiguous name cascade. This
          // only hits when the picked assembly is itself a top-
          // level one; the common case (id is a sub-assembly
          // leaf) misses here and falls through to the deep
          // search below, which applies the same id preference.
          if (this.pendingHeaderAssemblyId) {
            var wantAsmId = String(this.pendingHeaderAssemblyId);
            asmMatch = opts.find(function (o) {
              return String(o.value) === wantAsmId;
            });
            if (asmMatch) asmStrategy = "id";
          }

          if (!asmMatch) {
            asmMatch = opts.find(function (o) {
              return asmNorm(asmTextCandidate(o)) === nAsmTarget;
            });
            if (asmMatch) asmStrategy = "exact";
          }

          if (!asmMatch) {
            asmMatch = opts.find(function (o) {
              return asmNormStrong(asmTextCandidate(o)) === nAsmTargetStrong;
            });
            if (asmMatch) asmStrategy = "alphanumeric";
          }

          if (!asmMatch) {
            asmMatch = opts.find(function (o) {
              var n = asmNorm(asmTextCandidate(o));
              if (!n || !nAsmTarget) return false;
              return n.indexOf(nAsmTarget) >= 0
                  || nAsmTarget.indexOf(n) >= 0;
            });
            if (asmMatch) asmStrategy = "substring";
          }

          if (asmMatch) {
            console.log(
              "[Search] header search: matched assembly via '" +
              asmStrategy + "' strategy:",
              {
                value: asmMatch.value,
                text: asmTextCandidate(asmMatch),
              }
            );
            this.selectedWearAssemblyId = asmMatch.value;
            this.$nextTick(function () {
              try {
                $("#WearAssemblySelect").dropdown(
                  "set selected", asmMatch.value
                );
              } catch (e) {}
            });
            this.pendingHeaderAssemblyName = null;
          } else {
            // Top-level miss is the COMMON case — the
            // suggestion API returns the sub-assembly name
            // (the leaf the user picked from autocomplete),
            // but wearAssemblyOptions only holds top-level
            // assemblies. Move the name to the sub-assembly
            // queue and start the deep search to find which
            // parent contains it. Keep the original on the
            // assembly queue null so the watcher safety net
            // doesn't double-fire.
            console.log(
              "[Search] header search: no top-level match for '" +
              headerAsmTarget + "' — promoting to sub-assembly " +
              "deep search across " + opts.length + " top-level " +
              "assemblies. Available top-level labels:",
              opts.map(function (o) { return asmTextCandidate(o); })
            );
            this.pendingHeaderSubAssemblyName = String(this.pendingHeaderAssemblyName);
            this.pendingHeaderAssemblyName = null;
            this.applyHeaderAssemblyDeepSearch();
          }
        }
      } catch (e) {
        console.warn("[Create] inline wear: assembly options load failed:", e);
        if (key !== this.wearLoadKey) return;
        this.wearAssemblyOptions = [];
      } finally {
        // Always clear the loading flag — the stale-key
        // guard above protects wearAssemblyOptions, but
        // loading is UI state and a transient under-loading
        // flicker is better than infinite "Loading…" if
        // multiple watchers fire on mount and key bumping
        // strands the flag.
        this.wearAssembliesLoading = false;
      }
    },

    // Load the sub-assemblies + wear parts for the picked
    // assembly. Same endpoint ShopWearParts.vue uses —
    // returns sub-assemblies grouped, each with a parts
    // array of wear-eligible parts. Defaults every sub-
    // assembly to expanded so the user sees parts
    // immediately without an extra click.
    async loadInlineWearPartsForAssembly(assemblyId) {
      if (!assemblyId || !this.machineId) return;
      var key = ++this.wearLoadKey;
      this.wearPartsLoading = true;
      try {
        var data = await apiService.getAssembliesWithWearParts({
          assemblyId: assemblyId,
          companyMachineId: this.machineId,
        });
        if (key !== this.wearLoadKey) return;
        // Sort sub-assemblies alphabetically — the API
        // returns them in part-sequence order which isn't
        // useful when there are 6+ groups.
        var list = (data || []).slice().sort(function (a, b) {
          return (a.assemblyName || "").localeCompare(b.assemblyName || "");
        });
        // Header-search part surfacing: if the user came
        // here via the floating header search, pull the
        // matching part code to the top of its sub-
        // assembly's parts list. Mutates `list` in place
        // BEFORE the reactive assignment below so the
        // reorder rides on the same single update. Also
        // stashes _pendingHeaderExpandAssemblyId for the
        // post-assignment auto-expand step.
        this._floatHeaderSearchPartToTop(list);
        this.wearAssemblies = list;
        // Auto-expand the first sub-assembly so the user
        // immediately sees parts rather than a stack of
        // collapsed cards (which is easy to miss). When
        // there's more than one, the template renders a
        // "more categories below" hint so users know to
        // expand the rest. When there's only one, expand-
        // ing it is the obvious default — there's nothing
        // else to choose.
        var initialExpanded = {};
        if (list.length > 0) {
          initialExpanded[list[0].assemblyId] = true;
        }
        // If the part-floater stashed a sub-assembly id,
        // mark it expanded too so the floated part is
        // visible without an extra click. Override here
        // (rather than in the floater itself) because
        // expandedWearAssemblies is reassigned wholesale
        // a line down — any pre-assignment $set would be
        // clobbered.
        if (this._pendingHeaderExpandAssemblyId != null) {
          initialExpanded[this._pendingHeaderExpandAssemblyId] = true;
          this._pendingHeaderExpandAssemblyId = null;
        }
        this.expandedWearAssemblies = initialExpanded;
        // Modify-flow hydration step 4: pick up a pending
        // sub-assembly target if we arrived via the Modify
        // Order modal. Setting selectedSubAssemblyId fires
        // its watcher which auto-expands that card and
        // loads its image — same effect as the user
        // picking it from the dropdown manually.
        if (this.pendingModifySubAssemblyId) {
          var subTarget = String(this.pendingModifySubAssemblyId);
          var subMatch = list.find(function (sa) {
            return String(sa.assemblyId) === subTarget;
          });
          if (subMatch) {
            this.selectedSubAssemblyId = subMatch.assemblyId;
          }
          this.pendingModifySubAssemblyId = null;
        }

        // Header-search sub-assembly hydration. The deep
        // search resolved which parent contains a matching
        // sub-assembly and set selectedWearAssemblyId; this
        // is the loader run for that parent. Multi-strategy
        // name match against the freshly-loaded sub list
        // (same exact/alphanumeric/substring cascade we use
        // for the machine and top-level matchers) and set
        // selectedSubAssemblyId on hit.
        //
        // Skipped if the modify flow already picked one
        // (modify wins on collision) or if the user clicked
        // a sub-assembly between mount and now.
        if ((this.pendingHeaderSubAssemblyName || this.pendingHeaderSubAssemblyId)
            && !this.selectedSubAssemblyId) {
          var hsTarget = String(this.pendingHeaderSubAssemblyName || "");
          var hsNorm = function (s) {
            return String(s == null ? "" : s)
              .trim().toLowerCase().replace(/\s+/g, " ");
          };
          var hsNormStrong = function (s) {
            return String(s == null ? "" : s)
              .toLowerCase().replace(/[^a-z0-9]/g, "");
          };
          var hsNT = hsNorm(hsTarget);
          var hsNTS = hsNormStrong(hsTarget);

          // ID-FIRST: ?subAssemblyId= is the exact child
          // Assembly.Id (Email Insights deep link) — names like
          // "None" repeat across parents, the id never does.
          var hsMatch = null;
          if (this.pendingHeaderSubAssemblyId) {
            var hsWantId = String(this.pendingHeaderSubAssemblyId);
            hsMatch = list.find(function (sa) {
              return String(sa.assemblyId) === hsWantId;
            });
          }
          if (!hsMatch && hsTarget) {
            hsMatch = list.find(function (sa) {
              return hsNorm(sa.assemblyName) === hsNT;
            }) || list.find(function (sa) {
              return hsNormStrong(sa.assemblyName) === hsNTS;
            }) || list.find(function (sa) {
              var n = hsNorm(sa.assemblyName);
              return n && hsNT && (
                n.indexOf(hsNT) >= 0 || hsNT.indexOf(n) >= 0
              );
            });
          }

          if (hsMatch) {
            console.log(
              "[Search] header search (wear loader): " +
              "matched sub-assembly '" + hsMatch.assemblyName +
              "' (id " + hsMatch.assemblyId + ")"
            );
            this.selectedSubAssemblyId = hsMatch.assemblyId;
            // Auto-expand the matched card so the part the
            // user originally clicked is visible without
            // any further action.
            this.$set(
              this.expandedWearAssemblies,
              hsMatch.assemblyId,
              true
            );
          } else {
            console.warn(
              "[Search] header search (wear loader): no " +
              "sub-assembly match for '" + hsTarget +
              "'. Available sub-assembly names:",
              list.map(function (sa) { return sa.assemblyName; })
            );
          }
          this.pendingHeaderSubAssemblyName = null;
          this.pendingHeaderSubAssemblyId = 0;
        }
      } catch (e) {
        if (key !== this.wearLoadKey) return;
        console.warn("[Create] inline wear: parts load failed:", e);
        this.wearAssemblies = [];
      } finally {
        // Same always-clear pattern as above.
        this.wearPartsLoading = false;
      }
    },

    // Dispatcher — picks between the wear-only loader and
    // the all-parts loader based on the configuredWear
    // toggle. Both populate the same `wearAssemblies` array
    // (the shape is identical: array of {assemblyId,
    // assemblyName, parts: [...]}), so the accordion
    // template doesn't care which path filled it.
    loadInlinePartsForSelectedAssembly() {
      if (!this.selectedWearAssemblyId) return;
      if (this.configuredWear) {
        this.loadInlineWearPartsForAssembly(this.selectedWearAssemblyId);
      } else {
        this.loadInlineAllPartsForAssembly(this.selectedWearAssemblyId);
      }
    },

    // All-parts variant — calls getAssemblyWithParts (the
    // same API ShopWearParts.vue uses when "Show wear parts
    // only" is OFF). Returns sub-assemblies grouped, each
    // with a parts array of every part on the assembly,
    // wear-eligible or not. Normalizes to the same shape
    // wear-parts uses so the accordion template can render
    // both transparently.
    async loadInlineAllPartsForAssembly(assemblyId) {
      if (!assemblyId || !this.machineId) return;
      var key = ++this.wearLoadKey;
      this.wearPartsLoading = true;
      try {
        var data = await apiService.getAssemblyWithParts({
          id: assemblyId,
        });
        console.log("[Create] inline parts: getAssemblyWithParts response", { key, data });
        if (key !== this.wearLoadKey) return;
        // Normalize each sub-assembly to the shape the
        // accordion expects. The all-parts response uses
        // `text` for the display name; wear-parts uses
        // `assemblyName`. Map both to assemblyId/assemblyName.
        var rows = Array.isArray(data) ? data : [];
        var list = rows.map(function (item, idx) {
          return {
            assemblyId: item.assemblyId || item.id || item.value || ("subassembly-" + idx),
            assemblyName: item.assemblyName || item.text || item.name || "Sub-assembly " + (idx + 1),
            parts: (item.parts || []).map(function (p) {
              // Mirror ShopWearParts' default-quantity
              // behavior: pre-fill each row with
              // quantityPerMachine so users can hit Add
              // without having to fiddle with quantities.
              return Object.assign({}, p, {
                quantity: p.quantity || p.quantityPerMachine || 1,
              });
            }),
          };
        }).sort(function (a, b) {
          return (a.assemblyName || "").localeCompare(b.assemblyName || "");
        });
        // Header-search part surfacing — see the wear-loader
        // version above for full reasoning. Same operation:
        // mutate the local list in place before the
        // wearAssemblies assignment so the reorder rides on
        // the single reactive update, and stash the
        // sub-assembly id for the post-assignment expand.
        this._floatHeaderSearchPartToTop(list);
        this.wearAssemblies = list;
        // Auto-expand the first sub-assembly, same as the
        // wear-parts path. Template hint covers the >1 case.
        var initialExpanded = {};
        if (list.length > 0) {
          initialExpanded[list[0].assemblyId] = true;
        }
        if (this._pendingHeaderExpandAssemblyId != null) {
          initialExpanded[this._pendingHeaderExpandAssemblyId] = true;
          this._pendingHeaderExpandAssemblyId = null;
        }
        this.expandedWearAssemblies = initialExpanded;
        // Modify-flow hydration step 4 (all-parts variant) —
        // mirrors the wear-parts loader's sub-assembly pickup.
        if (this.pendingModifySubAssemblyId) {
          var subTarget = String(this.pendingModifySubAssemblyId);
          var subMatch = list.find(function (sa) {
            return String(sa.assemblyId) === subTarget;
          });
          if (subMatch) {
            this.selectedSubAssemblyId = subMatch.assemblyId;
          }
          this.pendingModifySubAssemblyId = null;
        }

        // Header-search sub-assembly hydration (all-parts
        // variant). This is the path the header search
        // lands in by default, since allParts=true on the
        // URL flips configuredWear off and the dispatcher
        // routes here. Logic mirrors the wear loader's
        // version above — multi-strategy name match,
        // auto-expand on hit, console diagnostic on miss.
        if ((this.pendingHeaderSubAssemblyName || this.pendingHeaderSubAssemblyId)
            && !this.selectedSubAssemblyId) {
          var hsTarget = String(this.pendingHeaderSubAssemblyName || "");
          var hsNorm = function (s) {
            return String(s == null ? "" : s)
              .trim().toLowerCase().replace(/\s+/g, " ");
          };
          var hsNormStrong = function (s) {
            return String(s == null ? "" : s)
              .toLowerCase().replace(/[^a-z0-9]/g, "");
          };
          var hsNT = hsNorm(hsTarget);
          var hsNTS = hsNormStrong(hsTarget);

          // ID-FIRST — see the wear loader's block above.
          var hsMatch = null;
          if (this.pendingHeaderSubAssemblyId) {
            var hsWantId = String(this.pendingHeaderSubAssemblyId);
            hsMatch = list.find(function (sa) {
              return String(sa.assemblyId) === hsWantId;
            });
          }
          if (!hsMatch && hsTarget) {
            hsMatch = list.find(function (sa) {
              return hsNorm(sa.assemblyName) === hsNT;
            }) || list.find(function (sa) {
              return hsNormStrong(sa.assemblyName) === hsNTS;
            }) || list.find(function (sa) {
              var n = hsNorm(sa.assemblyName);
              return n && hsNT && (
                n.indexOf(hsNT) >= 0 || hsNT.indexOf(n) >= 0
              );
            });
          }

          if (hsMatch) {
            console.log(
              "[Search] header search (all-parts loader): " +
              "matched sub-assembly '" + hsMatch.assemblyName +
              "' (id " + hsMatch.assemblyId + ")"
            );
            this.selectedSubAssemblyId = hsMatch.assemblyId;
            this.$set(
              this.expandedWearAssemblies,
              hsMatch.assemblyId,
              true
            );
          } else {
            console.warn(
              "[Search] header search (all-parts loader): " +
              "no sub-assembly match for '" + hsTarget +
              "'. Available sub-assembly names:",
              list.map(function (sa) { return sa.assemblyName; })
            );
          }
          this.pendingHeaderSubAssemblyName = null;
          this.pendingHeaderSubAssemblyId = 0;
        }
      } catch (e) {
        if (key !== this.wearLoadKey) return;
        console.warn("[Create] inline parts: load failed:", e);
        this.wearAssemblies = [];
      } finally {
        this.wearPartsLoading = false;
      }
    },

    // Expand/collapse one sub-assembly card.
    toggleInlineWearAssembly(assemblyId) {
      var current = this.expandedWearAssemblies[assemblyId];
      this.$set(this.expandedWearAssemblies, assemblyId, !current);
    },

    // Read expanded state — defaults to FALSE so a freshly
    // loaded panel starts with all sub-assemblies collapsed.
    // Users expand the ones they care about.
    isInlineWearAssemblyExpanded(assemblyId) {
      return this.expandedWearAssemblies[assemblyId] === true;
    },

    // ADD button for a wear part. Maps the wear-parts-API
    // part object to the shape addToCartClick expects
    // (partId, partCode, description, ...) and hands off.
    // addToCartClick takes care of:
    //   - the combined Add-to-Cart modal (add-mode or edit-mode)
    //   - pushing into selectedParts or selectedPartsQuote based
    //     on the destination the user picks in the modal
    //   - debounced sync + cart-bus emit (header counter refresh)
    // No new staging plumbing needed — this is the whole
    // point of routing through the existing gate.
    // ===== GUIDE-TOUR PREVIEW CONTEXT (admin builder) =====
    // Snapshot the current "Find Your Part" selections to localStorage ON DEMAND
    // (the builder emits "tour:capture-search-ctx" at target-pick time and reads
    // this synchronously right after).
    _tourWriteSearchCtx() {
      try {
        localStorage.setItem("jcs_tour_search_ctx", JSON.stringify({
          machineId: this.machineId || null,
          assemblyId: this.selectedWearAssemblyId || null,
          subAssemblyId: this.selectedSubAssemblyId || null,
          filter: this.partFilter || "",
          configuredWear: this.configuredWear !== false,
        }));
      } catch (e) {}
    },
    // Restore a step's saved selections so "Preview / resize" lands on the right
    // screen. Best-effort + additive: any gap is covered by the builder's auto-snap.
    async restoreSearchContext(ctx) {
      if (!ctx) return;
      try {
        if (ctx.machineId) {
          // In-memory ONLY (the part loaders read this.machineId). Do NOT persist to
          // localStorage.machineId — persisting leaked the machine into normal
          // navigation, so New Order auto-restored it instead of starting fresh on
          // step 2 ("select your machine").
          this.machineId = ctx.machineId;
        }
        var wantWear = ctx.configuredWear !== false;
        if (this.configuredWear !== wantWear) {
          this.configuredWear = wantWear;
          await this.$nextTick();
        }
        // Setting the assembly fires its watcher (resets the sub + kicks a
        // fire-and-forget parts load). We ALSO await the loader directly so the
        // sub-assembly + filter apply only once parts exist; the wearLoadKey guard
        // lets this later (awaited) load win over the watcher's.
        this.selectedWearAssemblyId = ctx.assemblyId || null;
        this.$nextTick(function () {
          try {
            var $a = window.$ && window.$("#WearAssemblySelect");
            if ($a && $a.dropdown) $a.dropdown("set selected", String(ctx.assemblyId || ""));
          } catch (e) {}
        });
        if (ctx.assemblyId) {
          if (wantWear) await this.loadInlineWearPartsForAssembly(ctx.assemblyId);
          else await this.loadInlineAllPartsForAssembly(ctx.assemblyId);
        }
        this.selectedSubAssemblyId = ctx.subAssemblyId || null;
        this.partFilter = ctx.filter || "";
      } catch (e) {}
    },
    addInlineWearPart(part, subAssembly) {
      if (!part) return;
      // NOTE: the Add-to-Cart modal is intentionally NOT suppressed during a guide
      // tour — showing it (PO# + quantity → "Add to Cart") IS the point of the
      // final tour step. The focus-trap that originally prompted suppression is
      // handled by no-enforce-focus on #addToCartModal, and the tour widget raises
      // the modal above its click-guard so it's usable. (Earlier suppression here
      // broke the cart in normal use AND killed the step-8 demo — see
      // GuideTourWidget._advanceClick for the modal-as-walkthrough handling.)
      var mapped = {
        partId: part.partId || part.id || part.Id || part.PartId,
        partCode: part.code || part.Code || part.partCode,
        description: part.description || part.Description,
        // The picker doesn't strictly need these but they
        // help downstream code that introspects the part
        // (e.g. row rendering on the cart page).
        price: part.price || part.Price,
        quantityPerMachine: part.quantityPerMachine || part.QuantityPerMachine || 1,
        // Tag the source so analytics / cart-page logic
        // can tell where the part was added from. Reflects
        // the active mode — wear when the toggle is on,
        // false when the user is browsing all parts.
        sourceAssemblyId: subAssembly && subAssembly.assemblyId,
        sourceAssemblyName: subAssembly && subAssembly.assemblyName,
        isWearPart: this.configuredWear === true,
      };
      // Bail with a friendly toast if the API gave us a
      // shape we can't normalize (shouldn't happen but
      // defensive against schema drift).
      if (!mapped.partId || !mapped.partCode) {
        console.warn("[Create] inline wear: unrecognized part shape", part);
        return;
      }
      this.addToCartClick(mapped);
    },

    // ===== ADD-TO-CART ENTRY POINT =====
    // Single gate for any "Add" click on the Search page. Opens
    // the combined modal, pre-filled in edit mode if the part is
    // already in one of the queues.
    addToCartClick(part) {
      if (!part || !part.partId || !part.partCode) return;

      this.pendingPart = part;

      if (this.forcedDestination) {
        // Intent nav link (New Order / New Quote) is AUTHORITATIVE: open on THAT
        // destination, even when the part is already staged in the OTHER one. A part can
        // live in both an order and a quote at once, so on ?intent=quote a part that's
        // only in the order opens ready to ADD to the quote — NOT editing the order line.
        // _applyDestinationState reflects edit (part already in this destination) vs add
        // (not yet); the other tile still shows "✓ in cart" and is one click away.
        this._applyDestinationState(this.forcedDestination);
      } else {
        var existing = this.findPartInQueues(part);
        if (existing) {
          // No intent → open on whichever destination the part is already staged in.
          this._applyDestinationState(existing.destination);
        } else if (this.selectedParts.length > 0 || this.selectedPartsQuote.length > 0) {
          // Progressive disclosure: tray already has items → make the user pick a
          // destination before the PO/Qty section reveals.
          this.pickerMode = "add";
          this.pendingDestination = null;
          this.pendingPoNumber = "";
          this.pendingQuantity = this.defaultQuantityFor(part);
          this.useNewPo = false;
        } else {
          // Empty tray → default to Order, full modal visible immediately.
          this._applyDestinationState("order");
        }
      }

      this.showAddToCartModal = true;
    },
    // ===== DESTINATION CHANGE =====
    // Called when the user clicks Order or Quote in the modal's
    // destination toggle. Resets the PO state because the picker
    // is destination-scoped — switching from order to quote means
    // a different set of existing groups apply, and any PO the
    // user might have selected for the previous destination
    // doesn't carry over.
    onPickDestination(dest) {
      // Reflect whether the part is already in the picked destination: update that
      // existing line (edit), or add it there (add) — so the same part can go to both.
      this._applyDestinationState(dest);
    },
    // ===== EXISTING PO CHOSEN =====
    // User clicked one of the existing-group buttons in the picker.
    // Set the PO and lock useNewPo to false so the input stays
    // hidden — the chosen group's PO is what we'll save with.
    onPickExistingPo(poNumber) {
      this.pendingPoNumber = poNumber;
      this.useNewPo = false;
    },
    // ===== NEW PO REQUESTED =====
    // User clicked "+ New PO" in the picker. Reveal the input and
    // clear the field so they're not editing a previously-picked
    // PO. Auto-focus once it's in the DOM.
    onPickNewPo() {
      this.pendingPoNumber = "";
      this.useNewPo = true;
      this.$nextTick(() => {
        var el = document.getElementById("addToCartPoInput");
        if (el) el.focus();
      });
    },
    // ===== CARTWIDGET EVENT BRIDGE =====
    // CartWidget (controlled mode) emits these three events when the
    // user interacts with the staging tray. Each handler is a thin
    // wrapper around the page's existing mutation logic:
    //
    //   onWidgetRemove     → removeQueuedPart      (existing)
    //   onWidgetClear      → clearSelectedParts    (existing)
    //   onWidgetQtyChange  → direct part.quantity  (deep watcher fires)
    //
    // Keeping the wrappers thin means the rest of the file (picker
    // modal, save flow, persistence, etc.) doesn't need to know
    // anything changed in the staging tray — it still reads / writes
    // selectedParts and selectedPartsQuote the same way.
    onWidgetRemove(event) {
      if (!event || !event.part) return;
      this.removeQueuedPart(event.part, event.destination);
    },
    onWidgetClear(event) {
      if (!event) return;
      this.clearSelectedParts(event.destination);
    },
    onWidgetQtyChange(event) {
      // The widget emits the same part object reference it received
      // via the parts/quoteParts prop, so mutating event.part.quantity
      // updates the row inside selectedParts (or selectedPartsQuote)
      // directly. The deep watcher on those arrays then fires
      // scheduleSyncToCart / scheduleSyncToQuoteCart automatically —
      // no need to call them from here.
      if (!event || !event.part) return;
      if (event.quantity == null || !isFinite(event.quantity)) return;
      event.part.quantity = event.quantity;
    },
    // Used by Tray chips and the Clear-all action to remove queued
    // parts. Each chip × removes a single (part, PO) pairing — same
    // part code under a different PO stays put.
    // Remove a single (part, PO) pairing from whichever queue holds
    // it. Caller passes destination so we know which queue + which
    // sync to fire. Defaults to 'order' for backward compatibility
    // with any caller that forgets to pass it.
    removeQueuedPart(part, destination) {
      if (!part) return;
      var dest = destination === "quote" ? "quote" : "order";
      if (dest === "quote") {
        this.selectedPartsQuote = this.selectedPartsQuote.filter(function (p) {
          return !(p.partId === part.partId && p.poNumber === part.poNumber);
        });
        this.scheduleSyncToQuoteCart();
      } else {
        this.selectedParts = this.selectedParts.filter(function (p) {
          return !(p.partId === part.partId && p.poNumber === part.poNumber);
        });
        this.scheduleSyncToCart();
      }
    },
    // Clear one or both queues. No arg = clear both (legacy
    // callers); 'order' or 'quote' = clear just that one.
    clearSelectedParts(destination) {
      if (!destination) {
        this.selectedParts = [];
        this.selectedPartsQuote = [];
        this.clearSelectionsStorage();
        this.clearSelectionsStorageQuote();
        this.scheduleSyncToCart();
        this.scheduleSyncToQuoteCart();
        return;
      }
      var dest = destination === "quote" ? "quote" : "order";
      if (dest === "quote") {
        this.selectedPartsQuote = [];
        this.clearSelectionsStorageQuote();
        this.scheduleSyncToQuoteCart();
      } else {
        this.selectedParts = [];
        this.clearSelectionsStorage();
        this.scheduleSyncToCart();
      }
    },

    // ===== LOCAL STORAGE PERSISTENCE FOR THE QUEUE =====
    // The queue lives in component state, which is wiped whenever
    // the user navigates away from /orders/create (cart →
    // breadcrumb → back, or any router push). Persisting it to
    // localStorage makes the staging tray survive navigation so
    // the user can pop over to the cart, come back, and continue
    // building their order.
    //
    // STORAGE SHAPE: per-company keys —
    //   jcsteele.insights.selections.<companyId>
    // This matches the cart's per-company behavior, so an admin
    // who shops as multiple companies has an independent queue
    // for each one. Switching contexts saves the outgoing queue
    // under its company's key and hydrates the incoming queue
    // from the new company's key.
    //
    // Cleared explicitly (per company) when:
    //   - the user clicks "Clear all" (clearSelectedParts)
    //   - saveCart succeeds for that company (parts now committed)
    _selectionsStorageKey(companyId) {
      var cid = companyId || this.companyId;
      if (!cid) return null;
      return "jcsteele.insights.selections." + cid;
    },
    saveSelectionsToStorage(forCompanyId) {
      try {
        // Allow the caller to specify which company's slot to write
        // to — the companyId watcher uses this to persist the
        // outgoing queue under the OLD company's key after
        // this.companyId has already moved on to the new one.
        var key = this._selectionsStorageKey(forCompanyId);
        if (!key) return;

        if (!this.selectedParts || this.selectedParts.length === 0) {
          localStorage.removeItem(key);
          return;
        }
        var payload = {
          companyId: forCompanyId || this.companyId,
          // The cart this queue is currently linked to (if any).
          // Captured at save time so hydration on next mount can
          // detect whether the cart still exists. When the user
          // places the order or saves the cart, localStorage.cartId
          // changes (new cart) or is cleared, and that mismatch
          // is our signal to drop the queue rather than restore
          // it against a cart that's no longer relevant.
          //
          // null until the user clicks Continue with N parts the
          // first time — pre-Continue queues persist freely
          // because they aren't linked to any cart yet.
          cartId: localStorage.cartId
            ? String(localStorage.cartId)
            : null,
          // Persisted committed snapshot — restored on hydration
          // so the "Go to Cart" / "Continue with N parts" button
          // state survives navigation. Without persisting this,
          // clicking back into the page would reset the snapshot
          // to null and the button would always show "Continue".
          committedSnapshot: this.committedSnapshot,
          // Strip any internal-resolution fields
          // (_companyMachineId, _assemblyId, _resolveDiagnostics)
          // before persisting. They're recomputed via
          // getPartContext on each save attempt, and storing them
          // would just leak transient resolution state across
          // navigations.
          selectedParts: this.selectedParts.map(function (p) {
            return {
              partId: p.partId,
              partCode: p.partCode,
              description: p.description,
              poNumber: p.poNumber,
              quantity: p.quantity,
            };
          }),
          savedAt: Date.now(),
        };
        localStorage.setItem(key, JSON.stringify(payload));
      } catch (e) {
        // Quota errors or disabled storage shouldn't break the
        // page — worst case the queue won't persist, which is the
        // pre-feature behavior anyway.
        console.warn("[Insights] Could not persist selections", e);
      }
    },
    loadSelectionsFromStorage() {
      try {
        var key = this._selectionsStorageKey();
        if (!key) return;

        var raw = localStorage.getItem(key);
        if (!raw) {
          // No saved queue for the active company — reset the
          // in-memory queue so a previous company's queue doesn't
          // bleed into this context.
          this.selectedParts = [];
          this.committedSnapshot = null;
          return;
        }
        var saved = JSON.parse(raw);
        if (!saved || !Array.isArray(saved.selectedParts)) {
          this.selectedParts = [];
          this.committedSnapshot = null;
          return;
        }

        // Cart-linkage check: if this queue was tagged with a
        // cartId at save time, that means the user had clicked
        // Continue and committed the parts to a specific cart.
        // We only restore the queue if that same cart is still
        // the user's active cart — any of the following kill
        // the link and clear the queue:
        //
        //   - localStorage.cartId is missing      (cart cleared)
        //   - localStorage.cartId differs         (cart placed →
        //     new cart started, or cart saved →
        //     archived to saved-carts list)
        //
        // Pre-Continue queues (cartId === null) aren't linked to
        // any cart yet, so they bypass this check entirely and
        // restore as long as the company matches.
        if (saved.cartId) {
          var currentCartId = localStorage.cartId
            ? String(localStorage.cartId)
            : null;
          if (
            !currentCartId ||
            String(saved.cartId) !== currentCartId
          ) {
            // The cart this queue mirrored is no longer the
            // active cart — order was placed, cart was saved,
            // or some other action moved on from it. Drop the
            // staging tray so it doesn't reappear stale.
            localStorage.removeItem(key);
            this.selectedParts = [];
            this.committedSnapshot = null;
            return;
          }
        }

        this.selectedParts = saved.selectedParts;
        // Restore the committed snapshot so the Continue button
        // reflects the correct state immediately. Older payloads
        // that pre-date this field will leave it null, which
        // hasUnsavedChanges interprets as "everything is unsaved"
        // — that's the safe default since the next click will
        // re-save the queue and capture a fresh snapshot anyway.
        this.committedSnapshot = saved.committedSnapshot || null;
      } catch (e) {
        console.warn("[Insights] Could not restore selections", e);
      }
    },
    clearSelectionsStorage() {
      try {
        var key = this._selectionsStorageKey();
        if (!key) return;
        localStorage.removeItem(key);
      } catch (e) {
        // No-op: same reasoning as saveSelectionsToStorage.
      }
    },

    // ===== CART LINKAGE VALIDATION =====
    // Runs after the queue hydrates from storage. The
    // committedSnapshot is a client-side belief about what's in
    // the cart, but it can drift from server-side reality —
    // notably after a company-switch round-trip (Head.vue's
    // per-company cart storage may restore a stale cartId), or
    // when other flows (Save Cart, Place Order, manual Shop
    // flow, team-cart pickup) modify the cart out-of-band.
    //
    // If the linked cart no longer exists, is empty, or fails
    // to load, we break the local linkage by clearing
    // committedSnapshot. The selectedParts queue stays — so the
    // user doesn't lose their staging work — but the button
    // flips back to "Continue with N parts" so the next click
    // creates a fresh cart rather than trying to update a
    // phantom one. This also clears the cartId tag in storage.
    //
    // Non-blocking: runs as a fire-and-forget async task. If
    // the cart fetch is slow, the user briefly sees "Go to
    // Cart" before it potentially flips to "Continue with N
    // parts" — that's acceptable, since the alternative is
    // blocking page render on a network call.
    // ===== SYNC FROM ACTIVE CART =====
    // Fetches the active order cart (localStorage.cartId) and
    // rebuilds selectedParts from its server-side contents.
    // Cart is the source of truth across pages — Search adds,
    // Create-Order adds, and SavedCarts Resume Cart all write
    // to the same cart row. Without this sync, a part added
    // on Search wouldn't appear here, the user would re-add it
    // under "the same PO 12345", and SaveOldCart would 400 with
    // "this part is already added".
    //
    // Filters out quote carts (Discriminator === Rfq) so an
    // active RFQ doesn't accidentally surface on the Create
    // Order page; quote staging is owned by /cart/rfqsaveditems.
    //
    // Non-blocking: runs as fire-and-forget. If the cart fetch
    // is slow, the user briefly sees the localStorage-cached
    // queue before it gets replaced — acceptable tradeoff
    // versus blocking page render on a network call.
    async syncFromActiveCart() {
      if (!localStorage.cartId || String(localStorage.cartId) === "0") {
        return;
      }
      if (!this.companyId) return;

      try {
        var cart = await apiService.getCart({ id: localStorage.cartId });
        if (!cart) return;

        // Filter: skip quote carts. On the slim chance
        // localStorage.cartId points to a row that's actually
        // an Rfq (e.g. the discriminator was flipped manually
        // in SQL), we don't want to ingest it here.
        var isQuote =
          cart.discriminator === "Rfq" ||
          cart.Discriminator === "Rfq" ||
          cart.discriminator === 1 ||
          cart.Discriminator === 1;
        if (isQuote) return;

        var pos = cart.purchaseOrders || [];
        if (pos.length === 0) return;

        // Convert cart shape → selectedParts shape. The
        // serializer surfaces each PurchaseOrderPart with
        // p.id === underlying Part.Id (verified in the cart
        // response projection), and p.part carries the code
        // and description for UI rendering.
        var derived = [];
        pos.forEach(function (po) {
          (po.parts || []).forEach(function (p) {
            var partInfo = p.part || {};
            derived.push({
              partId: p.id,
              partCode: partInfo.code || "",
              description: partInfo.description || "",
              poNumber: po.number || "",
              quantity: p.quantity || 1,
            });
          });
        });

        if (derived.length === 0) return;

        // Replace whatever was loaded from localStorage with
        // the cart-derived snapshot. Set committedSnapshot to
        // the same data so hasUnsavedChanges returns false —
        // everything in the queue at this point is already
        // saved server-side.
        this.selectedParts = derived;
        this.committedSnapshot = this.buildCommittedSnapshot();

        // Persist the new state with the cart linkage tag so
        // a refresh hydrates correctly.
        this.saveSelectionsToStorage();
      } catch (e) {
        console.warn(
          "[CreateOrder] cart-derivation on mount failed:",
          e
        );
      }
    },

    async validateLinkedCart() {
      // Nothing to validate if we never committed anything.
      if (!this.committedSnapshot) return;

      // No active cart in localStorage means the linkage is
      // already broken — clear the snapshot so the button
      // matches reality.
      if (!localStorage.cartId || String(localStorage.cartId) === "0") {
        console.log(
          "[Insights] No active cartId — breaking queue linkage"
        );
        this.committedSnapshot = null;
        this.saveSelectionsToStorage();
        return;
      }

      // Cart exists in localStorage; verify it on the backend.
      // Empty purchaseOrders means the cart was gutted by another
      // flow (cleared, items removed, etc.) — break linkage.
      try {
        var cart = await apiService.getCart({
          id: localStorage.cartId,
        });
        if (
          !cart ||
          !cart.purchaseOrders ||
          cart.purchaseOrders.length === 0
        ) {
          console.log(
            "[Insights] Linked cart " +
              localStorage.cartId +
              " is empty or missing — breaking linkage"
          );
          this.committedSnapshot = null;
          this.saveSelectionsToStorage();
        }
        // If cart has parts, leave linkage alone. Even if the
        // contents have drifted from committedSnapshot, the
        // hasUnsavedChanges check will catch the difference and
        // show "Continue with N parts" — which is correct.
      } catch (e) {
        console.warn(
          "[Insights] Linked cart fetch failed — breaking linkage",
          e
        );
        this.committedSnapshot = null;
        this.saveSelectionsToStorage();
      }
    },

    // ===== STAGING TRAY ↔ CART SYNC =====
    // Once the user clicks Continue with N parts, the staging
    // tray and the cart are mirrors of each other. After that
    // initial commit, edits to the staging tray (removing a
    // queued chip, clicking Clear all) need to flow back to the
    // cart so the two stay in sync — otherwise the cart stays
    // stale until the user clicks Continue again.
    //
    // Triggered from removeQueuedPart and clearSelectedParts.
    // Debounced through scheduleSyncToCart so rapid changes
    // (e.g. removing two chips in quick succession) coalesce
    // into a single round-trip rather than firing one per
    // keystroke. Pre-Continue queues (no localStorage.cartId
    // tag) bypass sync entirely — there's no cart to mirror
    // until Continue is clicked the first time.
    scheduleSyncToCart() {
      // Bail if there's no cart to sync TO. Pre-Continue queues
      // are local-only and don't need a backend round-trip when
      // the user edits them.
      if (!localStorage.cartId || String(localStorage.cartId) === "0") {
        return;
      }
      if (this._syncTimer) {
        clearTimeout(this._syncTimer);
      }
      var self = this;
      this._syncTimer = setTimeout(function () {
        self._syncTimer = null;
        self.syncQueueToCart();
      }, 300);
    },
    async syncQueueToCart() {
      // Guard against concurrent syncs. If a save is already in
      // flight when another change comes in, mark the queue as
      // dirty so we re-fire after the current save resolves.
      if (this.addingInsightPart) {
        this._syncDirty = true;
        return;
      }
      // Re-check the cart link — between scheduling and firing,
      // the user might have placed the order or otherwise
      // cleared the cart, in which case there's nothing to sync.
      if (!localStorage.cartId || String(localStorage.cartId) === "0") {
        return;
      }
      if (!this.companyId) return;

      this.addingInsightPart = true;
      var self = this;

      try {
        // Empty queue → the user removed everything that was in
        // the cart. Delete the cart entirely; this is the same
        // outcome as if the user had clicked Clear Cart on the
        // /cart page.
        if (!this.selectedParts || this.selectedParts.length === 0) {
          var cartIdToDelete = parseInt(localStorage.cartId, 10);
          try {
            await apiService.deleteCart({ id: cartIdToDelete });
          } catch (e) {
            // If the cart was already gone (user cleared it
            // server-side via another tab), the sync is still
            // effectively successful — local state catches up.
            console.warn(
              "[Insights → sync] deleteCart returned error " +
                "(possibly already deleted):",
              e
            );
          }
          localStorage.cartId = 0;
          this.clearSelectionsStorage();
          this.committedSnapshot = null;
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        // Non-empty queue → re-resolve every part and send a
        // fresh saveCart payload. Same machinery as
        // proceedWithSelected, but without the navigation step
        // at the end — the user stays on the create page.
        var resolved = await Promise.all(
          this.selectedParts.map(async function (p) {
            return await self.resolvePartContext(p);
          })
        );

        var missing = resolved.filter(function (p) {
          return !p._companyMachineId || !p._assemblyId;
        });
        if (missing.length > 0) {
          // If any part fails to resolve, skip the sync rather
          // than send bad IDs that would NRE the backend. Log
          // for diagnostics; the staging tray still reflects
          // the user's intent locally and on next mount or next
          // Continue click we'll try again.
          console.warn(
            "[Insights → sync] skipped — some parts failed to resolve:",
            missing.map(function (m) { return m.partCode; }).join(", ")
          );
          return;
        }

        // ===== ADD vs REMOVE vs QTY-CHANGE DELTA =====
        // The backend's SaveOldCart is fundamentally add-only: it
        // either adds a part to an existing PO or creates a new PO,
        // but it can't delete or update an existing row's quantity.
        // DoesPartExistInTheCart 400s any part already on the
        // server, so we can't just send the full queue. We compute
        // additions, removals, and quantity changes separately.
        //
        //   ADDS only           → send delta to SaveOldCart (cheap)
        //   REMOVALS / QTY edit → delete cart + recreate via
        //                         SaveNewCart (the only way to drop
        //                         a part or change its quantity in
        //                         one round-trip)
        //   NEITHER             → no-op, just refresh the badge
        //
        // Match key for adds/removals is partId + poNumber.
        // Quantity is checked separately against the snapshot so a
        // qty-only edit in the staging tray triggers a recreate
        // instead of falling through as a silent no-op.
        var snapshotKeys = {};
        var snapshotQtys = {};
        (this.committedSnapshot || []).forEach(function (s) {
          var k = s.partId + "|" + (s.poNumber || "");
          snapshotKeys[k] = true;
          snapshotQtys[k] = Number(s.quantity);
        });
        var currentKeys = {};
        resolved.forEach(function (p) {
          currentKeys[p.partId + "|" + (p.poNumber || "")] = true;
        });

        var addedParts = resolved.filter(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          return !snapshotKeys[key];
        });
        var hasRemovals = (this.committedSnapshot || []).some(function (s) {
          var key = s.partId + "|" + (s.poNumber || "");
          return !currentKeys[key];
        });
        // Detect qty-only changes — same partId+poNumber pair on
        // both sides but a different quantity. Without this
        // check, qty edits in the staging tray never reach the
        // server (the prior delta logic ignored quantity).
        var hasQtyChange = resolved.some(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          if (!(key in snapshotQtys)) return false;
          return Number(p.quantity) !== snapshotQtys[key];
        });

        // No additions, no removals, no qty changes — true no-op.
        // Refresh local snapshot, ping the badge, and bail.
        if (addedParts.length === 0 && !hasRemovals && !hasQtyChange) {
          this.committedSnapshot = this.buildCommittedSnapshot();
          this.saveSelectionsToStorage();
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        // Removals OR qty changes both need the full resolved
        // queue (cart gets recreated from scratch). Add-only
        // cases use the addedParts subset (incremental
        // SaveOldCart add).
        var needsRecreate = hasRemovals || hasQtyChange;
        var partsForPayload = needsRecreate ? resolved : addedParts;

        var poGroups = {};
        var poOrder = [];
        partsForPayload.forEach(function (p) {
          if (!poGroups[p.poNumber]) {
            poGroups[p.poNumber] = [];
            poOrder.push(p.poNumber);
          }
          poGroups[p.poNumber].push(p);
        });

        var purchaseOrders = poOrder.map(function (po) {
          var parts = poGroups[po];
          var first = parts[0];
          return {
            number: po,
            assemblyId: Number(first._assemblyId) || 0,
            machineId: Number(first._companyMachineId) || 0,
            configuredWear: true,
            parts: parts.map(function (p) {
              return {
                id: Number(p.partId) || 0,
                quantity: Number(p.quantity) || 1,
                quantityPerMachine: 0,
              };
            }),
          };
        });

        // ===== If removals or qty changes detected, delete + recreate =====
        // SaveOldCart can't drop parts or update existing
        // quantities. Delete the existing cart first, then save
        // with id=0 so the request hits SaveNewCart (which has
        // no duplicate check). The user's localStorage.cartId
        // rotates to the new cart's id — a small cost in
        // exchange for keeping queue and cart in sync.
        if (needsRecreate) {
          var oldCartId = parseInt(localStorage.cartId, 10) || 0;
          if (oldCartId > 0) {
            try {
              await apiService.deleteCart({ id: oldCartId });
            } catch (e) {
              // Soft-fail: a delete error doesn't block the
              // recreate (worst case the old cart lingers as
              // a duplicate in /account/savedcarts; the new
              // cart still gets created and is the active one).
              console.warn(
                "[Insights → sync] deleteCart before recreate failed:",
                e
              );
            }
          }
        }

        // saveCartId: 0 = recreate via SaveNewCart (removals or
        // qty changes), or the existing cart id for incremental
        // add via SaveOldCart.
        var saveCartId = needsRecreate
          ? 0
          : parseInt(localStorage.cartId, 10) || 0;
        var savePayload = {
          id: saveCartId,
          purchaseOrders: purchaseOrders,
          companyId: Number(this.companyId) || 0,
          specialInstructions: "",
          CreateQuote: false,
        };

        var response = await apiService.saveCart(savePayload);

        if (response && response.id) {
          localStorage.cartId = response.id;
        }

        // Update the committed snapshot to match what's now
        // in the cart. Without this, the button would still
        // say "Continue with N parts" because the snapshot
        // would be stale from before the sync.
        this.committedSnapshot = this.buildCommittedSnapshot();

        // Re-persist with the (possibly updated) cartId tag.
        this.saveSelectionsToStorage();

        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }
      } catch (err) {
        console.warn("[Insights → sync] saveCart failed:", err);
        // Show a non-blocking warning. The user's changes are
        // still reflected in the staging tray and saved to
        // localStorage — only the cart didn't update. They can
        // refresh the page to retry, or click Continue again
        // which will go through proceedWithSelected's surfacing.
        this.$bvToast.toast(
          "Couldn't sync the change to your cart. The staging tray " +
            "is up to date — refresh the page or click Continue to retry.",
          {
            title: "Cart sync failed",
            autoHideDelay: 6000,
            variant: "warning",
            solid: true,
          }
        );
      } finally {
        this.addingInsightPart = false;
        // If a change came in while the previous sync was still
        // running, fire one more pass now so we don't end up
        // with the cart lagging behind the staging tray.
        if (this._syncDirty) {
          this._syncDirty = false;
          this.scheduleSyncToCart();
        }
      }
    },

    // ===== ADD-TO-CART MODAL HANDLERS =====
    onAddToCartConfirm(bvModalEvent) {
      if (bvModalEvent && bvModalEvent.preventDefault) {
        bvModalEvent.preventDefault();
      }
      // Defensive: the OK button is :ok-disabled when canConfirm is
      // false, but b-modal's preventDefault contract means we still
      // re-validate here. Bail silently if the user managed to fire
      // the handler without picking a destination.
      if (this.pickerMode !== "edit" && !this.pendingDestination) {
        return;
      }
      var trimmedPo = (this.pendingPoNumber || "").trim();
      if (!trimmedPo) {
        // Don't close — re-focus the input and let the user fix it.
        this.$nextTick(() => {
          var el = document.getElementById("addToCartPoInput");
          if (el) el.focus();
        });
        return;
      }
      var qty = Math.max(1, Math.floor(Number(this.pendingQuantity) || 1));

      var pending = this.pendingPart;
      if (!pending) {
        this.showAddToCartModal = false;
        this.resetModalState();
        return;
      }

      var dest = this.pendingDestination === "quote" ? "quote" : "order";

      if (this.pickerMode === "edit") {
        // Update the existing entry in-place. Cross-destination
        // moves aren't supported, so dest is locked to whatever
        // queue the part is in.
        if (dest === "quote") {
          var qIdx = this.selectedPartsQuote.findIndex(function (p) {
            return p.partId === pending.partId;
          });
          if (qIdx >= 0) {
            this.$set(this.selectedPartsQuote, qIdx, Object.assign(
              {},
              this.selectedPartsQuote[qIdx],
              { poNumber: trimmedPo, quantity: qty }
            ));
          }
        } else {
          var oIdx = this.selectedParts.findIndex(function (p) {
            return p.partId === pending.partId;
          });
          if (oIdx >= 0) {
            this.$set(this.selectedParts, oIdx, Object.assign(
              {},
              this.selectedParts[oIdx],
              { poNumber: trimmedPo, quantity: qty }
            ));
          }
        }
      } else {
        // ADD mode — push a new entry into the chosen queue.
        if (dest === "quote") {
          this.selectedPartsQuote.push(Object.assign({}, pending, {
            poNumber: trimmedPo,
            quantity: qty,
          }));
        } else {
          this.selectedParts.push(Object.assign({}, pending, {
            poNumber: trimmedPo,
            quantity: qty,
          }));
        }
      }

      this.showAddToCartModal = false;
      this.resetModalState();

      // Auto-commit + sync. First-time on a destination creates
      // the corresponding cart; thereafter the debounced sync
      // mirrors changes to the server cart.
      if (dest === "quote") {
        var hasQuoteCart =
          localStorage.cartIdRFQ && String(localStorage.cartIdRFQ) !== "0";
        if (!hasQuoteCart) {
          this.proceedWithSelectedInPlaceQuote();
        } else {
          this.scheduleSyncToQuoteCart();
        }
      } else {
        var hasOrderCart =
          localStorage.cartId && String(localStorage.cartId) !== "0";
        if (!hasOrderCart) {
          this.proceedWithSelectedInPlace();
        } else {
          this.scheduleSyncToCart();
        }
      }
    },
    onAddToCartCancel() {
      this.showAddToCartModal = false;
      this.resetModalState();
    },
    // Edit-mode only — pull the part out of whichever queue it's
    // in and sync that destination's cart.
    onAddToCartRemove() {
      var pending = this.pendingPart;
      if (!pending) {
        this.showAddToCartModal = false;
        this.resetModalState();
        return;
      }
      var dest = this.pendingDestination === "quote" ? "quote" : "order";
      this.removeQueuedPart(pending, dest);
      this.showAddToCartModal = false;
      this.resetModalState();
    },
    onAddToCartShown() {
      var el = document.getElementById("addToCartPoInput");
      if (el) el.focus();
    },
    // Centralized cleanup so a stale mode/pendingPart can't leak
    // from one interaction into the next.
    resetModalState() {
      this.pickerMode = "add";
      this.pendingPart = null;
      this.pendingDestination = "order";
      this.pendingPoNumber = "";
      this.pendingQuantity = 1;
      this.useNewPo = false;
    },

        continueWithSelected() {
      if (this.selectedParts.length === 0) return;
      this.proceedWithSelected();
    },
    // Build a snapshot of the current queue suitable for storing
    // as committedSnapshot. Strips everything except the fields
    // that determine whether a part is "the same" between two
    // moments in time: partId + poNumber identify it, quantity is
    // tracked because qty changes count as unsaved edits.
    buildCommittedSnapshot() {
      return (this.selectedParts || []).map(function (p) {
        return {
          partId: p.partId,
          poNumber: p.poNumber,
          quantity: p.quantity,
        };
      });
    },
    // Single click handler for the bottom commit button. If
    // there are unsaved changes, fire the save flow. Otherwise
    // just navigate — no point in re-saving an unchanged cart.
    // Cancels any debounced sync since the user's explicit
    // action supersedes it.
    // Navigate to /orders/create. The order-queue is already
    // persisted to localStorage by the existing watcher on
    // selectedParts, so /orders/create restores it automatically
    // from localStorage and then handleContinueButtonClick over
    // there commits to a server cart. Mirrors
    // Index_-_Account.vue's continueOrder which works the same
    // way (write-to-localStorage + navigate, the destination
    // page does the commit).
    continueOrder() {
      if (!this.selectedParts || this.selectedParts.length === 0) return;
      this.$router.push({ name: "Create New Order" });
    },
    // Navigate to /cart/rfqsaveditems where the user's quote
    // state already lives. Same pattern as continueOrder —
    // queue is persisted, destination page restores + commits.
    continueQuote() {
      if (!this.selectedPartsQuote || this.selectedPartsQuote.length === 0) return;
      this.$router.push("/cart/rfqsaveditems");
    },
    handleContinueButtonClick() {
      if (this._syncTimer) {
        clearTimeout(this._syncTimer);
        this._syncTimer = null;
      }
      if (this.hasUnsavedChanges) {
        this.continueWithSelected();
      } else {
        this.$router.push({ name: "Cart" });
      }
    },
    // Commit the queue: resolve each queued part's machine/assembly
    // context, build a purchaseOrders payload grouped by PO matching
    // the backend's CartModel/PurchaseOrderModel shape (flat fields,
    // not nested objects), and hand it to apiService.saveCart.
    //
    // Backend payload contract (verified against CartController.Save
    // → SaveNewCart → groupedPo):
    //
    //   {
    //     id: 0,
    //     companyId: <int>,
    //     specialInstructions: "",
    //     CreateQuote: false,
    //     purchaseOrders: [
    //       {
    //         number: "<PO>",
    //         assemblyId: <int>,
    //         machineId:  <int>,   // ← CompanyMachine ID (NOT raw Machine ID)
    //         configuredWear: true,
    //         parts: [
    //           { id: <partId>, quantity: <int>, quantityPerMachine: 0 }
    //         ]
    //       }
    //     ]
    //   }
    //
    // The frontend ID resolution path tries, in order:
    //   1. apiService.getPartContext({partId, companyId}) — preferred,
    //      returns { assemblyId, companyMachineId } directly. Needs
    //      a small backend endpoint (see the response message that
    //      shipped with this commit for the C# code).
    //   2. apiService.resolveTopPartContext + URL param parsing —
    //      best-effort; only works if the redirectUrl carries the
    //      IDs as query params (it currently goes to /search/index
    //      so it likely doesn't).
    // If neither yields valid IDs, we surface a precise error listing
    // which parts failed and bail before calling saveCart.
    // Variant of proceedWithSelected that performs the save
    // but stays on the current page. Used by the auto-add
    // flow where every Add click commits to the cart but
    // doesn't yank the user away from the create page —
    // they should keep browsing the Frequently Ordered list
    // and add more parts. The Saved Quotes / View Cart
    // header badges update via the cart-updated event so
    // the user has the visual confirmation without leaving
    // the page.
    async proceedWithSelectedInPlace() {
      this._suppressCartNav = true;
      try {
        await this.proceedWithSelected();
      } finally {
        this._suppressCartNav = false;
      }
    },
    async proceedWithSelected() {
      if (this.addingInsightPart || this.selectedParts.length === 0) return;
      if (!this.companyId) return;

      this.addingInsightPart = true;
      var self = this;

      try {
        // ===== Step 1: Resolve every queued part's context =====
        var resolved = await Promise.all(
          this.selectedParts.map(async function (p) {
            return await self.resolvePartContext(p);
          })
        );

        // Pre-flight: every part must have a CompanyMachine ID and
        // an Assembly ID. Without both, the backend NREs in
        // _purchaseOrderManager.CreatePurchaseOrder when it does the
        // CompanyMachine lookup.
        var missing = resolved.filter(function (p) {
          return !p._companyMachineId || !p._assemblyId;
        });
        if (missing.length > 0) {
          var codes = missing.map(function (p) { return p.partCode; }).join(", ");
          this.$bvToast.toast(
            "Could not resolve the machine and assembly context for: " +
              codes +
              ". The backend needs a getPartContext endpoint — see the " +
              "console log for the payload we tried to send.",
            {
              title: "Cart update blocked",
              autoHideDelay: 12000,
              variant: "warning",
              solid: true,
            }
          );
          console.warn(
            "[Insights → saveCart] could not resolve context for parts:",
            missing
          );
          return;
        }

        // ===== ADD vs REMOVE DELTA =====
        // Same logic as syncQueueToCart. SaveOldCart can't drop
        // parts, so when the queue has shrunk relative to
        // committedSnapshot we delete+recreate via SaveNewCart
        // (id=0). When only adds, send the delta to SaveOldCart.
        // When neither, navigate without saving.
        var existingCartId = parseInt(localStorage.cartId, 10) || 0;
        var snapshotKeys = {};
        (this.committedSnapshot || []).forEach(function (s) {
          snapshotKeys[s.partId + "|" + (s.poNumber || "")] = true;
        });
        var currentKeys = {};
        resolved.forEach(function (p) {
          currentKeys[p.partId + "|" + (p.poNumber || "")] = true;
        });

        var addedParts = resolved.filter(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          return !snapshotKeys[key];
        });
        var hasRemovals = existingCartId > 0
          && (this.committedSnapshot || []).some(function (s) {
              var key = s.partId + "|" + (s.poNumber || "");
              return !currentKeys[key];
            });

        // Nothing new and nothing removed — navigate as if the
        // save succeeded. Avoids a wasted (and failing) round-trip
        // on a no-op commit.
        if (addedParts.length === 0 && !hasRemovals) {
          this.committedSnapshot = this.buildCommittedSnapshot();
          this.saveSelectionsToStorage();
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          if (!this._suppressCartNav) {
            this.$router.push({ name: "Cart" });
          }
          return;
        }

        // For removals, recreate uses the FULL queue. For adds-only
        // on an existing cart, send only the new parts. For a fresh
        // cart (existingCartId === 0), send the full queue —
        // SaveNewCart has no duplicate check.
        var partsForSave;
        if (hasRemovals || existingCartId === 0) {
          partsForSave = resolved;
        } else {
          partsForSave = addedParts;
        }

        // ===== Step 2: Group resolved parts by PO =====
        var poGroups = {};
        var poOrder = [];
        partsForSave.forEach(function (p) {
          if (!poGroups[p.poNumber]) {
            poGroups[p.poNumber] = [];
            poOrder.push(p.poNumber);
          }
          poGroups[p.poNumber].push(p);
        });

        // ===== Step 3: Build purchaseOrders payload (FLAT shape) =====
        var purchaseOrders = poOrder.map(function (po) {
          var parts = poGroups[po];
          var first = parts[0];
          return {
            number: po,
            assemblyId: Number(first._assemblyId) || 0,
            machineId: Number(first._companyMachineId) || 0,
            configuredWear: true,
            parts: parts.map(function (p) {
              return {
                id: Number(p.partId) || 0,
                quantity: Number(p.quantity) || 1,
                quantityPerMachine: 0,
              };
            }),
          };
        });

        // ===== Step 3.5: Delete existing cart if removals detected =====
        // SaveOldCart can't drop parts. To remove parts the user took
        // out of the queue, we delete the existing cart first then
        // recreate from scratch via SaveNewCart (id=0).
        if (hasRemovals && existingCartId > 0) {
          try {
            await apiService.deleteCart({ id: existingCartId });
          } catch (e) {
            console.warn(
              "[Insights → saveCart] deleteCart before recreate failed:",
              e
            );
          }
        }

        // ===== Step 4: Save cart =====
        // id=0 forces SaveNewCart on the recreate path (after a
        // removal-triggered delete) or on a fresh queue. For
        // incremental adds against an existing cart, we keep its
        // id so SaveOldCart adds the delta.
        var saveCartIdForPayload = hasRemovals ? 0 : existingCartId;
        var savePayload = {
          id: saveCartIdForPayload,
          purchaseOrders: purchaseOrders,
          companyId: Number(this.companyId) || 0,
          specialInstructions: "",
          CreateQuote: false,
        };

        console.log("[Insights → saveCart] sending payload:", savePayload);
        var response = await apiService.saveCart(savePayload);

        if (response && response.id) {
          localStorage.cartId = response.id;
        }

        // Capture the current queue as the committed snapshot.
        // This is what the Continue button's "Go to Cart" vs
        // "Continue with N parts" decision compares against —
        // the moment any part is added/removed/edited, the
        // button reverts to "Continue with N parts".
        this.committedSnapshot = this.buildCommittedSnapshot();

        // Re-persist the queue WITH the linked cart's id so
        // hydration on next mount can detect cart placement (id
        // gone or changed → user placed/saved/abandoned the cart
        // → clear queue) vs. ongoing work (id still matches →
        // restore queue so the staging tray mirrors the cart).
        // We deliberately do NOT clear selectedParts here — the
        // user expects the queue to stay visible alongside the
        // cart until the order is placed or the cart is saved.
        this.saveSelectionsToStorage();

        // Notify the header so the View Cart badge re-fetches
        // its count immediately. Without this, the count is
        // stale until the next page navigation triggers a
        // re-mount of Head.vue. The event bus is set up in
        // Head.vue's mounted() hook on Vue.prototype.$cartBus.
        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }

        // Successful add. Navigate to /cart unless the caller
        // set _suppressCartNav (the auto-add flow keeps the user
        // on the create page so they can keep adding parts).
        if (!this._suppressCartNav) {
          this.$router.push({ name: "Cart" });
        }
      } catch (err) {
        var details = "";
        try {
          var resp = err && err.error && err.error.response;
          if (resp && resp.data) {
            console.error("[Insights → saveCart] backend response:", resp.data);
            details =
              resp.data.title ||
              resp.data.exception ||
              (resp.data.errors && JSON.stringify(resp.data.errors)) ||
              JSON.stringify(resp.data);
          } else {
            console.error("[Insights → saveCart] error:", err);
            details = (err && err.message) || String(err);
          }
        } catch (e) {
          console.error("[Insights → saveCart] error (unparseable):", err);
        }

        this.$bvToast.toast(
          "Could not add the selected parts to your cart. " +
            (details
              ? "Server said: " + details
              : "Check the browser console for details."),
          {
            title: "Cart update failed",
            autoHideDelay: 9000,
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.addingInsightPart = false;
      }
    },
    // Tries multiple paths to look up a part's machine/assembly
    // context. Returns the part with _companyMachineId and
    // _assemblyId set, or nulls if everything failed. Logs each
    // step so the user can pinpoint where the chain breaks.
    async resolvePartContext(p) {
      var enriched = Object.assign({}, p, {
        _companyMachineId: null,
        _assemblyId: null,
        _resolveDiagnostics: [],
      });

      // Path 1 (preferred): apiService.getPartContext
      var hasGetPartContext = typeof apiService.getPartContext === "function";
      if (!hasGetPartContext) {
        enriched._resolveDiagnostics.push(
          "apiService.getPartContext is not defined — add the wrapper to FeApiService.js"
        );
      } else {
        try {
          var ctx = await apiService.getPartContext({
            partId: p.partId,
            companyId: this.companyId,
          });
          if (ctx) {
            enriched._companyMachineId =
              ctx.companyMachineId || ctx.CompanyMachineId || null;
            enriched._assemblyId = ctx.assemblyId || ctx.AssemblyId || null;
            if (enriched._companyMachineId && enriched._assemblyId) {
              enriched._resolveDiagnostics.push(
                "getPartContext OK: companyMachineId=" +
                  enriched._companyMachineId +
                  ", assemblyId=" +
                  enriched._assemblyId
              );
              return enriched;
            }
            enriched._resolveDiagnostics.push(
              "getPartContext returned but missing IDs: " +
                JSON.stringify(ctx)
            );
          } else {
            enriched._resolveDiagnostics.push(
              "getPartContext returned empty/null"
            );
          }
        } catch (e) {
          var status = e && e.error && e.error.response && e.error.response.status;
          enriched._resolveDiagnostics.push(
            "getPartContext threw" +
              (status ? " (HTTP " + status + ")" : "") +
              " — likely the PartController.GetPartContext endpoint isn't deployed yet"
          );
          console.warn(
            "[Insights] getPartContext failed for " + p.partCode,
            e
          );
        }
      }

      // Path 2 (fallback): URL parsing on resolveTopPartContext.
      try {
        var result = await apiService.resolveTopPartContext({
          partId: p.partId,
          partCode: p.partCode,
          companyId: this.companyId,
        });
        if (result && result.redirectUrl) {
          enriched._resolveDiagnostics.push(
            "resolveTopPartContext URL: " + result.redirectUrl
          );
          enriched._companyMachineId = this.parseUrlId(result.redirectUrl, [
            "companyMachineId",
            "machineId",
            "machineid",
          ]);
          enriched._assemblyId = this.parseUrlId(result.redirectUrl, [
            "assemblyId",
            "assemblyid",
          ]);
          if (!enriched._companyMachineId || !enriched._assemblyId) {
            enriched._resolveDiagnostics.push(
              "URL parsing produced: companyMachineId=" +
                enriched._companyMachineId +
                ", assemblyId=" +
                enriched._assemblyId +
                " — URL doesn't carry the IDs we need"
            );
          }
        }
      } catch (e) {
        enriched._resolveDiagnostics.push(
          "resolveTopPartContext threw — see console"
        );
        console.warn(
          "[Insights] resolveTopPartContext failed for " + p.partCode,
          e
        );
      }

      console.log(
        "[Insights] resolved " + p.partCode + ":",
        enriched._resolveDiagnostics
      );
      return enriched;
    },
    // Helper: pull a numeric ID out of a redirectUrl by trying each
    // candidate query-param name in order. Returns null if none of
    // them match. Defensive about malformed URLs.
    parseUrlId(url, paramNames) {
      if (!url || !paramNames || !paramNames.length) return null;
      try {
        var parsed = new URL(url, window.location.origin);
        for (var i = 0; i < paramNames.length; i++) {
          var v = parsed.searchParams.get(paramNames[i]);
          if (v) return v;
        }
        return null;
      } catch (e) {
        return null;
      }
    },
    // ===== MACHINE IMAGE =====
    // Pulls the machine's imagePath via getCompanyMachineDetails
    // and prefixes it with the Steele blob storage URL.
    // Mirrors the canonical pattern in ShopWearParts.vue. Sets
    // machineImage to null on missing path or fetch failure so
    // the template falls back to the placeholder.
    loadMachineImage() {
      if (!this.machineId) {
        this.machineImage = null;
        this.machineImageLoading = false;
        return;
      }
      var self = this;
      this.machineImageLoading = true;
      apiService
        .getCompanyMachineDetails({ id: this.machineId })
        .then(function (request) {
          console.log("[Create] machine image: getCompanyMachineDetails response", request);
          if (request && request.imagePath) {
            self.machineImage = self.machineImageUrl + request.imagePath;
          } else {
            self.machineImage = null;
          }
        })
        .catch(function (e) {
          console.warn("[Create] machine image: load failed", e);
          self.machineImage = null;
        })
        .finally(function () {
          self.machineImageLoading = false;
        });
    },

    // Pulls the assembly's imagePath via getAssemblyDetails
    // (the same call ShopWearParts.vue uses for its assembly
    // image). Different endpoint, same blob URL prefix —
    // assemblies and machines share the steeleimages
    // container. Sets assemblyImage to null on missing path
    // or fetch failure so the template hides the image area
    // entirely (no placeholder).
    loadAssemblyImage(assemblyId) {
      if (!assemblyId) {
        this.assemblyImage = null;
        this.assemblyImageLoading = false;
        return;
      }
      var self = this;
      this.assemblyImageLoading = true;
      apiService
        .getAssemblyDetails({ id: assemblyId })
        .then(function (request) {
          console.log("[Create] assembly image: getAssemblyDetails response", request);
          if (request && request.imagePath) {
            self.assemblyImage = self.machineImageUrl + request.imagePath;
          } else {
            self.assemblyImage = null;
          }
        })
        .catch(function (e) {
          console.warn("[Create] assembly image: load failed", e);
          self.assemblyImage = null;
        })
        .finally(function () {
          self.assemblyImageLoading = false;
        });
    },

    // Sub-assembly image loader — same endpoint as the
    // assembly image (the assembly table is unified;
    // sub-assemblies are rows in dbo.Assembly with a parent
    // FK), just stored in a separate state field so both
    // images can render side by side without one clobbering
    // the other.
    loadSubAssemblyImage(subAssemblyId) {
      if (!subAssemblyId) {
        this.subAssemblyImage = null;
        this.subAssemblyImageLoading = false;
        return;
      }
      var self = this;
      this.subAssemblyImageLoading = true;
      apiService
        .getAssemblyDetails({ id: subAssemblyId })
        .then(function (request) {
          console.log("[Create] sub-assembly image: getAssemblyDetails response", request);
          if (request && request.imagePath) {
            self.subAssemblyImage = self.machineImageUrl + request.imagePath;
          } else {
            self.subAssemblyImage = null;
          }
        })
        .catch(function (e) {
          console.warn("[Create] sub-assembly image: load failed", e);
          self.subAssemblyImage = null;
        })
        .finally(function () {
          self.subAssemblyImageLoading = false;
        });
    },

    // ===== PART-ROW ASSEMBLY THUMBNAILS =====
    // Fetch a single sub-assembly's image (once) and cache it in
    // assemblyThumbs keyed by assemblyId. Uses the same
    // getAssemblyDetails endpoint + blob prefix as the big locked
    // assembly image. Stores null on missing path / failure so the
    // row falls back to the neutral placeholder rather than retrying.
    ensureAssemblyThumb(assemblyId) {
      if (assemblyId == null) return;
      // Already fetched or in-flight — hasOwnProperty guards both
      // (the key is set to null up-front to mark "in progress").
      if (Object.prototype.hasOwnProperty.call(this.assemblyThumbs, assemblyId)) return;
      this.$set(this.assemblyThumbs, assemblyId, null);
      var self = this;
      apiService
        .getAssemblyDetails({ id: assemblyId })
        .then(function (request) {
          if (request && request.imagePath) {
            self.$set(
              self.assemblyThumbs,
              assemblyId,
              self.machineImageUrl + request.imagePath
            );
          }
        })
        .catch(function (e) {
          console.warn(
            "[Search] part-row thumb load failed for assembly " + assemblyId,
            e
          );
        });
    },

    // Walk the loaded wear-assemblies and request a thumbnail for
    // each unique part assemblyId. Keyed off part.assemblyId (always
    // present in the GetAssembliesWithWearParts response) so it works
    // regardless of how the group-level assemblyId is shaped.
    loadPartThumbnails() {
      var self = this;
      (this.wearAssemblies || []).forEach(function (asm) {
        (asm.parts || []).forEach(function (part) {
          if (!part) return;
          if (part.assemblyId != null) self.ensureAssemblyThumb(part.assemblyId);
          // Sub-assemblies often have no image of their own — also fetch the
          // PARENT assembly's diagram so a row can fall back to it.
          if (part.parentAssemblyId != null) {
            self.ensureAssemblyThumb(part.parentAssemblyId);
          }
        });
      });
    },

    // Best available thumbnail for a part row: its own sub-assembly image,
    // else the parent assembly image, else the selected assembly's diagram
    // (always loaded for the current picker). Keeps every row showing the
    // most specific diagram on file instead of a blank placeholder.
    partThumb(part) {
      if (!part) return null;
      return (
        this.assemblyThumbs[part.assemblyId] ||
        this.assemblyThumbs[part.parentAssemblyId] ||
        this.assemblyImage ||
        null
      );
    },

    // ===== IMAGE ZOOM MODAL =====
    // Same modal + zoom controls as ShopWearParts.vue so users
    // see consistent affordances on both pages.
    // ===== inPlaceZoomMove =====
    // Update the transform-origin for `key`'s image based on cursor
    // position relative to the image element. Maps cursor position
    // to percentage values (0%–100% across width and height) so the
    // CSS `transform: scale(...)` zooms toward the cursor, giving
    // a "magnifying glass" feel — moving the cursor pans the
    // visible area without requiring drag or extra clicks.
    //
    // Bail-out cases:
    //   - rect.width / rect.height could be 0 if the image has not
    //     fully laid out yet (e.g., during the brief loading flicker
    //     or in a hidden tab). Skip the update in that case so we
    //     don't divide by zero.
    inPlaceZoomMove(key, e) {
      // Locked: hold the captured origin from the click; ignore
      // any further cursor motion. This is what lets the user
      // mouse off the image (to scroll the parts list, click
      // around the form, whatever) without the zoomed view
      // shifting away from the spot they wanted to study.
      if (this.inPlaceZoomLocked[key]) return;
      var rect = e.currentTarget.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      this.inPlaceZoomOrigin[key] = x + "% " + y + "%";
    },

    // ===== onImageClick =====
    // Distinguishes single click (toggle lock) from double click
    // (open the existing popup modal) using the standard timer-
    // based pattern: the first click starts a 300ms timer; if a
    // second click arrives before it fires, we treat the pair as
    // a double-click — cancel the pending lock toggle and open
    // the modal. If the timer expires, the click was a true
    // single click and we toggle the lock.
    //
    // Snapshotting transform-origin at click time means that even
    // if the user's cursor moves during the 300ms detection
    // window, the lock anchors at the click position (the spot
    // the user actually wanted), not at wherever the cursor
    // happened to land when the timer fired. We only restore the
    // snapshot when transitioning into the locked state — when
    // unlocking, we leave the origin as-is so hover-zoom can
    // pick up smoothly.
    //
    // _imageClickTimer is an instance field rather than a data
    // property — it doesn't need reactivity, just identity for
    // clearing.
    onImageClick(key, src) {
      // When locked, ALL gesture handling lives in
      // onLockedMouseDown's click-count machine — so this
      // method exits immediately. Click-to-lock (single click
      // while UNLOCKED) and click-to-modal (double-click while
      // unlocked) still flow through here.
      if (this.inPlaceZoomLocked[key]) return;
      var self = this;
      if (this._imageClickTimer) {
        // Second click within 300ms — open the modal
        clearTimeout(this._imageClickTimer);
        this._imageClickTimer = null;
        self.openImageModal(src);
        return;
      }
      var snapshot = this.inPlaceZoomOrigin[key];
      this._imageClickTimer = setTimeout(function () {
        self._imageClickTimer = null;
        var willLock = !self.inPlaceZoomLocked[key];
        if (willLock) {
          self.imageCollapsed = false;
          // Claim the single app-wide dock slot — closes the Top-5 dock so
          // only one docked diagram shows at a time.
          if (self.$cartBus) self.$cartBus.$emit("dock-claimed", "search");
          self.inPlaceZoomOrigin[key] = snapshot;
          // Position the locked panel over .insights-panel
          // (the Frequently Ordered container on the right
          // column). Capture top, left, and width from the
          // panel's bounding rect at lock-activation time so
          // the fixed-positioned wrap adopts the right
          // column's exact geometry.
          //
          // Why .insights-panel and not the page title:
          // earlier rounds anchored the locked panel at the
          // page title in the LEFT column, where it visually
          // covered the form steps. User feedback: prefer the
          // panel to appear in the RIGHT column instead, where
          // Frequently Ordered currently sits — keeps the form
          // steps clickable and uses what would otherwise be
          // dead space when the user is focused on parts.
          //
          // Min-clamp on top to 10px guards against locking
          // while scrolled past the panel (negative rect.top
          // would pin the image above viewport).
          // Pin the locked image into the RIGHT column, at the cart's
          // position. The old anchor (.insights-panel / Frequently Ordered)
          // was removed from this page, which is why the lock used to
          // collapse to zero width and "disappear" — target the floating
          // cart stack instead, falling back to the right viewport edge if
          // it isn't mounted (e.g. cart empty / collapsed).
          // Pin the locked image at the floating cart/Top-5 stack's position
          // (right content edge) so it reads as the top of that shared
          // section. Use the live rect when the stack is mounted + visible;
          // when the cart is empty the stack is display:none (0×0 rect), so
          // fall back to its COMPUTED css position (width/right/top still
          // resolve) — the old fixed fallback dropped the image over the page
          // header, which both mis-placed it and hid the toolbar behind the
          // sticky header.
          var anchorEl = document.querySelector('.floating-widget-stack');
          var rect = anchorEl ? anchorEl.getBoundingClientRect() : null;
          if (rect && rect.width > 0) {
            self.lockedStickyTop = Math.max(rect.top, 10);
            self.lockedStickyLeft = rect.left;
            self.lockedStickyWidth = rect.width;
          } else if (anchorEl) {
            var cs = window.getComputedStyle(anchorEl);
            var w = parseFloat(cs.width) || 360;
            var rightPx = parseFloat(cs.right);
            if (!isFinite(rightPx)) rightPx = 0;
            var topPx = parseFloat(cs.top);
            self.lockedStickyWidth = w;
            self.lockedStickyLeft = Math.max(window.innerWidth - rightPx - w, 10);
            self.lockedStickyTop = isFinite(topPx) && topPx > 0 ? topPx : 200;
          } else {
            self.lockedStickyWidth = 360;
            self.lockedStickyLeft = Math.max(window.innerWidth - 360 - 24, 10);
            self.lockedStickyTop = 200;
          }
        }
        self.inPlaceZoomLocked[key] = willLock;
        // Tell App.vue to drop the floating cart/Top-5 stack below the
        // now-locked image (with a small gap) so the image sits above it.
        self._emitLockedImage();
      }, 300);
    },

    // ===== dockDiagramForPart =====
    // Clicking a part-row thumbnail brings the assembly DIAGRAM (the big
    // image carrying the callout numbers) into the right-hand floating
    // panel — the same dock action as clicking the assembly image itself —
    // instead of opening the popup. If this assembly has no diagram on
    // file, fall back to the part's own thumbnail in the popup so the
    // click still does something.
    //
    // NOTE: auto-zooming the docked diagram to the part's specific callout
    // (e.g. "8S" parsed from "(L8S-18-R)") needs the on-image position of
    // that label, which isn't in the data yet — see the parseCalloutToken
    // stub and the open question on approach (OCR vs stored coordinates).
    dockDiagramForPart(part) {
      if (this.assemblyImage) {
        // Claim the single app-wide dock slot IMMEDIATELY on every part click
        // (synchronously) so the Top-5 dock always closes. Previously the
        // claim only fired from inside onImageClick's 300ms lock timer and
        // only on the first lock, so repeat clicks raced and left two panels.
        if (this.$cartBus) this.$cartBus.$emit("dock-claimed", "search");
        this.imageCollapsed = false;
        if (!this.inPlaceZoomLocked.assembly) {
          this.onImageClick("assembly", this.assemblyImage);
        } else {
          // Already docked: snap back to the full view first so switching
          // from one part to another is ALWAYS visible (the old behavior
          // looked like "nothing happened" when the next part had no
          // callout match). zoomDockedToCallout below then re-aims at the
          // new part's callout if one is found.
          this.$set(this.inPlaceZoomOrigin, "assembly", "50% 50%");
          this.lockedZoom = 4;
        }
        this.zoomDockedToCallout(part);
        this._armImgMeasure();
        return;
      }
      var thumb = part && this.assemblyThumbs[part.assemblyId];
      if (thumb) this.openImageModal(thumb);
    },

    // Keep the balloon-hotspot layer aligned: measure the docked image now, and
    // again after the lock/zoom transition settles, plus (once) on window
    // resize. measureAssemblyImg() no-ops if the ref is gone, so a lingering
    // resize handler after teardown is harmless.
    _armImgMeasure() {
      var self = this;
      this.$nextTick(function () { self.measureAssemblyImg(); });
      setTimeout(function () { self.measureAssemblyImg(); }, 450);
      if (!this._imgMeasureBound) {
        this._imgMeasureBound = function () { self.measureAssemblyImg(); };
        window.addEventListener("resize", this._imgMeasureBound);
      }
    },

    // ===== zoomDockedToCallout (best-effort) =====
    // After docking the assembly diagram, try to OCR-locate the part's
    // callout (e.g. "8S" parsed from "(L8S-18-R)") on the image and aim
    // the locked-zoom transform-origin at it. Silent no-op if OCR can't
    // read the image (cross-origin/CORS, CDN blocked) or there's no
    // confident label match — the diagram just stays docked at its
    // default view, so the click never "does nothing".
    zoomDockedToCallout(part) {
      var self = this;
      var img = this.assemblyImage;
      // Prefer the pre-computed Azure callout map (reliable, no CORS). Fall back
      // to the live in-browser locator (currently disabled) only if no match.
      this.ensureCalloutMap()
        .then(function () {
          var pre = self.precomputedCallout(part);
          if (pre) {
            self._aimDockedZoom(img, pre.xFrac, pre.yFrac);
            return;
          }
          self._zoomViaLiveOcr(part, img);
        })
        .catch(function () {
          self._zoomViaLiveOcr(part, img);
        });
    },

    // Aim the docked assembly diagram's locked-zoom transform-origin at a
    // fractional (0..1) point and zoom in. Applied AFTER the 300ms click-to-lock
    // engages — the lock snapshot-resets the origin, which would clobber ours.
    _aimDockedZoom(img, xFrac, yFrac) {
      var self = this;
      setTimeout(function () {
        if (!self.inPlaceZoomLocked.assembly) return;
        if (self.assemblyImage !== img) return; // assembly changed meanwhile
        self.$set(
          self.inPlaceZoomOrigin,
          "assembly",
          (xFrac * 100).toFixed(2) + "% " + (yFrac * 100).toFixed(2) + "%"
        );
        self.lockedZoom = 5;
      }, 350);
    },

    // Fallback: the original client-side OCR locator (no-op while disabled).
    _zoomViaLiveOcr(part, img) {
      var self = this;
      var desc = (part && (part.description || part.code)) || "";
      locateCallout(img, desc)
        .then(function (loc) {
          if (loc) self._aimDockedZoom(img, loc.xFrac, loc.yFrac);
        })
        .catch(function () {
          /* best-effort: leave the diagram docked without zoom */
        });
    },

    // Lazy-load the pre-computed callout map once. 404 / parse error degrades
    // to {} so the dock still works (just no auto-zoom) before the asset ships.
    ensureCalloutMap() {
      if (this._calloutMapPromise) return this._calloutMapPromise;
      var self = this;
      // Fetch the map as a STATIC asset from public/ (served at the dist ROOT
      // as /assembly-callouts.json), NOT a bundled import. Two hard-won
      // constraints: (1) inlining or importing the 1.86MB JSON makes the prod
      // build hang (terser/babel choke on the giant literal), so it must stay
      // external; (2) the dist->Azure watcher only syncs the root + known
      // folders (js/css/img/videos) — a public/callouts/ SUBfolder 404s on the
      // host — so the map lives at the root. Loads once on first diagram
      // interaction; degrades to {} on any failure so the dock still works.
      this._calloutMapPromise = fetch("/assembly-callouts.json")
        .then(function (r) { return r.ok ? r.json() : {}; })
        .then(function (m) {
          self.calloutMap = m || {};
          return self.calloutMap;
        })
        .catch(function () {
          self.calloutMap = {};
          return self.calloutMap;
        });
      return this._calloutMapPromise;
    },

    // Strip the blob prefix off a docked image URL to get its map key
    // ("…/images/DLM/498.jpg" -> "DLM/498.jpg").
    imagePathFromUrl(url) {
      if (!url || !this.machineImageUrl) return null;
      return url.indexOf(this.machineImageUrl) === 0
        ? decodeURI(url.slice(this.machineImageUrl.length))
        : null;
    },

    // Pre-computed balloon center for this part's Item on the docked diagram,
    // or null (unknown image / no match / map absent).
    precomputedCallout(part) {
      if (!this.calloutMap || !this.assemblyImage || !part || !part.item) return null;
      var path = this.imagePathFromUrl(this.assemblyImage);
      if (!path) return null;
      var entry = this.calloutMap[path];
      return entry ? findCalloutBox(entry, part.item) : null;
    },

    // Pre-computed balloon center for this part on ITS OWN sub-assembly drawing
    // (partThumb) — used to crop the row thumbnail to the balloon. Cached per
    // (imagePath|item); only caches once the map is loaded so it recomputes when
    // ensureCalloutMap resolves. null = no drawing / map / matching balloon.
    partCalloutFrac(part) {
      var map = this.calloutMap;
      if (!map || !part || !part.item) return null;
      var url = this.partThumb(part);
      if (!url) return null;
      var path = this.imagePathFromUrl(url);
      if (!path) return null;
      if (!this._partFracCache) this._partFracCache = {};
      var key = path + "|" + String(part.item).toUpperCase();
      if (Object.prototype.hasOwnProperty.call(this._partFracCache, key)) return this._partFracCache[key];
      var entry = map[path];
      var frac = entry ? findCalloutBox(entry, part.item) : null;
      this._partFracCache[key] = frac;
      return frac;
    },
    // Inline style turning the thumb into a zoomed crop of the drawing centered
    // on the part's balloon. background-position % aligns the balloon point to
    // the thumb center; ~650% size shows a tight region around it.
    partThumbCropStyle(part) {
      var f = this.partCalloutFrac(part), url = this.partThumb(part);
      if (!f || !url) return null;
      return {
        backgroundImage: 'url("' + url + '")',
        backgroundRepeat: "no-repeat",
        backgroundSize: "650%",
        backgroundPosition: (f.xFrac * 100).toFixed(2) + "% " + (f.yFrac * 100).toFixed(2) + "%"
      };
    },
    // Parts of a sub-assembly matching the active filters: the text filter (code /
    // item # / price) AND the "Show discounted wear parts only" toggle (keeps only
    // 2%-discount-eligible parts). No active filter returns all. Drives the list,
    // per-category count, and category visibility.
    // Display-ready unit price for a part — exposed as data-part-price so the guide
    // tour's {part price} token can read it off the row (matches the on-screen price).
    partPriceText(part) {
      var n = part && part.price;
      if (n == null || isNaN(Number(n))) return "";
      return Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });
    },
    filteredParts(asm) {
      var self = this;
      var parts = (asm && asm.parts) || [];
      var q = (this.partFilter || "").trim().toLowerCase();
      var dOnly = this.showDiscountedOnly;
      if (!q && !dOnly) return parts;
      return parts.filter(function (p) {
        if (dOnly && !self.isWearEligible(p)) return false;
        if (!q) return true;
        return (
          String(p.code || "").toLowerCase().indexOf(q) >= 0 ||
          String(p.description || "").toLowerCase().indexOf(q) >= 0 ||
          String(p.item || "").toLowerCase().indexOf(q) >= 0 ||
          String(p.price == null ? "" : p.price).toLowerCase().indexOf(q) >= 0
        );
      });
    },

    // ===== Clickable balloon overlay on the docked diagram =====
    // Diagram finished loading: measure it AND make sure the callout map is
    // loaded, so balloon hotspots light up as soon as the drawing is shown —
    // no need to click a part image first.
    onAssemblyImgLoad() {
      this.measureAssemblyImg();
      this.ensureCalloutMap();
      this.ensureCalloutOverrides();   // admin balloon fixes (clickable for all users)
      // Auto-dock the assembly diagram to the right panel on load so the
      // customer doesn't have to click the (now-redundant) inline image — it
      // just goes right. Guarded to fire once per assembly image so it can't
      // re-trigger or hit onImageClick's double-click-to-modal path.
      if (this.assemblyImage && !this.inPlaceZoomLocked.assembly &&
          this._autoDockedFor !== this.assemblyImage) {
        this._autoDockedFor = this.assemblyImage;
        // Start the auto-docked view ZOOMED OUT (whole machine), not the default
        // 4x. onImageClick doesn't touch lockedZoom, so setting it first sticks;
        // the customer zooms in / clicks a part or balloon from the full view.
        this.$set(this.inPlaceZoomOrigin, "assembly", "50% 50%");
        this.lockedZoom = 1;
        this.onImageClick("assembly", this.assemblyImage);
      }
    },
    // Measure the docked assembly <img>'s untransformed box (offset + client
    // size within its wrap) so hotspots can be placed over the contained image.
    measureAssemblyImg() {
      var el = this.$refs.assemblyImg;
      if (Array.isArray(el)) el = el[0];
      if (!el || !el.clientWidth) return;
      this.assemblyImgBox = {
        cw: el.clientWidth, ch: el.clientHeight,
        ox: el.offsetLeft, oy: el.offsetTop
      };
    },
    // Position one balloon hotspot (px, relative to the img box = the overlay
    // layer). Accounts for object-fit:contain letterboxing; the layer mirrors
    // the img's zoom transform, so these stay aligned at any zoom.
    calloutHotspotStyle(b, box) {
      box = box || this.assemblyImgBox;
      var entry = this.dockedBalloons.entry;
      if (!box.cw || !box.ch || !entry || !entry.w || !entry.h || !b.box) return { display: "none" };
      var scale = Math.min(box.cw / entry.w, box.ch / entry.h);
      var offX = (box.cw - entry.w * scale) / 2;
      var offY = (box.ch - entry.h * scale) / 2;
      // Center a CIRCLE on the balloon. The OCR box wraps just the printed
      // number (wider than tall for 2-char codes), so a square sized to the
      // larger dimension — slightly enlarged to approximate the balloon ring —
      // reads as a round ring around the number rather than a rectangle.
      var cx = offX + (b.box.x + b.box.w / 2) * scale;
      var cy = offY + (b.box.y + b.box.h / 2) * scale;
      var d = Math.max(b.box.w, b.box.h) * scale * 1.6;
      return {
        left: (cx - d / 2) + "px",
        top: (cy - d / 2) + "px",
        width: d + "px",
        height: d + "px"
      };
    },
    // The overlay layer matches the img's box + mirrors its zoom transform.
    calloutLayerStyle() {
      var box = this.assemblyImgBox;
      return {
        left: box.ox + "px", top: box.oy + "px",
        width: box.cw + "px", height: box.ch + "px",
        transformOrigin: this.inPlaceZoomOrigin.assembly,
        transform: this.inPlaceZoomLocked.assembly ? "scale(" + this.lockedZoom + ")" : null
      };
    },
    // Click a balloon -> dock the diagram to the right panel zoomed to THIS
    // balloon, AND reveal/highlight/scroll to its part in the list.
    onBalloonClick(b) {
      if (!b || !b.code) return;
      this._dockToBalloonBox(b);
      this.revealItem(b.code);
    },
    // ===== Clickable balloons in the zoom MODAL (double-click view) =====
    // Measure the modal image's base (untransformed, scale=1) box so the hotspot
    // overlay can sit on it; the layer then mirrors the image's pan/zoom transform.
    measureModalImg() {
      var self = this;
      this.$nextTick(function () {
        var el = self.$refs.modalImg;
        if (Array.isArray(el)) el = el[0];
        if (!el || !el.clientWidth) return;
        self.modalImgBox = { cw: el.clientWidth, ch: el.clientHeight, ox: el.offsetLeft, oy: el.offsetTop };
        // Natural pixel dims — used by the balloon editor to seed reference dims
        // for a brand-new drawing that isn't in the OCR map yet.
        self._assemblyNatural = { w: el.naturalWidth || 0, h: el.naturalHeight || 0 };
      });
    },
    // Overlay layer sized to the modal image's base box, mirroring its pan/zoom
    // transform (translate + scale, center origin) so hotspots track the image.
    modalCalloutLayerStyle() {
      var box = this.modalImgBox;
      return {
        left: box.ox + "px", top: box.oy + "px",
        width: box.cw + "px", height: box.ch + "px",
        transform: "translate(" + this.pointX + "px, " + this.pointY + "px) scale(" + this.scale + ")",
        transformOrigin: "center center"
      };
    },
    // Click a balloon in the modal -> close it + dock/reveal the part (same
    // outcome as clicking the balloon on the docked diagram).
    onModalBalloonClick(b) {
      if (!b || !b.code) return;
      this.closeImageModal();
      this.onBalloonClick(b);
    },

    // ===================================================================
    // Master-only balloon editor — runs in the double-click zoom MODAL.
    // Add/move/resize/relabel balloons OCR couldn't read (or hide spurious
    // ones). Saved server-side (CalloutController) + merged over the OCR map
    // in dockedBalloons, so fixes are clickable for every user everywhere.
    // ===================================================================
    _normCode(s) { return String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, ""); },
    // Load the saved overrides once (for ALL users, so admin fixes are clickable).
    ensureCalloutOverrides() {
      if (this._calloutOverridesPromise) return this._calloutOverridesPromise;
      var self = this;
      this._calloutOverridesPromise = apiService.getCalloutOverrides()
        .then(function (d) {
          self.calloutOverrides = (d && d.images) ? d.images : {};
          return self.calloutOverrides;
        })
        .catch(function () { self.calloutOverrides = {}; return self.calloutOverrides; });
      return this._calloutOverridesPromise;
    },
    // Enter edit mode (in the zoom MODAL): seed the working copy from saved
    // overrides. Editing happens on the big modal image (zoom/pan available),
    // so there's no docked-panel manipulation here.
    startBalloonEdit() {
      var self = this;
      this.ensureCalloutOverrides().then(function () {
        var path = self.imagePathFromUrl(self.assemblyImage);
        // Brand-new drawing (no OCR entry + no prior override): seed ref dims from
        // the image's natural size so dockedBalloons can synthesize an entry.
        var entry = self.dockedBalloons.entry;
        if ((!entry || !entry.w) && path) {
          var nat = self._assemblyNatural || {};
          if (nat.w && nat.h && !(self.calloutOverrides[path] && self.calloutOverrides[path].w)) {
            self.$set(self.calloutOverrides, path, { w: nat.w, h: nat.h, managed: true, balloons: [] });
          }
        }
        // Seed the working set from the CURRENT effective bubbles (OCR map OR a
        // managed override) so EVERY bubble is editable — drag / rename / resize /
        // delete uniformly. Boxes are already in entry-pixel space.
        self.editBalloons = (self.dockedBalloons.items || []).map(function (it) {
          return { code: it.code, x: it.box.x, y: it.box.y, w: it.box.w, h: it.box.h };
        });
        self.editSelectedIdx = -1;
        self.editDirty = false;
        self.editError = "";
        self.balloonEditMode = true;
        self.$nextTick(function () { self.measureModalImg(); });
      });
    },
    cancelBalloonEdit() {
      this.balloonEditMode = false;
      this.editBalloons = [];
      this.editHidden = [];
      this.editSelectedIdx = -1;
      this.editDirty = false;
      this.editError = "";
      this._editDrag = null;
    },
    // Convert a screen point to entry-space px using the MODAL image's on-screen
    // (transformed) rect — correct at any zoom/pan. Inverts the object-fit:contain
    // letterbox inside the element box.
    _modalPxToEntry(clientX, clientY) {
      var el = this.$refs.modalImg;
      if (Array.isArray(el)) el = el[0];
      if (!el) return null;
      var rect = el.getBoundingClientRect();
      var box = this.modalImgBox, entry = this.dockedBalloons.entry;
      if (!rect.width || !rect.height || !box.cw || !entry || !entry.w) return null;
      var fx = (clientX - rect.left) / rect.width, fy = (clientY - rect.top) / rect.height;
      var elx = fx * box.cw, ely = fy * box.ch;            // element-box px (incl. letterbox)
      var cs = Math.min(box.cw / entry.w, box.ch / entry.h);
      var offX = (box.cw - entry.w * cs) / 2, offY = (box.ch - entry.h * cs) / 2;
      return { x: (elx - offX) / cs, y: (ely - offY) / cs };
    },
    // Click empty drawing area in the modal (NOT a pan-drag, NOT a marker) -> add
    // a balloon there. Wired to the zoom-wrap click; markers @click.stop, and a
    // pan sets _dragMoved so this no-ops then.
    onModalAddClick(e) {
      if (!this.balloonEditMode || this.modalImage !== this.assemblyImage) return;
      if (this._dragMoved) return;
      var p = this._modalPxToEntry(e.clientX, e.clientY);
      if (!p) return;
      var entry = this.dockedBalloons.entry;
      var d = Math.max(entry.w, entry.h) * 0.03;
      this.editBalloons.push({ code: "", x: p.x - d / 2, y: p.y - d / 2, w: d, h: d });
      this.editSelectedIdx = this.editBalloons.length - 1;
      this.editDirty = true;
      var self = this;
      this.$nextTick(function () {
        var el = self.$refs.editCodeInput;
        if (Array.isArray(el)) el = el[0];
        if (el && el.focus) el.focus();
      });
    },
    // Marker style in the modal layer (the layer mirrors the modal img transform).
    editMarkerStyle(b) {
      return this.calloutHotspotStyle({ box: { x: b.x, y: b.y, w: b.w, h: b.h } }, this.modalImgBox);
    },
    startMarkerDrag(idx) {
      if (!this.balloonEditMode) return;
      this._dragMoved = false;
      this.editSelectedIdx = idx;
      this._editDrag = { idx: idx, mode: "move" };
      this._bindEditDrag();
    },
    startMarkerResize(idx) {
      if (!this.balloonEditMode) return;
      this._dragMoved = false;
      this.editSelectedIdx = idx;
      this._editDrag = { idx: idx, mode: "resize" };
      this._bindEditDrag();
    },
    _bindEditDrag() {
      var self = this;
      this._onEditMove = function (ev) { self._editDragMove(ev); };
      this._onEditUp = function () { self._editDragEnd(); };
      window.addEventListener("mousemove", this._onEditMove);
      window.addEventListener("mouseup", this._onEditUp);
    },
    // Move = set the balloon CENTER to the cursor's entry-px; resize = radius is
    // the distance from the (fixed) center to the cursor. Both use the absolute
    // modal inversion so they're correct at any zoom/pan.
    _editDragMove(e) {
      var d = this._editDrag; if (!d) return;
      this._dragMoved = true;       // a marker drag also suppresses click-to-add
      var p = this._modalPxToEntry(e.clientX, e.clientY);
      var b = this.editBalloons[d.idx];
      if (!p || !b) return;
      if (d.mode === "move") {
        this.$set(b, "x", p.x - b.w / 2);
        this.$set(b, "y", p.y - b.h / 2);
      } else {
        var cx = b.x + b.w / 2, cy = b.y + b.h / 2;
        var r = Math.max(6, Math.sqrt((p.x - cx) * (p.x - cx) + (p.y - cy) * (p.y - cy)));
        this.$set(b, "x", cx - r); this.$set(b, "y", cy - r);
        this.$set(b, "w", r * 2); this.$set(b, "h", r * 2);
      }
      this.editDirty = true;
    },
    _editDragEnd() {
      this._editDrag = null;
      if (this._onEditMove) window.removeEventListener("mousemove", this._onEditMove);
      if (this._onEditUp) window.removeEventListener("mouseup", this._onEditUp);
    },
    removeEditBalloon(idx) {
      this.editBalloons.splice(idx, 1);
      if (this.editSelectedIdx === idx) this.editSelectedIdx = -1;
      else if (this.editSelectedIdx > idx) this.editSelectedIdx--;
      this.editDirty = true;
    },
    toggleHideOcr(code) {
      var c = this._normCode(code);
      var i = this.editHidden.indexOf(c);
      if (i >= 0) this.editHidden.splice(i, 1);
      else this.editHidden.push(c);
      this.editDirty = true;
    },
    isOcrHidden(code) { return this.editHidden.indexOf(this._normCode(code)) >= 0; },
    // ===== Admin: add a missing catalog Item # on a part row =====
    partDbId(part) { return (part && (part.id || part.partId)) || null; },
    startAddItem(part) {
      this.addItemFor = this.partDbId(part);
      this.addItemCode = (part && part.item) || "";   // pre-fill so the editor edits/clears too
      this.addItemError = "";
      var self = this;
      this.$nextTick(function () {
        var el = self.$refs.addItemInput;
        if (Array.isArray(el)) el = el[0];
        if (el && el.focus) el.focus();
        if (el && el.select) el.select();
      });
    },
    cancelAddItem() {
      this.addItemFor = null;
      this.addItemCode = "";
      this.addItemError = "";
    },
    // Click the Item # box: a SINGLE click docks the diagram (full experience,
    // for everyone). For admins a DOUBLE click edits the item # in place — so the
    // single-click dock is delayed briefly and cancelled if a dblclick follows.
    onItemBoxClick(part) {
      if (this.addItemFor === this.partDbId(part)) return;   // already editing this row
      if (!this.isMasterUser) { this.dockDiagramForPart(part); return; }
      var self = this;
      if (this._itemClickTimer) clearTimeout(this._itemClickTimer);
      this._itemClickTimer = setTimeout(function () {
        self._itemClickTimer = null;
        self.dockDiagramForPart(part);
      }, 250);
    },
    // Admin double-click -> edit the item # in place (cancels the pending dock).
    onItemBoxDblClick(part) {
      if (!this.isMasterUser) return;
      if (this._itemClickTimer) { clearTimeout(this._itemClickTimer); this._itemClickTimer = null; }
      if (this.addItemFor === this.partDbId(part)) return;   // already editing
      this.startAddItem(part);
    },
    saveAddItem(part) {
      var self = this;
      var id = this.partDbId(part);
      if (!id) return;
      if (this.addItemSaving) return;          // a save is already in flight (Enter + blur)
      if (this.addItemFor !== id) return;      // not actively editing this row (cancelled/closed)
      var code = (this.addItemCode || "").trim();   // blank = clear the item # (remove)
      if (code === ((part.item || "").trim())) { this.cancelAddItem(); return; }  // unchanged -> just close
      this.addItemSaving = true;
      this.addItemError = "";
      apiService.setPartItem(id, { item: code, code: part.code })
        .then(function (res) {
          self.$set(part, "item", (res && res.item) || code);
          self._partFracCache = {};       // item changed -> bust the thumb-crop cache
          self.addItemFor = null;
          self.addItemCode = "";
          self.addItemSaving = false;
        })
        .catch(function (err) {
          self.addItemSaving = false;
          var status = err && err.response && err.response.status;
          if (status === 400) {
            var d = err.response.data;
            self.addItemError = (d && (d.title || (typeof d === "string" ? d : null))) || "Not saved (code mismatch).";
          } else if (status === 401 || status === 403) {
            self.addItemError = "Master only.";
          } else {
            self.addItemError = "Save failed — try again.";
          }
        });
    },
    // Persist: merge this image's working balloons/hidden into the overrides blob
    // and POST the whole thing; on success refresh the in-memory map.
    saveBalloonEdit() {
      var self = this;
      var path = this.imagePathFromUrl(this.assemblyImage);
      if (!path) { this.cancelBalloonEdit(); return; }
      var entry = this.dockedBalloons.entry;
      var refW = (entry && entry.w) || 0, refH = (entry && entry.h) || 0;
      var balloons = this.editBalloons
        .filter(function (b) { return self._normCode(b.code); })
        .map(function (b) {
          return { code: self._normCode(b.code), x: Math.round(b.x), y: Math.round(b.y),
                   w: Math.round(b.w), h: Math.round(b.h) };
        });
      var images = {};
      Object.keys(this.calloutOverrides || {}).forEach(function (k) { images[k] = self.calloutOverrides[k]; });
      // This image is now fully admin-managed (managed:true) — the stored set IS
      // the bubble list; the OCR map is ignored for it going forward.
      images[path] = { w: refW, h: refH, managed: true, balloons: balloons };
      var email = (this.currentUser && this.currentUser.email) || "";
      var payload = { images: images, updatedUtc: new Date().toISOString(), updatedBy: email };
      this.editSaving = true;
      this.editError = "";
      apiService.saveCalloutOverrides(payload)
        .then(function () {
          self.calloutOverrides = images;     // reactive reassign -> dockedBalloons recomputes
          self._partFracCache = {};           // balloons changed -> bust the thumb-crop cache
          self.editSaving = false;
          self.balloonEditMode = false;
          self.editSelectedIdx = -1;
          self.editDirty = false;
        })
        .catch(function (err) {
          self.editSaving = false;
          var status = err && err.response && err.response.status;
          self.editError = (status === 401 || status === 403)
            ? "You don't have permission to save (Master only)."
            : "Save failed — please try again.";
        });
    },
    // Dock the assembly diagram into the floating panel and zoom straight to a
    // KNOWN balloon box (from the overlay). This bypasses the item->token
    // re-derivation in precomputedCallout(), so it aims correctly even when the
    // OCR token differs from the part Item (e.g. balloon read "110W", part
    // "10W") — the same gesture as clicking the part's Item box, but anchored
    // on the clicked balloon.
    _dockToBalloonBox(b) {
      var img = this.assemblyImage;
      var entry = this.dockedBalloons.entry;
      if (!img || !entry || !entry.w || !entry.h || !b || !b.box) return;
      var xFrac = (b.box.x + b.box.w / 2) / entry.w;
      var yFrac = (b.box.y + b.box.h / 2) / entry.h;
      if (this.$cartBus) this.$cartBus.$emit("dock-claimed", "search");
      this.imageCollapsed = false;
      if (!this.inPlaceZoomLocked.assembly) {
        this.onImageClick("assembly", img);
      } else {
        this.$set(this.inPlaceZoomOrigin, "assembly", "50% 50%");
        this.lockedZoom = 4;
      }
      this._aimDockedZoom(img, xFrac, yFrac);
      this._armImgMeasure();
    },
    // Find a loaded part (in ANY category, expanded or not) whose Item matches
    // the balloon code. Exact match first; then a fuzzy fallback for OCR
    // digit-doubling/typos (e.g. the "10W" balloon read as "110W") — accept a
    // part whose Item is a suffix of the code, or vice-versa, min length 2 so
    // noise can't match, preferring the longest such Item.
    _findPartByItem(code) {
      var norm = function (s) { return String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, ""); };
      var endsWith = function (s, suf) {
        return s.length >= suf.length && s.slice(s.length - suf.length) === suf;
      };
      var want = norm(code);
      if (!want) return null;
      var subs = this.displayedSubAssemblies || [];
      // 1) exact Item match.
      for (var i = 0; i < subs.length; i++) {
        var ps = subs[i].parts || [];
        for (var j = 0; j < ps.length; j++) {
          if (norm(ps[j].item) === want) return { part: ps[j], asm: subs[i] };
        }
      }
      // 2) fuzzy suffix fallback (only when nothing matched exactly).
      var best = null, bestLen = 0;
      for (var a = 0; a < subs.length; a++) {
        var pp = subs[a].parts || [];
        for (var k = 0; k < pp.length; k++) {
          var it = norm(pp[k].item);
          if (it.length < 2) continue;
          var match = (want.length > it.length && endsWith(want, it)) ||
                      (it.length > want.length && endsWith(it, want));
          if (match && it.length > bestLen) { best = { part: pp[k], asm: subs[a] }; bestLen = it.length; }
        }
      }
      return best;
    },
    _scrollToPartCode(pc) {
      var want = String(pc || "").trim().toLowerCase();
      var rows = this.$el.querySelectorAll("[data-part-code]");
      for (var i = 0; i < rows.length; i++) {
        if ((rows[i].getAttribute("data-part-code") || "").trim().toLowerCase() === want) {
          if (rows[i].scrollIntoView) rows[i].scrollIntoView({ behavior: "smooth", block: "center" });
          return true;
        }
      }
      return false;
    },
    // Reveal the part for a balloon code: expand its category if collapsed; if
    // it's hidden by "wear parts only", turn that off and finish after the parts
    // reload (handled in the wearAssemblies watcher via _pendingRevealItem).
    revealItem(code) {
      var self = this;
      var hit = this._findPartByItem(code);
      if (hit) {
        if (hit.asm && hit.asm.assemblyId != null) {
          this.$set(this.expandedWearAssemblies, hit.asm.assemblyId, true);
        }
        // Float the picked part to the TOP of its sub-assembly AND float that
        // sub-assembly to the top of the list, using the EXACT objects
        // _findPartByItem returned (not a code re-search, which could float the
        // wrong instance when a part appears in more than one sub-assembly).
        this.floatHitToTop(hit);
        this.setHeaderHighlight(hit.part.code);
        this.$nextTick(function () { self._scrollToPartCode(hit.part.code); });
        return;
      }
      if (this.configuredWear) {
        this._pendingRevealItem = code;
        this.configuredWear = false; // re-fetches all parts (configuredWear watcher)
      }
    },
    // Float the picked part to the TOP of its sub-assembly AND that sub-assembly
    // to the TOP of the list, in the LIVE wearAssemblies. Uses the EXACT objects
    // _findPartByItem returned (indexOf), with code/id fallbacks. Array splice/
    // unshift are reactive in Vue 2 + filteredParts/displayedSubAssemblies
    // preserve order, so the reorder shows immediately. Same outcome the header
    // search gets via _floatHeaderSearchPartToTop during (re)load.
    floatHitToTop(hit) {
      if (!hit || !hit.asm || !Array.isArray(hit.asm.parts)) return;
      var sa = hit.asm;
      // 1) the PART to the top of its sub-assembly.
      var pIdx = sa.parts.indexOf(hit.part);
      if (pIdx < 0 && hit.part) {
        var c = String(hit.part.code || hit.part.partCode || hit.part.partNumber || "").trim().toLowerCase();
        if (c) pIdx = sa.parts.findIndex(function (p) { return String(p.code || p.partCode || p.partNumber || "").trim().toLowerCase() === c; });
      }
      if (pIdx > 0) { var moved = sa.parts.splice(pIdx, 1)[0]; sa.parts.unshift(moved); }
      // 2) the CATEGORY (sub-assembly) to the top of the full list.
      var list = this.wearAssemblies || [];
      var sIdx = list.indexOf(sa);
      if (sIdx < 0 && sa.assemblyId != null) {
        sIdx = list.findIndex(function (x) { return x && String(x.assemblyId) === String(sa.assemblyId); });
      }
      if (sIdx > 0) { var movedSa = list.splice(sIdx, 1)[0]; list.unshift(movedSa); }
    },

    // ===== onLockedMouseDown =====
    // Click-count gesture machine for the locked image panel.
    // Runs only when locked (returns early otherwise). Each
    // mousedown increments _lockClickCount; the count drives
    // which gesture mode is active for the ensuing drag:
    //
    //   1 click + drag  =  pan zoom inside the box (mode 1).
    //                     Updates inPlaceZoomOrigin so the
    //                     transform-origin follows the
    //                     cursor — natural-scroll style:
    //                     drag right shows what was on the
    //                     left. Used to find part numbers
    //                     that are clipped by the visible
    //                     window at 4x zoom.
    //
    //   2 clicks + drag =  move the whole panel around the
    //                     page (mode 2). Updates
    //                     lockedStickyTop / lockedStickyLeft.
    //
    //   3 clicks        =  close the lock (mode 3). Fires
    //                     immediately on the 3rd mousedown,
    //                     no drag needed.
    //
    // Click counting:
    //   - A drag "consumes" the count — onUp resets it to 0
    //     after a drag so back-to-back drags don't escalate.
    //   - A click without drag schedules a 400ms reset timer;
    //     another mousedown within the window cancels the
    //     timer and bumps the count toward 2 or 3.
    //
    // Listeners attach to document (not the wrap) so dragging
    // continues even if the cursor leaves the wrap — important
    // at 4x zoom where the visible window is small and users
    // will routinely drag past the edges.
    onLockedMouseDown(key, e) {
      if (!this.inPlaceZoomLocked[key]) return;

      // Block the browser's native HTML5 image drag (the
      // ghost-image-follows-cursor behavior). Without this,
      // mousedown on an <img> kicks off the OS-level drag
      // which intercepts mousemove events and makes our
      // custom drag feel like it "doesn't start" until the
      // user releases the mouse. preventDefault here +
      // draggable="false" on the img tag together cover both
      // major browser paths.
      if (e && e.preventDefault) e.preventDefault();

      // Cancel any pending count-reset; this click extends
      // the rapid-click window.
      if (this._lockClickResetTimer) {
        clearTimeout(this._lockClickResetTimer);
        this._lockClickResetTimer = null;
      }
      this._lockClickCount = (this._lockClickCount || 0) + 1;
      var clickNum = this._lockClickCount;
      var self = this;

      // Closing the lock is now done by the explicit × button
      // in the panel's top-right corner (see onCloseLock).
      // Triple-click unlock has been retired in favor of that
      // visible affordance, so we cap meaningful click counts
      // at 2 (mode 1 = pan inside, mode 2 = move panel) and
      // ignore any escalation past that.
      if (clickNum > 2) {
        this._lockClickCount = 2;
        clickNum = 2;
      }

      // Snapshot starting state for delta-based drag math.
      var startX = e.clientX;
      var startY = e.clientY;
      var startTop = this.lockedStickyTop;
      var startLeft = this.lockedStickyLeft;
      var wrap = e.currentTarget;
      // Parse the current transform-origin so mode-1 pan can
      // delta from it (natural-scroll feel: visible content
      // moves opposite the drag direction).
      var origin = (this.inPlaceZoomOrigin[key] || "50% 50%")
        .split(" ");
      var startOriginX = parseFloat(origin[0]);
      var startOriginY = parseFloat(origin[1]);
      if (isNaN(startOriginX)) startOriginX = 50;
      if (isNaN(startOriginY)) startOriginY = 50;
      // Zoom scale matches the CSS transform: scale(4) on the
      // locked image. Hardcoded mirror — if CSS scale changes,
      // update here too.
      var ZOOM_SCALE = 4;
      var moved = false;

      // Tag the wrap with a mode-specific class so the cursor
      // can flip to crosshair (pan) or grabbing (move).
      var modeClass = clickNum === 1
        ? "is-panning-internal"
        : "is-dragging";

      function onMove(ev) {
        var dx = ev.clientX - startX;
        var dy = ev.clientY - startY;
        if (!moved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
          moved = true;
          if (wrap && wrap.classList) {
            wrap.classList.add(modeClass);
          }
        }
        if (!moved) return;

        if (clickNum === 1) {
          // Mode 1: pan internally. Delta cursor pixels →
          // delta percentage of zoomed-image dimensions.
          // Subtract from startOrigin so visible content
          // moves opposite the drag (natural scroll).
          if (!wrap || !wrap.getBoundingClientRect) return;
          var rect = wrap.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) return;
          var dxPct = (dx / (rect.width * ZOOM_SCALE)) * 100;
          var dyPct = (dy / (rect.height * ZOOM_SCALE)) * 100;
          var nx = startOriginX - dxPct;
          var ny = startOriginY - dyPct;
          // Clamp to image bounds so dragging past the edge
          // doesn't push origin into negative / >100% (which
          // would show empty space outside the image).
          nx = Math.max(0, Math.min(100, nx));
          ny = Math.max(0, Math.min(100, ny));
          self.inPlaceZoomOrigin[key] = nx + "% " + ny + "%";
        } else if (clickNum === 2) {
          // Mode 2: move whole panel.
          self.lockedStickyTop = startTop + dy;
          self.lockedStickyLeft = startLeft + dx;
        }
      }

      function onUp() {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        if (wrap && wrap.classList) {
          wrap.classList.remove(modeClass);
        }
        if (moved) {
          // A drag happened — the gesture has been consumed.
          // Reset count so the NEXT click starts fresh at 1
          // rather than escalating into mode 2 or 3 unwanted.
          self._lockClickCount = 0;
        } else {
          // No drag — give the user time to add another click
          // before we conclude the gesture sequence. 400ms is
          // a typical multi-click window (slightly more than
          // the system double-click default to be forgiving).
          self._lockClickResetTimer = setTimeout(function () {
            self._lockClickCount = 0;
          }, 400);
        }
      }

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },

    // ===== onCloseLock =====
    // Wired to the × button rendered in the locked panel's
    // top-right corner (template) — replaces the previous
    // triple-click unlock gesture with an always-visible
    // affordance. Resets the click-count machine state too,
    // so a fresh click sequence starts on the next lock
    // (otherwise a stale _lockClickResetTimer could leave
    // _lockClickCount at 1 or 2 going into the next session).
    onCloseLock(key) {
      this.inPlaceZoomLocked[key] = false;
      this.imageCollapsed = false;
      this._lockClickCount = 0;
      if (this._lockClickResetTimer) {
        clearTimeout(this._lockClickResetTimer);
        this._lockClickResetTimer = null;
      }
      // Image gone → let the floating stack rise back to the content top.
      this._emitLockedImage();
    },

    // Notify App.vue whether a zoom image is locked and how tall it is, so
    // the global floating cart/Top-5 stack can sit BELOW the pinned image
    // (small gap) or flush at the content top when none is locked. Measured
    // on $nextTick so the locked wrap has rendered at its final height.
    _emitLockedImage() {
      var self = this;
      this.$nextTick(function () {
        var lw = document.querySelector(".machine-image-wrap.is-zoom-locked");
        if (!self.$cartBus) return;
        // Collapsed to the side pill → the image no longer sits at the
        // stack top, so report inactive and the cart/Top-5 stack rises
        // back to the content top.
        var showing = !!lw && !self.imageCollapsed;
        self.$cartBus.$emit("locked-image-change", {
          active: showing,
          height: showing ? Math.round(lw.getBoundingClientRect().height) : 0,
        });
      });
    },

    // ===== collapseLockedImage / expandLockedImage =====
    // The → button in the locked-panel toolbar parks the image as a pill on
    // the right rail (above the cart pill); clicking the pill restores it.
    // The lock itself stays active — only the image's visibility toggles.
    collapseLockedImage() {
      this.imageCollapsed = true;
      this._emitLockedImage();
    },
    expandLockedImage() {
      this.imageCollapsed = false;
      this._emitLockedImage();
    },
    // −/+ zoom controls on the docked image. Adjust the locked zoom scale
    // (clamped 1×–8×); the <img> binds transform: scale(lockedZoom).
    lockedZoomIn() {
      this.lockedZoom = Math.min(8, this.lockedZoom + 1);
    },
    lockedZoomOut() {
      this.lockedZoom = Math.max(1, this.lockedZoom - 1);
    },
    // Scroll-wheel zoom on the INLINE chart card — mirrors the Sales Insights
    // map's feel (multiplicative ×1.15 per notch, TerritoryMap.onWheel).
    // Only active while the image is click-LOCKED: preventDefault is called
    // conditionally inside the handler (not via a .prevent modifier) so the
    // wheel never hijacks normal page scrolling over an unlocked image.
    // Same 1–8 clamp as the −/+ buttons above.
    onLockedWheel(key, e) {
      if (!this.inPlaceZoomLocked[key]) return;
      e.preventDefault();
      var f = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      this.lockedZoom = Math.min(8, Math.max(1, Math.round(this.lockedZoom * f * 100) / 100));
    },

    openImageModal(src) {
      this.modalImage = src;
      this.resetZoom();
      this.showImageModal = true;
      var self = this;
      this.$nextTick(function () { self.measureModalImg(); });
    },
    closeImageModal() {
      this.showImageModal = false;
      this.resetZoom();
    },

    // ===== REPORT INCORRECT CHART (Master-only) =====
    // Double-click on the fullscreen chart → report dialog. Inert while the
    // balloon editor is active (its click-to-add owns the surface) and for
    // non-Masters / unresolvable charts (reportTarget null).
    onModalDblClick() {
      if (this.balloonEditMode) return;
      if (!this.isMasterUser || !this.reportTarget) return;
      this.openReportChartModal();
    },
    openReportChartModal() {
      var t = this.reportTarget;
      if (!t) return;
      // Freeze the viewer's context NOW — the b-modal opens after the
      // fullscreen viewer closes, and page state must not shift under it.
      this.reportChartSnapshot = {
        target: t,
        machineName: this.machineDisplayName || "",
        assemblyName: this.assemblyDisplayName || "",
        subAssemblyName: t.subName || "",
        partFilter: this.partFilter || (this.headerSearch && this.headerSearch.code) || "",
        companyId: Number(this.companyId) || 0,
        companyMachineId: Number(this.machineId) || 0,   // CompanyMachine.Id — the machine dropdown's value space
        pageUrl: window.location.href,
        chartUrl: this.modalImage
      };
      this.reportChartNote = "";
      this.reportChartSending = false;
      this.reportChartSuccess = false;
      this.reportChartError = "";
      // Close the fullscreen viewer FIRST — its overlay would bury the
      // b-modal; the snapshot above already captured everything needed.
      this.closeImageModal();
      this.showReportChartModal = true;
    },
    submitReportChart(bvModalEvent) {
      bvModalEvent.preventDefault();   // stay open to show the success/error banner
      var s = this.reportChartSnapshot;
      if (!s || this.reportChartSending) return;
      this.reportChartSending = true;
      this.reportChartError = "";
      var self = this;
      apiService
        .reportChart({
          entityType: s.target.type,
          entityId: s.target.id,
          companyId: s.companyId,
          companyMachineId: s.companyMachineId,
          machineName: s.machineName,
          assemblyName: s.assemblyName,
          subAssemblyName: s.subAssemblyName,
          partFilter: s.partFilter,
          pageUrl: s.pageUrl,
          chartUrl: s.chartUrl,
          note: (this.reportChartNote || "").trim()
        })
        .then(function () {
          self.reportChartSuccess = true;
          setTimeout(function () { self.showReportChartModal = false; }, 1800);
        })
        .catch(function (err) {
          self.reportChartError =
            (err && err.response && err.response.data && err.response.data.message) ||
            "Something went wrong sending the report. Please try again.";
        })
        .finally(function () {
          self.reportChartSending = false;
        });
    },
    resetReportChart() {
      this.reportChartNote = "";
      this.reportChartSending = false;
      this.reportChartSuccess = false;
      this.reportChartError = "";
      this.reportChartSnapshot = null;
    },

    // ===== PARTS INQUIRY MODAL =====
    // Open the modal AND close the Semantic UI dropdown
    // that contains the trigger button — otherwise the
    // dropdown menu sits open behind the modal backdrop.
    //
    // `context` switches the pre-filled message between
    // "missing machine" and "missing assembly" copy. Both
    // paths submit to the same /Email/parts-inquiry
    // endpoint — the parts team gets one inbox either way.
    // Defaults to "machine" if called from a v-on handler
    // that passes a MouseEvent rather than a string.
    openPartsInquiryModal(context) {
      if (typeof context !== "string") context = "machine";
      try {
        $("#MachineSelect").dropdown("hide");
      } catch (e) {
        // Dropdown not initialized yet — harmless.
      }
      try {
        $("#WearAssemblySelect").dropdown("hide");
      } catch (e) {
        // Same — assembly dropdown may not exist yet.
      }
      // Pre-fill name/email/company from the logged-in
      // user + selected company so the user doesn't retype
      // info we already have. Each field guards against
      // overwriting a value the user may have already
      // entered (e.g. they opened the modal, typed
      // something, closed it, reopened it).
      if (this.currentUser) {
        if (!this.partsInquiry.customerName) {
          var first = this.currentUser.firstName || "";
          var last = this.currentUser.lastName || "";
          this.partsInquiry.customerName = (first + " " + last).trim();
        }
        if (!this.partsInquiry.customerEmail && this.currentUser.email) {
          this.partsInquiry.customerEmail = this.currentUser.email;
        }
      }
      // Company comes from the company selector in Step 1
      // (this.companyName), populated whenever the user
      // picks a company. Falls through silently if no
      // company is selected yet.
      if (!this.partsInquiry.customerCompany && this.companyName) {
        this.partsInquiry.customerCompany = this.companyName;
      }
      // For the assembly path, pre-fill the equipment
      // field with the currently-selected machine's name
      // — saves the user retyping it. We look up the
      // selected machine from the machines array (which
      // already populated when the user picked it in
      // Step 2). Guarded behind the same "don't overwrite
      // existing input" rule as the other fields above.
      if (
        context === "assembly" &&
        !this.partsInquiry.machineDescription &&
        this.machineId
      ) {
        var self = this;
        var selectedMachine = (this.machines || []).find(function (m) {
          return String(m.value) === String(self.machineId);
        });
        if (selectedMachine && selectedMachine.text) {
          this.partsInquiry.machineDescription = selectedMachine.text;
        }
      }
      // Pre-fill the description with a sensible default —
      // most users opening this modal are reporting either
      // a missing machine or a missing assembly, depending
      // on which footer they clicked. The text is editable
      // so they can replace or extend it before sending.
      if (!this.partsInquiry.description) {
        if (context === "assembly") {
          this.partsInquiry.description =
            "I'm missing an assembly on my machine. Please reach " +
            "out to me for more information or to provide me " +
            "with a status update.\n\nThanks!";
        } else {
          this.partsInquiry.description =
            "I'm missing a machine on my account. Please reach " +
            "out to me for more information or to provide me " +
            "with a status update.\n\nThanks!";
        }
      }
      this.showPartsInquiryModal = true;
    },

    onPartsInquiryFileChange(e) {
      var files = e.target.files || (e.dataTransfer && e.dataTransfer.files);
      if (!files || !files.length) {
        this.partsInquiryFile = null;
        return;
      }
      var fileTypes = ["jpg", "jpeg", "png"];
      var ext = files[0].name.split(".").pop().toLowerCase();
      if (fileTypes.indexOf(ext) === -1) {
        this.$refs.partsInquiryFile.value = null;
        this.partsInquiryFile = null;
        this.partsInquiryFileError = "Only JPEG, JPG and PNG files are supported.";
        return;
      }
      if (files[0].size > 1024 * 1024) {
        this.$refs.partsInquiryFile.value = null;
        this.partsInquiryFile = null;
        this.partsInquiryFileError = "Maximum file size of 1 MB.";
        return;
      }
      this.partsInquiryFile = files[0];
      this.partsInquiryFileError = "";
    },

    submitPartsInquiry(bvModalEvent) {
      // Keep the modal open while the request is in flight
      // — the OK button label flips to "Sending…" via the
      // partsInquirySending flag, and the success/error
      // banner takes over the body when the call resolves.
      if (bvModalEvent) bvModalEvent.preventDefault();
      if (!this.partsInquiryFormValid || this.partsInquirySending) return;

      this.partsInquirySending = true;
      this.partsInquiryError = "";

      var fd = new FormData();
      fd.append("CustomerName", this.partsInquiry.customerName.trim());
      fd.append("CustomerEmail", this.partsInquiry.customerEmail.trim());
      if (this.partsInquiry.customerCompany) {
        fd.append("CustomerCompany", this.partsInquiry.customerCompany.trim());
      }
      if (this.partsInquiry.machineDescription) {
        fd.append("MachineDescription", this.partsInquiry.machineDescription.trim());
      }
      fd.append("Description", this.partsInquiry.description.trim());
      if (this.partsInquiryFile) {
        fd.append("InquiryFile", this.partsInquiryFile);
      }

      var self = this;
      apiService.submitPartsInquiry(fd)
        .then(function () {
          self.partsInquirySending = false;
          self.partsInquirySuccess = true;
          // Auto-close the modal after a short delay so the
          // user sees the confirmation but doesn't have to
          // manually dismiss.
          setTimeout(function () {
            self.showPartsInquiryModal = false;
          }, 1800);
        })
        .catch(function (err) {
          self.partsInquirySending = false;
          var msg = "Please try again or call (800) 278-3353.";
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : null;
          if (traceId) {
            msg = "Please try again or call (800) 278-3353. (TraceID: " + traceId + ")";
          }
          self.partsInquiryError = msg;
        });
    },

    resetPartsInquiryForm() {
      // Fired when the modal closes (any path: OK/cancel/X
      // /backdrop). Clears the form so the next open
      // starts clean — except we leave name/email alone so
      // a returning user doesn't have to re-enter them.
      this.partsInquirySending = false;
      this.partsInquirySuccess = false;
      this.partsInquiryError = "";
      this.partsInquiryFile = null;
      this.partsInquiryFileError = "";
      this.partsInquiry.customerCompany = "";
      this.partsInquiry.machineDescription = "";
      this.partsInquiry.description = "";
      if (this.$refs.partsInquiryFile) {
        this.$refs.partsInquiryFile.value = null;
      }
    },
    resetZoom() {
      this.scale = 1;
      this.pointX = 0;
      this.pointY = 0;
    },
    zoomIn() {
      this.scale = Math.min(this.maxScale, this.scale + 0.25);
    },
    zoomOut() {
      this.scale = Math.max(this.minScale, this.scale - 0.25);
      if (this.scale === 1) {
        this.pointX = 0;
        this.pointY = 0;
      }
    },
    handleWheel(event) {
      // Multiplicative ×1.15 per wheel notch — the same feel as the Sales
      // Insights map (TerritoryMap.onWheel). Replaces the old additive ±0.2
      // steps, which felt sluggish at low zoom and jumpy near max.
      var f = event.deltaY < 0 ? 1.15 : 1 / 1.15;
      this.scale = Math.min(
        this.maxScale,
        Math.max(this.minScale, Math.round(this.scale * f * 100) / 100)
      );
      if (this.scale === 1) {
        this.pointX = 0;
        this.pointY = 0;
      }
    },
    startDrag(event) {
      this._dragMoved = false;       // distinguishes a click (add) from a pan
      this._dragStartCX = event.clientX;
      this._dragStartCY = event.clientY;
      if (this.scale <= 1) return;
      this.isDragging = true;
      this.startX = event.clientX - this.pointX;
      this.startY = event.clientY - this.pointY;
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(event) {
      if (!this.isDragging) return;
      // Only count as a pan (which suppresses click-to-add) past a small
      // threshold, so hand jitter during a click still registers as a click.
      if (Math.abs(event.clientX - this._dragStartCX) > 3 ||
          Math.abs(event.clientY - this._dragStartCY) > 3) {
        this._dragMoved = true;
      }
      this.pointX = event.clientX - this.startX;
      this.pointY = event.clientY - this.startY;
    },
    stopDrag() {
      this.isDragging = false;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
    },

    // ===== MODIFY-FLOW HYDRATION =====
    // Reads the `modify*` query params planted by the
    // Modify Order modal on Cart.vue and walks Steps 1-3
    // through their pre-fill sequence:
    //
    //   1. companyId    → set immediately, fires
    //                     getMachinesByCompany so the
    //                     machine dropdown can populate.
    //   2. machineId    → set after a brief delay so the
    //                     dropdown's options exist; the
    //                     machineId watcher then fires
    //                     loadInlineWearAssemblyOptions.
    //   3. assemblyId   → stashed in pendingModifyAssemblyId;
    //                     loadInlineWearAssemblyOptions
    //                     consumes it once options arrive,
    //                     setting selectedWearAssemblyId.
    //   4. subAssemblyId→ stashed in pendingModifySub-
    //                     AssemblyId; the parts loader
    //                     (wear or all-parts) consumes it
    //                     after sub-assemblies populate.
    //
    // PO Number, wear toggle, and the cart's existing
    // staging tray are restored alongside — staging via
    // syncFromActiveCart which already runs in mounted().
    //
    // Query params get stripped from the URL after
    // application via router.replace so a refresh doesn't
    // re-trigger pre-fill against potentially stale state.
    applyModifyQuery() {
      var q = this.$route.query || {};
      if (!q.modifyMachineId && !q.modifyCompanyId) return;

      console.log("[Create] modify hydration: applying", q);

      // Wear toggle — applied first so the right loader
      // (wear vs all-parts) gets used when parts populate.
      if (q.modifyConfiguredWear !== undefined) {
        this.configuredWear = q.modifyConfiguredWear === "1";
      }

      // PO Number — keeps the user adding to the same
      // Order. Both the form field and localStorage so the
      // PO picker modal that opens on each Add starts on
      // this PO.
      if (q.modifyPoNumber) {
        this.poNumber = String(q.modifyPoNumber);
        try { localStorage.poNumber = q.modifyPoNumber; } catch (e) {}
      }

      // Stash assembly + sub-assembly targets — picked up
      // by the loaders down the chain.
      if (q.modifyAssemblyId) {
        this.pendingModifyAssemblyId = String(q.modifyAssemblyId);
      }
      if (q.modifySubAssemblyId) {
        this.pendingModifySubAssemblyId = String(q.modifySubAssemblyId);
      }

      // Company first — synchronous setter, async fetch.
      if (q.modifyCompanyId) {
        try { localStorage.companyId = q.modifyCompanyId; } catch (e) {}
        this.companyId = String(q.modifyCompanyId);
        this.getMachinesByCompany();
        // Also refresh insights so Frequently Ordered shows
        // the right company while the user adds more parts.
        if (typeof this.getInsights === "function") {
          this.getInsights();
        }
      }

      // Machine after a short delay so the dropdown's
      // options exist (getMachinesByCompany resolves async).
      // The machineId watcher then fires
      // loadInlineWearAssemblyOptions, which picks up
      // pendingModifyAssemblyId once options arrive.
      var self = this;
      if (q.modifyMachineId) {
        setTimeout(function () {
          try { localStorage.machineId = q.modifyMachineId; } catch (e) {}
          self.machineId = String(q.modifyMachineId);
          // Force Semantic UI to redraw the dropdown's visible
          // label — see syncMachineDropdownDisplay docstring.
          self.syncMachineDropdownDisplay();
        }, 800);
      }

      // Strip query params so a refresh doesn't re-trigger.
      // Catch is a no-op — vue-router throws on identical
      // navigations and we don't care.
      this.$router.replace({ query: {} }).catch(function () {});
    },

    // ===== AUTO-ADD-TO-CART HAND-OFF =====
    // Triggered from mounted() — runs BEFORE applyModifyQuery
    // so the query string hasn't been wiped yet. Looks for
    // `autoAddToCart=1` plus partId/code on the query (set
    // by the Account dashboard's Top 5 Most Purchased basket,
    // the resolveTopPartContext API's redirectUrl, or any
    // future entry point that wants to drop the user into
    // this page with the Add-to-Cart modal already open).
    //
    // Builds a synthetic part object from the query params —
    // partId + partCode minimum, plus description if it was
    // tunneled through (`autoAddDesc`) so the modal's lede
    // can show the part's full name. Hands off to
    // addToCartClick wrapped in $nextTick, which is the same
    // gate the page's own ADD buttons use, so the auto-add
    // path runs through identical pickerMode logic (add vs
    // edit), default-quantity logic, destination disclosure,
    // and on-confirm staging-queue plumbing.
    //
    // $nextTick gives Vue a frame to commit any reactive
    // state set just above (companyId, selectedParts loaded
    // from storage) before b-modal evaluates v-if/v-show
    // gates inside its template. Without nextTick the modal
    // can fail to render on first paint when this runs
    // immediately after data() is ready but before the first
    // render pass has happened.
    //
    // Strips the auto-add params from the URL after firing
    // so a refresh doesn't re-pop the modal. Uses an
    // explicit shallow-copy + delete pattern (rather than
    // applyModifyQuery's blast-everything approach) so any
    // params we don't own — like the route's `code` /
    // `partId` that the page may consume separately —
    // survive untouched.
    applyAutoAddToCartQuery() {
      var q = this.$route.query || {};
      if (q.autoAddToCart !== "1") return;
      if (!q.partId || !q.code) {
        console.warn(
          "[Create] auto-add: autoAddToCart=1 but partId/code missing",
          q
        );
        return;
      }

      console.log("[Create] auto-add: opening modal with", {
        partId: q.partId,
        partCode: q.code,
        autoAddDesc: q.autoAddDesc,
      });

      // Minimal part shape for addToCartClick. partId and
      // partCode are the only required fields; description
      // is surfaced in the modal's lede if available.
      // partQuantityPerMachine is omitted because the
      // Account dashboard doesn't pass it; defaultQuantityFor
      // falls back to 1, which is the right default for a
      // freshly-added part.
      var part = {
        partId: String(q.partId),
        partCode: String(q.code),
        description: q.autoAddDesc ? String(q.autoAddDesc) : "",
      };

      var self = this;
      this.$nextTick(function () {
        self.addToCartClick(part);
      });

      // Strip the auto-add params from the URL so a refresh
      // doesn't re-trigger the modal. Preserve other params
      // (like modify* — applyModifyQuery, which runs right
      // after us in mounted, needs them).
      var nextQuery = {};
      Object.keys(q).forEach(function (k) {
        if (k !== "autoAddToCart" && k !== "autoAddDesc") {
          nextQuery[k] = q[k];
        }
      });
      this.$router.replace({ query: nextQuery }).catch(function () {});
    },

    // ===== HEADER-SEARCH HYDRATION =====
    // Triggered from mounted() (and from the $route watcher
    // when the user picks another part from the header bar
    // without leaving this page). The floating search bar in
    // Head.vue's selectOption pushes:
    //
    //   /search?code=<partCode>
    //          &machineName=<displayName>
    //          &assembly=<assemblyName>
    //          &allParts=true
    //
    // We mirror that into:
    //   1. headerSearch — display-only state for the banner
    //      at the top of the right column.
    //   2. configuredWear=false when allParts=true. The
    //      selectOption side picks allParts=true to surface
    //      the user's chosen part regardless of wear/non-
    //      wear status; honoring it here keeps the inline
    //      parts list from filtering away the very part the
    //      user just clicked. Skipped if allParts isn't on
    //      the URL — preserves the existing default of
    //      configuredWear=true for normal navigation.
    //   3. pendingHeaderMachineName / pendingHeaderAssembly-
    //      Name — stashed for the loaders to resolve against
    //      their loaded option lists. Same lifecycle as
    //      pendingModify*: the loader nulls them out after
    //      consumption.
    //
    // We do NOT strip the URL after applying. Two reasons:
    //   - The banner reads from headerSearch (not the URL),
    //     so on refresh the banner needs the URL params to
    //     re-hydrate it.
    //   - clearHeaderSearch() is the explicit dismiss path;
    //     it strips the four params surgically (keeping any
    //     other query params intact).
    //
    // ===== PERSISTENCE ACROSS NAVIGATION =====
    // The URL is the primary source of truth — it covers
    // refresh, deep links, and same-page re-search via the
    // $route watcher. But navigating away to /cart and
    // clicking a /search link (rather than browser back)
    // strips the query, so the URL alone doesn't survive
    // in-app navigation.
    //
    // Fallback chain on entry:
    //   1. URL has any of code/machineName/assembly →
    //      URL wins, hydrate from there, mirror to
    //      localStorage.
    //   2. URL is bare but localStorage has stored params →
    //      hydrate from localStorage. URL stays bare on
    //      purpose (we don't want to surprise the user by
    //      mutating /search → /search?code=… without their
    //      action; the banner is the visible signal that a
    //      search is active, and refresh restores from
    //      localStorage just the same).
    //   3. Both empty → no header search; return early.
    //
    // localStorage is cleared by clearHeaderSearch() (banner
    // ×) — that's the single explicit dismiss path. A new
    // header-search arrival overwrites previous storage on
    // step 1's mirror.
    // ===== loadWearPartIds =====
    // Fetches THIS company's wear-eligible PartIds (its
    // dbo.Wears = the Account Setup wear list) and caches
    // them on the component for use by isWearEligible(part).
    // The "2% Discount" chip must reflect the customer's OWN
    // wear parts, not the global wear-eligible catalog — so
    // we use the company-scoped endpoint keyed by
    // localStorage.companyId. Called on mount and again from
    // the companyId watcher when the user switches company.
    //
    // No companyId → empty list (chip simply won't render).
    //
    // Failure handling: silently swallow (empty list). The
    // chip just won't render — same UX as if the part isn't
    // eligible — plus a console warning for diagnosis.
    // Nothing else on the page depends on this list, so a
    // transient API blip (or a pre-deploy 404 on the new
    // endpoint) doesn't block anything else.
    async loadWearPartIds() {
      var companyId = localStorage.companyId || "";
      if (!companyId || !apiService.getCompanyWearPartIds) {
        this.wearPartIds = [];
        return;
      }
      try {
        var data = await apiService.getCompanyWearPartIds(companyId);
        if (Array.isArray(data)) {
          this.wearPartIds = data.map(function (id) {
            return String(id);
          });
        } else {
          this.wearPartIds = [];
          console.warn(
            "[Search] company wear part IDs: expected array, got",
            data
          );
        }
      } catch (e) {
        this.wearPartIds = [];
        console.warn(
          "[Search] company wear part IDs: load failed", e
        );
      }
    },

    // ===== isWearEligible =====
    // True iff `part` is registered in dbo.Wears (active,
    // any company). Used by the template's "2% Discount"
    // chip. Replaces the old `v-if="configuredWear"`
    // gating, which conflated wear-eligibility (per-part)
    // with the wear-only filter toggle (per-view) and so
    // hid the chip whenever the user browsed in all-parts
    // mode.
    //
    // Defensive checks:
    //   - falsy `part` returns false (defensive against a
    //     v-for race where the loop runs once with an
    //     undefined entry during sub-assembly hydration).
    //   - both partId and id checked because the wear /
    //     all-parts loaders shape parts slightly
    //     differently (wear loader emits both `partId`
    //     and `Id`; all-parts loader emits `id`).
    isWearEligible(part) {
      if (!part) return false;
      var pid = String(part.partId || part.id || "");
      if (!pid) return false;
      return !!this.wearPartIdSet[pid];
    },

    applyHeaderSearchQuery() {
      var q = this.$route.query || {};
      // Activity trail: the Search page executing a header search.
      if (q.code || q.description) recordSearch("search-page", q.code || q.description);
      // Top-5 deep-links carry the part code as `pf` so the parts list narrows
      // to that single part (the header search instead shows all + surfaces).
      // Applied straight from the URL; clearing the filter (×) still works since
      // this only re-runs on mount or a code/machine/assembly change.
      if (q.pf) this.partFilter = String(q.pf);
      var hasUrlParams = !!(q.code || q.machineName || q.assembly);
      var source = q;
      var sourceLabel = "URL";

      if (!hasUrlParams) {
        // Try localStorage as the navigation-survival
        // fallback. Wrapped in try/catch because
        // localStorage can throw in private browsing or
        // when quota is hit, and a JSON.parse of malformed
        // data can throw too. Either failure mode just
        // means "no persisted search" — fall through to
        // the early return below.
        var raw = null;
        try { raw = localStorage.headerSearchParams; } catch (e) {}
        if (raw) {
          try {
            var stored = JSON.parse(raw);
            if (stored && (stored.code || stored.machineName || stored.assembly)) {
              source = stored;
              sourceLabel = "localStorage";
            }
          } catch (e) {
            // Malformed — wipe so we don't keep choking on
            // it across loads.
            try { delete localStorage.headerSearchParams; } catch (ee) {}
          }
        }
      }

      if (!source.code && !source.machineName && !source.assembly) return;

      console.log("[Search] header search query (source: " + sourceLabel + "):", {
        code: source.code,
        machineName: source.machineName,
        assembly: source.assembly,
        allParts: source.allParts,
      });

      this.headerSearch = {
        code: source.code ? String(source.code) : "",
        machineName: source.machineName ? String(source.machineName) : "",
        assembly: source.assembly ? String(source.assembly) : "",
        active: true,
      };

      // A new header search is applying — drop any lingering
      // highlight from a prior search so it can't briefly flash
      // on the wrong row before the new part is surfaced. The
      // floater re-arms it once the new part lands.
      this.clearHeaderHighlight();

      // Reset the off-account heads-up too; applyPendingHeaderMachine
      // (called below) re-raises it if THIS search's machine isn't
      // on the account. Clearing first avoids a stale banner from a
      // previous off-account search bleeding into an on-account one.
      this.headerOffAccount = false;
      this.headerOffAccountMachine = "";
      this.headerOffAccountCode = "";

      // allParts=true is the explicit "show every part"
      // intent set by Head.vue when the user picks a code
      // from autocomplete. Without flipping configuredWear
      // off here, the inline parts list would silently hide
      // any non-wear part — including the very part the
      // user clicked.
      if (source.allParts === "true") {
        this.configuredWear = false;
      }

      // Stash names for the loaders. Both consumers null
      // these out after applying.
      if (source.machineName) {
        this.pendingHeaderMachineName = String(source.machineName);
      }
      if (source.assembly) {
        this.pendingHeaderAssemblyName = String(source.assembly);
      }
      // Disambiguating assembly id (Head.vue forwards it for
      // suggestion picks). Coerce to a Number so the downstream
      // String(opt.value) === String(this.pendingHeaderAssemblyId)
      // comparisons are clean; a missing/garbage value collapses
      // to 0 ("resolve by name as before"). `source` is whichever
      // won above (URL or the localStorage JSON), so this covers
      // the cross-page-nav fallback too.
      this.pendingHeaderAssemblyId = Number(source.assemblyId) || 0;

      // Exact deep-link ids from Email Insights (resolved
      // server-side against the customer's ACCOUNT): machineSel
      // is a CompanyMachine.Id in the machine dropdown's own
      // value space; subAssembly/-Id pins the sub-assembly card.
      // All optional — 0/absent falls back to the name cascades.
      this.pendingHeaderMachineId = Number(source.machineSel) || 0;
      this.pendingHeaderSubAssemblyId = Number(source.subAssemblyId) || 0;
      if (source.subAssembly) {
        this.pendingHeaderSubAssemblyName = String(source.subAssembly);
      }

      // ===== MIRROR TO LOCALSTORAGE =====
      // Persist the applied source params so that subsequent
      // navigations across pages (Search → Create Order →
      // Create RFQ via the nav tabs) can re-hydrate from
      // storage even though those nav clicks don't carry URL
      // params. Each destination page calls
      // applyHeaderSearchQuery on mount; when the URL has no
      // params, it falls back to reading this localStorage
      // entry.
      //
      // Stale-state safety: an earlier round of this code
      // tried "delete after apply" (one-shot semantics) to
      // protect against a bad suggestion from one search
      // corrupting the next. That defense is no longer
      // needed because Head.vue's autocomplete now filters
      // suggestions by the current company's machine list
      // (see Head.vue:loadUserMachineNames + the filter step
      // in getSearchedParts) — so storage can only ever
      // contain params for machines the user actually owns.
      // Bad state can't enter the system in the first place.
      //
      // Re-saves on every application — including
      // localStorage-sourced ones — so the storage timestamp
      // implicit in "most recently saved" tracks reality.
      //
      // Wrapped because localStorage can throw under quota /
      // private-browsing conditions; failing to persist is a
      // soft degradation (search still works for this
      // session, just doesn't survive cross-page nav).
      try {
        localStorage.headerSearchParams = JSON.stringify({
          code: source.code || "",
          machineName: source.machineName || "",
          assembly: source.assembly || "",
          // Persist the disambiguating id so cross-page nav
          // (Search → Create Order/RFQ via the tabs, which drop
          // URL params) re-hydrates with the same assembly the
          // user actually picked, not just its (possibly shared)
          // name. Read back via Number(source.assemblyId)||0 in
          // the fallback branch above.
          assemblyId: source.assemblyId || 0,
          // Email Insights deep-link ids — persisted for the same
          // cross-page-nav reason as assemblyId above.
          machineSel: source.machineSel || 0,
          subAssembly: source.subAssembly || "",
          subAssemblyId: source.subAssemblyId || 0,
          allParts: source.allParts || "",
        });
      } catch (e) {}

      // Try the machine application immediately — when this
      // method runs from the $route watcher, machines are
      // already loaded and we can resolve right away. From
      // mount the machines list is usually still in flight,
      // so this no-ops via the _machinesLoaded guard inside
      // applyPendingHeaderMachine, and the
      // getMachinesByCompany hook picks up later.
      this.applyPendingHeaderMachine();
    },

    // Resolve pendingHeaderMachineName → machineId by
    // matching against the loaded machines list. Called
    // from three places (because any of them can be the
    // one that arrives last):
    //   - applyHeaderSearchQuery (mount/$route watcher),
    //     for the case where machines are already loaded.
    //   - getMachinesByCompany / getMachineSelectedItems,
    //     for the (more common) case where the URL params
    //     are read first and the machines arrive later.
    //   - the `machines` watcher, as a safety net for any
    //     code path that swaps the array without going
    //     through one of those two loaders.
    //
    // Idempotent and silent when there's nothing pending —
    // every call site can fire it freely without guarding.
    //
    // Match strategy: cascading from strict to loose, so a
    // perfect match wins but minor formatting drift between
    // the part-suggestion API (source of pendingHeaderMachine-
    // Name) and the machines API (source of machines[].text)
    // doesn't fail the auto-fill. Differences we've actually
    // seen: punctuation ("EXTRUDER: 90ADEX" vs "EXTRUDER
    // 90ADEX"), whitespace runs, casing.
    //
    // Each step logs to console so the user can read in
    // DevTools why a match did or didn't happen — important
    // because this is a silent UX behavior with no on-page
    // error surface when the strings disagree.
    applyPendingHeaderMachine() {
      if (!this.pendingHeaderMachineName && !this.pendingHeaderMachineId) return;
      if (!this._machinesLoaded) {
        console.log(
          "[Search] header search: machines not loaded yet, " +
          "deferring machine hydration for '" +
          (this.pendingHeaderMachineName || ("machineSel " + this.pendingHeaderMachineId)) + "'"
        );
        return;
      }

      var target = String(this.pendingHeaderMachineName || "");
      var machines = this.machines || [];

      // ID-FIRST: ?machineSel= carries the exact CompanyMachine.Id
      // (Email Insights resolves it server-side against the
      // customer's account), which is the machine dropdown's own
      // value space — no name fuzziness possible. On a hit, select
      // and stop; on a miss (stale link, machine since unlinked),
      // fall through to the name cascade below.
      if (this.pendingHeaderMachineId) {
        var wantMachineId = String(this.pendingHeaderMachineId);
        var idMatch = machines.find(function (m) {
          return String(m.value) === wantMachineId;
        });
        this.pendingHeaderMachineId = 0;   // one-shot either way
        if (idMatch) {
          console.log(
            "[Search] header search: matched machine via 'id' " +
            "strategy (machineSel " + wantMachineId + "):",
            { value: idMatch.value, text: idMatch.text || idMatch.name || "" }
          );
          this.machineId = String(idMatch.value);
          try { localStorage.machineId = this.machineId; } catch (e) {}
          this.headerOffAccount = false;
          this.headerOffAccountMachine = "";
          this.headerOffAccountCode = "";
          var selfId = this;
          this.$nextTick(function () {
            selfId.syncMachineDropdownDisplay();
          });
          this.pendingHeaderMachineName = null;
          return;
        }
        console.warn(
          "[Search] header search: machineSel " + wantMachineId +
          " not in this company's machine list — falling back to " +
          "name matching for '" + target + "'"
        );
      }
      if (!target) { this.pendingHeaderMachineName = null; return; }

      // Normalization helpers — declared inline because
      // they're only useful here. `norm` keeps the string
      // mostly recognizable (whitespace + casing only);
      // `normStrong` strips ALL non-alphanumerics so e.g.
      // "EXTRUDER: 90ADEX" and "EXTRUDER 90ADEX" and
      // "extruder-90adex" all collapse to "extruder90adex".
      var norm = function (s) {
        return String(s == null ? "" : s)
          .trim().toLowerCase().replace(/\s+/g, " ");
      };
      var normStrong = function (s) {
        return String(s == null ? "" : s)
          .toLowerCase().replace(/[^a-z0-9]/g, "");
      };

      var nTarget = norm(target);
      var nTargetStrong = normStrong(target);

      console.log("[Search] header search: resolving machine", {
        target: target,
        normalized: nTarget,
        machineCount: machines.length,
        sample: machines.slice(0, 8).map(function (m) {
          // Surface every plausible label field at once so
          // the console log shows whether the API actually
          // returns `text`, `name`, `machineName`, or some
          // other key we haven't accounted for.
          return {
            value: m.value,
            text: m.text,
            name: m.name,
            machineName: m.machineName,
          };
        }),
      });

      // Cascading match. Each strategy looks at a different
      // shape of the data; we stop at the first hit and
      // record which one fired. textCandidate() pulls the
      // first non-empty label-ish field from a row so
      // strategies don't have to know which key the API
      // chose.
      var textCandidate = function (m) {
        return m.text || m.name || m.machineName || "";
      };

      var match = null;
      var strategy = null;

      // 1. Exact text match (case-insensitive, whitespace-
      //    collapsed). Should fire on a clean match.
      match = machines.find(function (m) {
        return norm(textCandidate(m)) === nTarget;
      });
      if (match) strategy = "exact";

      // 2. Strong-normalized match — strips all punctuation
      //    and whitespace. Catches "EXTRUDER: 90ADEX" vs
      //    "EXTRUDER 90ADEX" type drift.
      if (!match) {
        match = machines.find(function (m) {
          return normStrong(textCandidate(m)) === nTargetStrong;
        });
        if (match) strategy = "alphanumeric";
      }

      // 3. Substring match either direction. Last resort
      //    before giving up — handles cases where one side
      //    has extra context (e.g. "EXTRUDER 90ADEX -
      //    BRICK PLANT" vs "EXTRUDER: 90ADEX").
      if (!match) {
        match = machines.find(function (m) {
          var nText = norm(textCandidate(m));
          if (!nText || !nTarget) return false;
          return nText.indexOf(nTarget) >= 0
              || nTarget.indexOf(nText) >= 0;
        });
        if (match) strategy = "substring";
      }

      if (match) {
        console.log(
          "[Search] header search: matched machine via '" +
          strategy + "' strategy:",
          {
            value: match.value,
            text: textCandidate(match),
          }
        );
        this.machineId = String(match.value);
        try { localStorage.machineId = this.machineId; } catch (e) {}
        // Machine IS on the account — make sure any prior
        // off-account heads-up is cleared.
        this.headerOffAccount = false;
        this.headerOffAccountMachine = "";
        this.headerOffAccountCode = "";
        var self = this;
        this.$nextTick(function () {
          self.syncMachineDropdownDisplay();
        });
      } else {
        // Surface every machine text in the console so the
        // user can spot what the actual labels look like
        // and we can adjust the match (or fix the upstream
        // data) if needed.
        console.warn(
          "[Search] header search: NO MATCH for machine '" +
          target + "' after exact/alphanumeric/substring " +
          "strategies. Available machine labels:",
          machines.map(function (m) {
            return textCandidate(m);
          })
        );
        // OFF-ACCOUNT HEADS-UP. We only reach here after machines
        // are loaded (the _machinesLoaded guard up top returns
        // early otherwise), so a no-match genuinely means the
        // part's machine isn't set up on this company's account —
        // not a mid-load false negative. Only raise it for an
        // active header search (the user actually picked a part);
        // never disable ordering. The banner is dismissible and
        // gets reset on the next search / successful match /
        // clearHeaderSearch.
        if (this.headerSearch && this.headerSearch.code) {
          this.headerOffAccount = true;
          this.headerOffAccountMachine = target;
          this.headerOffAccountCode = String(this.headerSearch.code);
          console.log(
            "[Search] header search: machine '" + target +
            "' is not on company " + this.companyId +
            "'s account — showing non-blocking off-account banner " +
            "for part '" + this.headerOffAccountCode + "'."
          );
        }
      }
      this.pendingHeaderMachineName = null;
    },

    // Dismiss the banner, clear the form's hydrated machine /
    // assembly state, AND notify Head.vue to clear its search
    // input. Per-user-request: the × should fully reset the
    // header search context, not just hide the banner.
    //
    // What gets cleared:
    //   - this.headerSearch state object (banner hide)
    //   - pending hydration vars
    //   - localStorage.headerSearchParams (cross-page persist)
    //   - localStorage.searchTerm + .description (Head.vue's
    //     own persistence keys for its input field)
    //   - machineId / selectedWearAssemblyId /
    //     selectedSubAssemblyId (cascade-clears Find Your
    //     Machine + Choose an Assembly + Sub-Assembly via
    //     existing watchers)
    //   - Emits $headerSearchBus 'cleared' so Head.vue
    //     resets its searchTerm input field LIVE — without
    //     this, the input keeps showing the dismissed term
    //     until the next page load.
    //
    // Company is left intact intentionally — Step 1 is a
    // user-level setting that shouldn't be perturbed by a
    // search dismiss.
    clearHeaderSearch() {
      this.headerSearch = {
        code: "",
        machineName: "",
        assembly: "",
        active: false,
      };
      this.pendingHeaderMachineName = null;
      this.pendingHeaderMachineId = 0;
      this.pendingHeaderAssemblyName = null;
      this.pendingHeaderAssemblyId = 0;
      this.pendingHeaderSubAssemblyName = null;
      this.pendingHeaderSubAssemblyId = 0;
      this.clearHeaderHighlight();
      this.headerOffAccount = false;
      this.headerOffAccountMachine = "";
      this.headerOffAccountCode = "";

      // Wipe persisted copies so navigating away and back
      // doesn't re-hydrate the dismissed search.
      try { delete localStorage.headerSearchParams; } catch (e) {}
      try { delete localStorage.searchTerm; } catch (e) {}
      try { delete localStorage.description; } catch (e) {}

      // Cross-page machine/assembly persistence cleanup.
      // Head.vue's selectCompany manages four localStorage keys
      // that persist the user's selected machine + assembly
      // across page navigation (machineId / machineIdRFQ for
      // Order vs RFQ, assemblyId / assemblyIdRFQ likewise).
      // Without wiping these, clicking × on the Search page
      // and then navigating to /orders/create or /rfq/create
      // would re-populate those pages with the dismissed
      // machine — exactly the bug reported. Clearing all four
      // ensures the dismiss is fully effective cross-page.
      //
      // Cart-related keys (cartId, cartIdRFQ, poNumber, etc.)
      // are intentionally NOT cleared here — those represent
      // the user's actual cart state, not search context.
      try { delete localStorage.machineId; } catch (e) {}
      try { delete localStorage.machineIdRFQ; } catch (e) {}
      try { delete localStorage.assemblyId; } catch (e) {}
      try { delete localStorage.assemblyIdRFQ; } catch (e) {}

      // Cascade-clear the form's hydrated state. Setting
      // machineId to null fires the existing machineId watcher
      // which clears the assembly options + parts list
      // downstream. Explicitly nulling the assembly + sub-
      // assembly selections too in case the watcher debouncing
      // leaves residual state.
      this.machineId = null;
      this.selectedWearAssemblyId = null;
      this.selectedSubAssemblyId = null;

      // Force the Semantic UI dropdowns to update their visible
      // labels back to placeholder text ('Select your machine',
      // 'Select an assembly'). SUI manages its display state
      // independently of the hidden <input v-model="machineId">,
      // so the Vue data nullification above doesn't refresh
      // the visible chip — without these explicit dropdown
      // clear() calls, the user sees 'EXTRUDER: 90ADEX...'
      // hanging around even though machineId is null underneath.
      // (Mirrors $("#WearAssemblySelect").dropdown("clear")
      // pattern used elsewhere in this file's reset paths.)
      // Wrapped because jQuery may not be available in fast-
      // nav edge cases — silent failure beats a crash here.
      try { $("#MachineSelect").dropdown("clear"); } catch (e) {}
      try { $("#WearAssemblySelect").dropdown("clear"); } catch (e) {}

      // Notify Head.vue (across components) to clear its own
      // searchTerm input field. Lazy-init the bus the same way
      // mounted does for $cartBus. Head.vue's mounted hook
      // subscribes to this event; see handleHeaderSearchCleared.
      Vue.prototype.$headerSearchBus =
        Vue.prototype.$headerSearchBus || new Vue();
      Vue.prototype.$headerSearchBus.$emit('cleared');

      var q = this.$route.query || {};
      var nextQuery = {};
      Object.keys(q).forEach(function (k) {
        if (k !== "code" && k !== "machineName"
            && k !== "assembly" && k !== "assemblyId"
            && k !== "allParts") {
          nextQuery[k] = q[k];
        }
      });
      this.$router.replace({ query: nextQuery })
        .catch(function () {});
    },

    // ===== HEADER-SEARCH DEEP SEARCH =====
    // Triggered from loadInlineWearAssemblyOptions when the
    // top-level match misses. The suggestion API returns
    // the sub-assembly name (e.g. "28PC WEDGE GROOVE LINER")
    // which lives one level below wearAssemblyOptions
    // (e.g. "HCV AUGERS AND LINERS"). To resolve, fan out
    // parallel calls to the parts API for each top-level
    // assembly — first one whose sub-list contains a name
    // match wins.
    //
    // Picks the right endpoint based on configuredWear:
    //   - all-parts (configuredWear=false) — the path the
    //     header search lands in by default since
    //     applyHeaderSearchQuery sets configuredWear=false
    //     when allParts=true is on the URL. Uses
    //     getAssemblyWithParts; sub-assembly names are at
    //     row.text || row.assemblyName || row.name.
    //   - wear-only (configuredWear=true) — uncommon but
    //     supported for completeness. Uses
    //     getAssembliesWithWearParts; sub-assembly names
    //     at row.assemblyName.
    //
    // First success sets selectedWearAssemblyId to the
    // matching parent. The selectedWearAssemblyId watcher
    // then triggers the regular parts-loader, which sees
    // pendingHeaderSubAssemblyName still on the queue and
    // selects the sub-assembly via its hydration block.
    // The duplicate fetch (deep search fetched the parent
    // already; loader fetches it again) is intentional —
    // letting the loader own the wearAssemblies state keeps
    // sub-assembly selection out of this method's
    // responsibility and avoids racing the loader's
    // wearLoadKey guard.
    //
    // The wearLoadKey is captured at start so a stale deep-
    // search response (user changed machine mid-flight)
    // doesn't override their newer selection.
    applyHeaderAssemblyDeepSearch() {
      if (!this.pendingHeaderSubAssemblyName) return;
      if (this.selectedWearAssemblyId) {
        // User picked something while the search was in
        // flight — don't overwrite.
        console.log(
          "[Search] header search: deep search aborted, " +
          "user selected an assembly first"
        );
        this.pendingHeaderSubAssemblyName = null;
        return;
      }
      var opts = this.wearAssemblyOptions || [];
      if (opts.length === 0) {
        console.warn(
          "[Search] header search: deep search aborted, " +
          "no top-level assemblies available"
        );
        this.pendingHeaderSubAssemblyName = null;
        return;
      }

      var self = this;
      // ===== STALE-CHECK STRATEGY =====
      // Originally this captured `wearLoadKey`, but that
      // value gets bumped by mounted()'s 600ms setTimeout
      // when it calls loadInlineWearAssemblyOptions a
      // second time as part of its bootstrap chain. That
      // second call is harmless to the assembly options
      // themselves (same machine, same data), but it
      // invalidated every deep-search response that landed
      // after T=600ms — making the entire fan-out silently
      // fail when the API was fast enough to put deep-
      // search start before the 600ms boundary (which is
      // exactly the localStorage-restore path's typical
      // timing because nothing else is competing for the
      // network).
      //
      // Capturing machineId instead means we ONLY abort
      // when the user genuinely switches machines (the
      // case we actually care about), not when the same
      // machine's bootstrap chatter fires a redundant
      // assembly-options reload. machineId only changes
      // when applyPendingHeaderMachine, the dropdown's
      // change handler, or a localStorage restore moves
      // it — none of which fire from the bootstrap
      // setTimeout.
      var startMachineId = String(this.machineId || "");
      var target = String(this.pendingHeaderSubAssemblyName);

      // Local normalization helpers. Same shape as the
      // top-level matcher above but inlined since closure-
      // capture is the cleanest way to hand them to the
      // promise chain.
      var norm = function (s) {
        return String(s == null ? "" : s)
          .trim().toLowerCase().replace(/\s+/g, " ");
      };
      var normStrong = function (s) {
        return String(s == null ? "" : s)
          .toLowerCase().replace(/[^a-z0-9]/g, "");
      };
      var nT = norm(target);
      var nTS = normStrong(target);

      // The disambiguating sub-assembly id (captured locally so
      // the async fan-out below can't observe it being nulled by
      // another path mid-flight). The suggestion's assemblyId IS
      // the leaf sub-assembly's id, which is exactly what each
      // fetchSubs row exposes as `id` — so an id hit pinpoints
      // the ONE correct parent even when several parents contain
      // a same-NAMED sub-assembly (the bug). "" = no id supplied
      // → pure name behavior (unchanged).
      var wantSubId = this.pendingHeaderAssemblyId
        ? String(this.pendingHeaderAssemblyId) : "";

      console.log(
        "[Search] header search: deep search starting for " +
        "sub-assembly '" + target + "' (wantSubId=" +
        (wantSubId || "<none>") + ") across " +
        opts.length + " top-level assemblies, " +
        "configuredWear=" + this.configuredWear +
        ", startMachineId=" + startMachineId
      );

      // Pick the API based on configuredWear, with a
      // shape-normalizing extractor that pulls a list of
      // sub-assembly objects with at least { id, name }
      // from whatever the API returned.
      var fetchSubs;
      if (this.configuredWear) {
        fetchSubs = function (parentValue) {
          return apiService.getAssembliesWithWearParts({
            assemblyId: parentValue,
            companyMachineId: self.machineId,
          }).then(function (data) {
            var rows = Array.isArray(data) ? data : [];
            return rows.map(function (sa) {
              return {
                id: sa.assemblyId,
                name: sa.assemblyName || "",
              };
            });
          });
        };
      } else {
        fetchSubs = function (parentValue) {
          return apiService.getAssemblyWithParts({
            id: parentValue,
          }).then(function (data) {
            var rows = Array.isArray(data) ? data : [];
            return rows.map(function (sa, idx) {
              return {
                id: sa.assemblyId || sa.id || sa.value
                    || ("subassembly-" + idx),
                name: sa.assemblyName || sa.text
                    || sa.name || "",
              };
            });
          });
        };
      }

      // Issue all parent calls in parallel. We use a
      // hand-rolled "first match wins" since Promise.race
      // would resolve on the first response regardless of
      // whether it had a hit. We resolve as soon as any
      // promise's result contains the target sub-assembly,
      // and ignore later resolutions.
      var settled = false;
      var pending = opts.length;
      // When an id is supplied we hold off on committing a mere
      // NAME match (a different parent might still come back with
      // the exact-id sub, and the fan-out order is nondeterministic
      // — committing the first name match would re-introduce the
      // ambiguity bug). We remember the first name-only parent here
      // and only fall back to it once every parent has reported in
      // with no id hit. With no id supplied, name matches commit
      // immediately as before.
      var nameFallback = null;
      var commitParent = function (parent, sub, via) {
        settled = true;
        console.log(
          "[Search] header search: deep search HIT (" + via +
          ") — parent '" + parent.text + "' (id " +
          parent.value + ") contains sub-assembly '" +
          (sub ? sub.name : "?") + "' (id " +
          (sub ? sub.id : "?") + ")"
        );
        // Set the parent. The selectedWearAssemblyId watcher
        // fires loadInlinePartsForSelectedAssembly, which fetches
        // sub-assemblies + parts. Its hydration block consumes
        // pendingHeaderSubAssemblyName (left on the queue) and
        // selects the sub.
        self.selectedWearAssemblyId = parent.value;
        self.$nextTick(function () {
          try {
            $("#WearAssemblySelect").dropdown(
              "set selected", parent.value
            );
          } catch (e) {}
        });
      };
      opts.forEach(function (parent) {
        fetchSubs(parent.value).then(function (subs) {
          if (settled) return;
          // Stale-response guard. machineId changes only
          // on user action (machine switch) or another
          // applyPendingHeaderMachine pass. Internal
          // bootstrap reloads keep it stable, so this
          // doesn't false-positive the way wearLoadKey
          // used to.
          if (String(self.machineId || "") !== startMachineId) {
            console.log(
              "[Search] header search: deep search response " +
              "for parent '" + parent.text + "' dropped — " +
              "machineId changed from '" + startMachineId +
              "' to '" + self.machineId + "' (user switched " +
              "machines mid-search)"
            );
            settled = true;
            return;
          }
          // ID-FIRST. If the suggestion carried a sub-assembly
          // id and this parent contains it, that's the exact,
          // unambiguous parent — commit immediately and stop.
          var idHit = wantSubId
            ? subs.find(function (s) { return String(s.id) === wantSubId; })
            : null;
          // Name match across the same three strategies as before.
          var nameHit = subs.find(function (s) {
            return norm(s.name) === nT;
          }) || subs.find(function (s) {
            return normStrong(s.name) === nTS;
          }) || subs.find(function (s) {
            var n = norm(s.name);
            return n && nT && (
              n.indexOf(nT) >= 0 || nT.indexOf(n) >= 0
            );
          });
          pending--;
          if (idHit) {
            commitParent(parent, idHit, "id");
          } else if (nameHit && !wantSubId) {
            // No id was supplied — preserve the original
            // first-name-match-wins behavior exactly.
            commitParent(parent, nameHit, "name");
          } else if (nameHit && wantSubId && !nameFallback) {
            // An id WAS supplied but this parent only matched by
            // name. Stash as a fallback (first one wins) and keep
            // waiting — a later parent may still hold the exact id.
            nameFallback = { parent: parent, sub: nameHit };
            if (pending === 0) {
              commitParent(nameFallback.parent, nameFallback.sub,
                "name-fallback (no id match found)");
            }
          } else if (pending === 0) {
            // Every parent has reported back. If we stashed a
            // name fallback (id wasn't found anywhere), use it so
            // an absent/mismatched id degrades to the proven name
            // behavior rather than failing outright. Otherwise
            // give up cleanly — leave selections untouched.
            if (nameFallback) {
              commitParent(nameFallback.parent, nameFallback.sub,
                "name-fallback (no id match found)");
            } else {
              settled = true;
              console.warn(
                "[Search] header search: deep search " +
                "exhausted, no parent contains a sub-" +
                "assembly matching '" + target + "'" +
                (wantSubId ? " (id " + wantSubId + ")" : "")
              );
              self.pendingHeaderSubAssemblyName = null;
            }
          }
        }).catch(function (e) {
          pending--;
          console.warn(
            "[Search] header search: deep search fetch " +
            "failed for parent '" + parent.text + "':", e
          );
          if (pending === 0 && !settled) {
            settled = true;
            self.pendingHeaderSubAssemblyName = null;
          }
        });
      });
    },

    // ===== HEADER-SEARCH PART SURFACING =====
    // Once the parts data has loaded for the selected
    // assembly, find the specific part code the user
    // clicked from the header search and pull it to the
    // top of its sub-assembly's parts array. Without this,
    // the searched-for part can land mid-list (parts are
    // ordered by part-sequence on the API side) which
    // visually buries the very thing the user navigated
    // here to act on.
    //
    // Operates on the local list BEFORE assignment to
    // wearAssemblies so the splice/unshift happens off the
    // reactive graph, and the eventual assignment to
    // wearAssemblies triggers exactly one reactive update
    // showing the final ordering.
    //
    // Also auto-expands the sub-assembly card containing
    // the matched part — the existing pendingHeaderSub-
    // AssemblyName block already does this when the deep
    // search route fired, but this covers the path where
    // the top-level matched directly (no deep search) and
    // we still want the floated part visible without an
    // extra click. $set is used so the auto-expand happens
    // on the final reactive expandedWearAssemblies map
    // after this method returns; the loaders assign that
    // map in the same window so a direct mutation here
    // would just get clobbered.
    //
    // No-op when:
    //   - no headerSearch.code on state (banner not active)
    //   - list is empty
    //   - the part isn't in any sub-assembly's parts list
    //     (logged so we can spot data drift between
    //     suggestion API's `code` and the parts API's
    //     `code`/`partCode` fields)
    _floatHeaderSearchPartToTop(list) {
      if (!this.headerSearch || !this.headerSearch.code) return;
      if (!Array.isArray(list) || list.length === 0) return;

      var target = String(this.headerSearch.code);
      var norm = function (s) {
        return String(s == null ? "" : s).trim().toLowerCase();
      };
      var normStrong = function (s) {
        return String(s == null ? "" : s)
          .toLowerCase().replace(/[^a-z0-9]/g, "");
      };
      var nT = norm(target);
      var nTS = normStrong(target);

      // The parts API uses `code` in the wear-loader path
      // and `code`/`partCode`/`partNumber` interchangeably
      // in the all-parts path depending on which downstream
      // service the response went through. Probe all three
      // so this doesn't quietly miss when one's the empty
      // string.
      var partCodeOf = function (p) {
        return p.code || p.partCode || p.partNumber || "";
      };

      var foundInAssemblyId = null;
      var foundInAssemblyName = null;
      var foundCode = null;

      list.forEach(function (sa) {
        if (!sa || !Array.isArray(sa.parts)) return;
        if (sa.parts.length === 0) return;

        // Cascading match. We don't fall through to a
        // substring strategy on parts because part codes
        // are tightly formatted; a substring hit is
        // probably wrong (e.g. "NDH S 2012" would
        // partial-match "NDH S 20121" too). Exact and
        // alphanumeric are enough to absorb real-world
        // formatting drift without false positives.
        var idx = sa.parts.findIndex(function (p) {
          return norm(partCodeOf(p)) === nT;
        });
        if (idx === -1) {
          idx = sa.parts.findIndex(function (p) {
            return normStrong(partCodeOf(p)) === nTS;
          });
        }

        if (idx > 0) {
          // Mutate in place — at this stage `list` is the
          // local pre-assignment array, so this doesn't
          // poke Vue's reactivity. The eventual
          // wearAssemblies = list assignment is the
          // single reactive trigger.
          var moved = sa.parts.splice(idx, 1)[0];
          sa.parts.unshift(moved);
          foundInAssemblyId = sa.assemblyId;
          foundInAssemblyName = sa.assemblyName;
          foundCode = partCodeOf(moved);
        } else if (idx === 0) {
          // Already at the top — nothing to move, but
          // still record the location for the auto-expand
          // step below.
          foundInAssemblyId = sa.assemblyId;
          foundInAssemblyName = sa.assemblyName;
          foundCode = partCodeOf(sa.parts[0]);
        }
      });

      if (foundInAssemblyId != null) {
        console.log(
          "[Search] header search: floated part '" +
          foundCode + "' to top of sub-assembly '" +
          foundInAssemblyName + "' (id " + foundInAssemblyId + ")"
        );
        // Stash for the caller to apply after assigning
        // wearAssemblies — both loaders assign
        // expandedWearAssemblies right after the
        // wearAssemblies assignment, and we want our
        // expand to ride on that same reactive update,
        // not get overwritten by it.
        this._pendingHeaderExpandAssemblyId = foundInAssemblyId;
        // Flag the surfaced part for the transient highlight.
        // Store the matched code (lower-cased so the template's
        // case-insensitive compare is trivial). Auto-clear after
        // a few seconds so the page settles back to normal once
        // the user has spotted it.
        this.setHeaderHighlight(foundCode);
      } else {
        console.log(
          "[Search] header search: part '" + target +
          "' not found in any loaded sub-assembly's parts " +
          "list. This is fine if the user expanded a " +
          "different assembly than the one their part lives " +
          "in. Available codes by sub-assembly:",
          list.map(function (sa) {
            return {
              subAssembly: sa.assemblyName,
              codes: (sa.parts || [])
                .map(function (p) { return partCodeOf(p); })
            };
          })
        );
      }
    },

    // ===== HEADER-SEARCH HIGHLIGHT HELPERS =====
    // setHeaderHighlight records the surfaced part's code and
    // arms a ~4s timer to clear it. Re-arming cancels any prior
    // timer so a rapid second search doesn't leave a stale
    // highlight lingering on the wrong row. Wrapped in a method
    // (rather than inlined) so the off-account banner reset path
    // and clearHeaderSearch can also clear it via
    // clearHeaderHighlight without duplicating timer bookkeeping.
    setHeaderHighlight(code) {
      this.headerHighlightCode = String(code == null ? "" : code)
        .trim().toLowerCase();
      // PERSIST the highlight — no auto-clear timer. The surfaced/selected part
      // (from a header search OR a clicked diagram balloon → revealItem) stays
      // marked until a DIFFERENT part is surfaced (this method re-called) or the
      // search is cleared (clearHeaderHighlight). Was a 4s transient; the user
      // wants the found part to stay highlighted in the results.
      if (this._headerHighlightTimer) {
        clearTimeout(this._headerHighlightTimer);
        this._headerHighlightTimer = null;
      }
    },
    clearHeaderHighlight() {
      this.headerHighlightCode = "";
      if (this._headerHighlightTimer) {
        clearTimeout(this._headerHighlightTimer);
        this._headerHighlightTimer = null;
      }
    },
    // Template predicate: is THIS part the one the header search
    // surfaced? Case-insensitive compare against the stashed code,
    // probing the same code fields the floater used so the match
    // is consistent. Returns false when no highlight is active.
    isHeaderHighlightPart(part) {
      if (!this.headerHighlightCode || !part) return false;
      var code = part.code || part.partCode || part.partNumber || "";
      return String(code).trim().toLowerCase() === this.headerHighlightCode;
    },

    onMachineChange(event) {
      this.machineId = event.target.value;
    },
    // Mirrors onMachineChange — fires from the hidden
    // input inside the Semantic UI dropdown when the user
    // picks an assembly from the menu. An empty string
    // (Semantic UI's representation of "no selection")
    // gets normalized to null so downstream watchers and
    // truthiness checks behave the same as they did when
    // this was a native <select> bound to v-model="null".
    onWearAssemblyChange(event) {
      var v = event.target.value;
      this.selectedWearAssemblyId = v === "" ? null : v;
    },
    onAssemblyChange(event) {
      this.assemblyId = event.target.value;
    },
    onCompanyChange(event) {
      this.companyId = event.target.value;
      // Update display name + insights to follow the new company.
      var match = (this.companies || []).find(function (c) {
        return String(c.value) === String(event.target.value);
      });
      this.companyName = match ? match.text : "";
      this.getMachinesByCompany();
      this.getInsights();
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(" ", {
          title: "DELETE ALL ITEMS IN CART",
          id: "deleteModal",
          size: "md",
          buttonSize: "sm",
          okVariant: "dark",
          okTitle: "Yes, please clear cart",
          cancelVariant: "outline",
          cancelTitle: "No, please don’t",
          hideHeaderClose: true,
          centered: true,
        })
        .then((clear) => {
          if (clear && localStorage.cartId) {
            apiService
              .deleteCart({
                id: localStorage.cartId,
              })
              .then((request) => {
                this.deleteCartSuccessful(request);
                this.disabled = false;
              })
              .catch();
          }
        })
        .catch();
    },
    deleteCartSuccessful() {
      // See RFQSavedItems.vue's deleteCartSuccessful for
      // the full rationale: companyId is shared user-state,
      // not cart-state, and clearing it as a side-effect
      // of a cart action makes the user appear logged out
      // (greeting disappears, navbar resets, create-page
      // guards bounce them). Cart deletion shouldn't touch
      // it.
      localStorage.cartId = 0;

      delete localStorage.poNumber;
      delete localStorage.machineId;
      delete localStorage.assemblyId;

      // Notify the global cart bus so the header badge
      // refreshes after an order-cart wipe. Same pattern
      // as Cart.vue's deleteCartSuccessful and the
      // matching RFQ-side handlers.
      if (this.$cartBus) {
        this.$cartBus.$emit("cart-updated");
      }

      this.$router.push({
        name: "Search For Part",
      });
    },

    // ========================================================================
    // QUOTE-SIDE PARALLEL METHODS
    // ========================================================================
    // These mirror the order-side helpers (saveSelectionsToStorage,
    // loadSelectionsFromStorage, syncFromActiveCart, validateLinkedCart,
    // scheduleSyncToCart, syncQueueToCart, proceedWithSelectedInPlace,
    // proceedWithSelected, buildCommittedSnapshot) for the quote queue.
    // Differences from the order-side equivalents:
    //   - Read/write localStorage.cartIdRFQ (not cartId)
    //   - Storage key prefix "jcsteele.insights.selections.rfq."
    //   - saveCart payload sets CreateQuote: true
    //   - Filter syncFromActiveQuoteCart to discriminator === "Rfq"
    //   - Operate on this.selectedPartsQuote / committedSnapshotQuote
    // Order-side methods are intentionally untouched — fewer regression
    // surfaces in exchange for some duplication.
    // ========================================================================

    _selectionsQuoteStorageKey(companyId) {
      var cid = companyId || this.companyId;
      if (!cid) return null;
      return "jcsteele.insights.selections.rfq." + cid;
    },

    saveSelectionsToStorageQuote(forCompanyId) {
      try {
        var key = this._selectionsQuoteStorageKey(forCompanyId);
        if (!key) return;

        if (!this.selectedPartsQuote || this.selectedPartsQuote.length === 0) {
          localStorage.removeItem(key);
          return;
        }
        var payload = {
          companyId: forCompanyId || this.companyId,
          cartId: localStorage.cartIdRFQ
            ? String(localStorage.cartIdRFQ)
            : null,
          committedSnapshot: this.committedSnapshotQuote,
          selectedParts: this.selectedPartsQuote.map(function (p) {
            return {
              partId: p.partId,
              partCode: p.partCode,
              description: p.description,
              poNumber: p.poNumber,
              quantity: p.quantity,
            };
          }),
          savedAt: Date.now(),
        };
        localStorage.setItem(key, JSON.stringify(payload));
      } catch (e) {
        console.warn("[Insights/Quote] Could not persist selections", e);
      }
    },

    loadSelectionsFromStorageQuote() {
      try {
        var key = this._selectionsQuoteStorageKey();
        if (!key) return;

        // A successful quote add ALWAYS creates the server quote cart and stamps
        // localStorage.cartIdRFQ (see proceedWithSelectedQuote → saveCart). So a quote
        // staging tray can never legitimately exist without an active quote cart — if
        // there's no cartIdRFQ, any saved tray is orphaned/stale (the quote was placed
        // or cleared elsewhere). Drop it synchronously here, BEFORE the Add-to-Cart
        // modal can open from this tray (mounted() opens the modal off the restored
        // tray before the async cart sync/validate run), so it never falsely reports a
        // part as already "in your quote." The order side self-corrects because its
        // active cart re-syncs the tray; the quote side had no cart to correct it.
        var activeQuoteCart =
          localStorage.cartIdRFQ && String(localStorage.cartIdRFQ) !== "0";
        if (!activeQuoteCart) {
          this.selectedPartsQuote = [];
          this.committedSnapshotQuote = null;
          localStorage.removeItem(key);
          return;
        }

        var raw = localStorage.getItem(key);
        if (!raw) {
          this.selectedPartsQuote = [];
          this.committedSnapshotQuote = null;
          return;
        }
        var saved = JSON.parse(raw);
        if (!saved || !Array.isArray(saved.selectedParts)) {
          this.selectedPartsQuote = [];
          this.committedSnapshotQuote = null;
          return;
        }

        // Cart-linkage check: if this queue was tagged with a
        // cartIdRFQ at save time, only restore if that same cart
        // is still active.
        if (saved.cartId) {
          var currentCartId = localStorage.cartIdRFQ
            ? String(localStorage.cartIdRFQ)
            : null;
          if (
            !currentCartId ||
            String(saved.cartId) !== currentCartId
          ) {
            localStorage.removeItem(key);
            this.selectedPartsQuote = [];
            this.committedSnapshotQuote = null;
            return;
          }
        }

        this.selectedPartsQuote = saved.selectedParts;
        this.committedSnapshotQuote = saved.committedSnapshot || null;
      } catch (e) {
        console.warn("[Insights/Quote] Could not restore selections", e);
      }
    },

    clearSelectionsStorageQuote() {
      try {
        var key = this._selectionsQuoteStorageKey();
        if (!key) return;
        localStorage.removeItem(key);
      } catch (e) {
        // No-op
      }
    },

    // ===== SYNC FROM ACTIVE QUOTE CART =====
    // Fetches the active quote cart (localStorage.cartIdRFQ) and
    // rebuilds selectedPartsQuote from its server-side contents.
    // Filters to Rfq-discriminator carts only — if cartIdRFQ
    // somehow points to a Cart row (cross-discriminator pollution),
    // we don't want to ingest it here.
    async syncFromActiveQuoteCart() {
      if (!localStorage.cartIdRFQ || String(localStorage.cartIdRFQ) === "0") {
        return;
      }
      if (!this.companyId) return;

      try {
        var cart = await apiService.getCart({ id: localStorage.cartIdRFQ });
        if (!cart) return;

        var isQuote =
          cart.discriminator === "Rfq" ||
          cart.Discriminator === "Rfq" ||
          cart.discriminator === 1 ||
          cart.Discriminator === 1;
        if (!isQuote) return;

        var pos = cart.purchaseOrders || [];
        if (pos.length === 0) return;

        var derived = [];
        pos.forEach(function (po) {
          (po.parts || []).forEach(function (p) {
            var partInfo = p.part || {};
            derived.push({
              partId: p.id,
              partCode: partInfo.code || "",
              description: partInfo.description || "",
              poNumber: po.number || "",
              quantity: p.quantity || 1,
            });
          });
        });

        if (derived.length === 0) return;

        this.selectedPartsQuote = derived;
        this.committedSnapshotQuote = this.buildCommittedSnapshotQuote();
        this.saveSelectionsToStorageQuote();
      } catch (e) {
        console.warn(
          "[Search/Quote] cart-derivation on mount failed:",
          e
        );
      }
    },

    async validateLinkedQuoteCart() {
      // Validate whenever there's anything to reconcile — a snapshot OR a staged tray.
      // (An orphan tray with no snapshot must still be checked so the Add-to-Cart modal
      // doesn't report stale "in your quote" parts.)
      if (!this.committedSnapshotQuote &&
          (!this.selectedPartsQuote || this.selectedPartsQuote.length === 0)) return;

      if (!localStorage.cartIdRFQ || String(localStorage.cartIdRFQ) === "0") {
        console.log(
          "[Insights/Quote] No active cartIdRFQ — clearing stale quote tray + linkage"
        );
        this.selectedPartsQuote = [];
        this.committedSnapshotQuote = null;
        this.saveSelectionsToStorageQuote();
        return;
      }

      try {
        var cart = await apiService.getCart({
          id: localStorage.cartIdRFQ,
        });
        if (
          !cart ||
          !cart.purchaseOrders ||
          cart.purchaseOrders.length === 0
        ) {
          console.log(
            "[Insights/Quote] Linked cart " +
              localStorage.cartIdRFQ +
              " is empty or missing — clearing stale quote tray + linkage"
          );
          this.selectedPartsQuote = [];
          this.committedSnapshotQuote = null;
          this.saveSelectionsToStorageQuote();
        }
      } catch (e) {
        console.warn(
          "[Insights/Quote] Linked cart fetch failed — breaking linkage",
          e
        );
        this.committedSnapshotQuote = null;
        this.saveSelectionsToStorageQuote();
      }
    },

    scheduleSyncToQuoteCart() {
      if (!localStorage.cartIdRFQ || String(localStorage.cartIdRFQ) === "0") {
        return;
      }
      if (this._syncTimerQuote) {
        clearTimeout(this._syncTimerQuote);
      }
      var self = this;
      this._syncTimerQuote = setTimeout(function () {
        self._syncTimerQuote = null;
        self.syncQueueToQuoteCart();
      }, 300);
    },

    async syncQueueToQuoteCart() {
      if (this.addingInsightPart) {
        this._syncDirtyQuote = true;
        return;
      }
      if (!localStorage.cartIdRFQ || String(localStorage.cartIdRFQ) === "0") {
        return;
      }
      if (!this.companyId) return;

      this.addingInsightPart = true;
      var self = this;

      try {
        // Empty queue → delete the cart entirely.
        if (!this.selectedPartsQuote || this.selectedPartsQuote.length === 0) {
          var cartIdToDelete = parseInt(localStorage.cartIdRFQ, 10);
          try {
            await apiService.deleteCart({ id: cartIdToDelete });
          } catch (e) {
            console.warn(
              "[Insights/Quote → sync] deleteCart returned error:",
              e
            );
          }
          localStorage.cartIdRFQ = 0;
          this.clearSelectionsStorageQuote();
          this.committedSnapshotQuote = null;
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        // Non-empty queue → re-resolve every part and send a
        // fresh saveCart payload.
        var resolved = await Promise.all(
          this.selectedPartsQuote.map(async function (p) {
            return await self.resolvePartContext(p);
          })
        );

        var missing = resolved.filter(function (p) {
          return !p._companyMachineId || !p._assemblyId;
        });
        if (missing.length > 0) {
          console.warn(
            "[Insights/Quote → sync] skipped — some parts failed to resolve:",
            missing.map(function (m) { return m.partCode; }).join(", ")
          );
          return;
        }

        // ===== ADD vs REMOVE vs QTY-CHANGE DELTA =====
        var snapshotKeys = {};
        var snapshotQtys = {};
        (this.committedSnapshotQuote || []).forEach(function (s) {
          var k = s.partId + "|" + (s.poNumber || "");
          snapshotKeys[k] = true;
          snapshotQtys[k] = Number(s.quantity);
        });
        var currentKeys = {};
        resolved.forEach(function (p) {
          currentKeys[p.partId + "|" + (p.poNumber || "")] = true;
        });

        var addedParts = resolved.filter(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          return !snapshotKeys[key];
        });
        var hasRemovals = (this.committedSnapshotQuote || []).some(function (s) {
          var key = s.partId + "|" + (s.poNumber || "");
          return !currentKeys[key];
        });
        var hasQtyChange = resolved.some(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          if (!(key in snapshotQtys)) return false;
          return Number(p.quantity) !== snapshotQtys[key];
        });

        if (addedParts.length === 0 && !hasRemovals && !hasQtyChange) {
          this.committedSnapshotQuote = this.buildCommittedSnapshotQuote();
          this.saveSelectionsToStorageQuote();
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        var needsRecreate = hasRemovals || hasQtyChange;
        var partsForPayload = needsRecreate ? resolved : addedParts;

        var poGroups = {};
        var poOrder = [];
        partsForPayload.forEach(function (p) {
          if (!poGroups[p.poNumber]) {
            poGroups[p.poNumber] = [];
            poOrder.push(p.poNumber);
          }
          poGroups[p.poNumber].push(p);
        });

        var purchaseOrders = poOrder.map(function (po) {
          var parts = poGroups[po];
          var first = parts[0];
          return {
            number: po,
            assemblyId: Number(first._assemblyId) || 0,
            machineId: Number(first._companyMachineId) || 0,
            configuredWear: true,
            parts: parts.map(function (p) {
              return {
                id: Number(p.partId) || 0,
                quantity: Number(p.quantity) || 1,
                quantityPerMachine: 0,
              };
            }),
          };
        });

        // ===== If removals or qty changes, delete + recreate =====
        if (needsRecreate) {
          var oldCartId = parseInt(localStorage.cartIdRFQ, 10) || 0;
          if (oldCartId > 0) {
            try {
              await apiService.deleteCart({ id: oldCartId });
            } catch (e) {
              console.warn(
                "[Insights/Quote → sync] deleteCart before recreate failed:",
                e
              );
            }
          }
        }

        var saveCartId = needsRecreate
          ? 0
          : parseInt(localStorage.cartIdRFQ, 10) || 0;
        var savePayload = {
          id: saveCartId,
          purchaseOrders: purchaseOrders,
          companyId: Number(this.companyId) || 0,
          specialInstructions: "",
          CreateQuote: true,
        };

        var response = await apiService.saveCart(savePayload);

        if (response && response.id) {
          localStorage.cartIdRFQ = response.id;
        }

        this.committedSnapshotQuote = this.buildCommittedSnapshotQuote();
        this.saveSelectionsToStorageQuote();

        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }
      } catch (err) {
        console.warn("[Insights/Quote → sync] saveCart failed:", err);
        this.$bvToast.toast(
          "Couldn't sync the change to your quote. The staging tray " +
            "is up to date — refresh the page or click Continue to retry.",
          {
            title: "Quote sync failed",
            autoHideDelay: 6000,
            variant: "warning",
            solid: true,
          }
        );
      } finally {
        this.addingInsightPart = false;
        if (this._syncDirtyQuote) {
          this._syncDirtyQuote = false;
          this.scheduleSyncToQuoteCart();
        }
      }
    },

    buildCommittedSnapshotQuote() {
      return (this.selectedPartsQuote || []).map(function (p) {
        return {
          partId: p.partId,
          poNumber: p.poNumber,
          quantity: p.quantity,
        };
      });
    },

    async proceedWithSelectedInPlaceQuote() {
      this._suppressCartNav = true;
      try {
        await this.proceedWithSelectedQuote();
      } finally {
        this._suppressCartNav = false;
      }
    },

    async proceedWithSelectedQuote() {
      if (this.addingInsightPart || this.selectedPartsQuote.length === 0) return;
      if (!this.companyId) return;

      this.addingInsightPart = true;
      var self = this;

      try {
        var resolved = await Promise.all(
          this.selectedPartsQuote.map(async function (p) {
            return await self.resolvePartContext(p);
          })
        );

        var missing = resolved.filter(function (p) {
          return !p._companyMachineId || !p._assemblyId;
        });
        if (missing.length > 0) {
          var codes = missing.map(function (p) { return p.partCode; }).join(", ");
          this.$bvToast.toast(
            "Could not resolve the machine and assembly context for: " +
              codes +
              ". The backend needs a getPartContext endpoint — see the " +
              "console log for the payload we tried to send.",
            {
              title: "Quote update blocked",
              autoHideDelay: 12000,
              variant: "warning",
              solid: true,
            }
          );
          console.warn(
            "[Insights/Quote → saveCart] could not resolve context for parts:",
            missing
          );
          return;
        }

        var existingCartId = parseInt(localStorage.cartIdRFQ, 10) || 0;
        var snapshotKeys = {};
        (this.committedSnapshotQuote || []).forEach(function (s) {
          snapshotKeys[s.partId + "|" + (s.poNumber || "")] = true;
        });
        var currentKeys = {};
        resolved.forEach(function (p) {
          currentKeys[p.partId + "|" + (p.poNumber || "")] = true;
        });

        var addedParts = resolved.filter(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          return !snapshotKeys[key];
        });
        var hasRemovals = existingCartId > 0
          && (this.committedSnapshotQuote || []).some(function (s) {
              var key = s.partId + "|" + (s.poNumber || "");
              return !currentKeys[key];
            });

        if (addedParts.length === 0 && !hasRemovals) {
          this.committedSnapshotQuote = this.buildCommittedSnapshotQuote();
          this.saveSelectionsToStorageQuote();
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          if (!this._suppressCartNav) {
            this.$router.push("/cart/rfqsaveditems");
          }
          return;
        }

        var partsForSave;
        if (hasRemovals || existingCartId === 0) {
          partsForSave = resolved;
        } else {
          partsForSave = addedParts;
        }

        var poGroups = {};
        var poOrder = [];
        partsForSave.forEach(function (p) {
          if (!poGroups[p.poNumber]) {
            poGroups[p.poNumber] = [];
            poOrder.push(p.poNumber);
          }
          poGroups[p.poNumber].push(p);
        });

        var purchaseOrders = poOrder.map(function (po) {
          var parts = poGroups[po];
          var first = parts[0];
          return {
            number: po,
            assemblyId: Number(first._assemblyId) || 0,
            machineId: Number(first._companyMachineId) || 0,
            configuredWear: true,
            parts: parts.map(function (p) {
              return {
                id: Number(p.partId) || 0,
                quantity: Number(p.quantity) || 1,
                quantityPerMachine: 0,
              };
            }),
          };
        });

        if (hasRemovals && existingCartId > 0) {
          try {
            await apiService.deleteCart({ id: existingCartId });
          } catch (e) {
            console.warn(
              "[Insights/Quote → saveCart] deleteCart before recreate failed:",
              e
            );
          }
        }

        var saveCartIdForPayload = hasRemovals ? 0 : existingCartId;
        var savePayload = {
          id: saveCartIdForPayload,
          purchaseOrders: purchaseOrders,
          companyId: Number(this.companyId) || 0,
          specialInstructions: "",
          CreateQuote: true,
        };

        console.log("[Insights/Quote → saveCart] sending payload:", savePayload);
        var response = await apiService.saveCart(savePayload);

        if (response && response.id) {
          localStorage.cartIdRFQ = response.id;
        }

        this.committedSnapshotQuote = this.buildCommittedSnapshotQuote();
        this.saveSelectionsToStorageQuote();

        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }

        if (!this._suppressCartNav) {
          this.$router.push("/cart/rfqsaveditems");
        }
      } catch (err) {
        var details = "";
        try {
          var resp = err && err.error && err.error.response;
          if (resp && resp.data) {
            console.error("[Insights/Quote → saveCart] backend response:", resp.data);
            details =
              resp.data.title ||
              resp.data.exception ||
              (resp.data.errors && JSON.stringify(resp.data.errors)) ||
              JSON.stringify(resp.data);
          } else {
            console.error("[Insights/Quote → saveCart] error:", err);
            details = (err && err.message) || String(err);
          }
        } catch (e) {
          console.error("[Insights/Quote → saveCart] error (unparseable):", err);
        }

        this.$bvToast.toast(
          "Could not add the selected parts to your quote. " +
            (details
              ? "Server said: " + details
              : "Check the browser console for details."),
          {
            title: "Quote update failed",
            autoHideDelay: 9000,
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.addingInsightPart = false;
      }
    },
  },
};
</script>

<style scoped>
#createOrder {
  padding-bottom: 15px;
}

/* Page breadcrumb spacing */
#createOrder #breadcrumb {
  margin-bottom: 8px;
}

#createOrder #breadcrumb .container {
  padding-left: 0;
  padding-right: 0;
}

/* Left and right columns align better */
#createOrder .row.align-items-stretch {
  align-items: stretch;
}

/* Main order form panel */
#orderForm {
  height: 100%;
}

/* Title spacing */
#orderForm h1 {
  margin-bottom: 12px;
  text-transform: uppercase;
}

#orderForm h2 {
  margin-bottom: 0;
}

/* Update banner */
#updateBanner {
  cursor: pointer;
  height: 100%;
  min-height: 92px;
}

#updateBanner .impUpdate {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Inputs and dropdown rhythm */
#orderForm label {
  display: block;
  margin-bottom: 6px;
}

#orderForm input[type="text"] {
  width: 100%;
}

/* Semantic dropdowns in this page */
#CompanySelect,
#MachineSelect {
  min-height: 44px;
}

/* Let the machine and assembly dropdown menus grow with
   their content rather than capping at Semantic UI's
   default ~200px (which forces scroll even on short
   lists). Cap at 60vh so a very long list still can't
   push other UI off-screen — past that, the menu's own
   scrollbar takes over. The "Don't see your machine?" /
   "Don't see your assembly?" help footers are the last
   children of .menu, so this rule applies to the items +
   footer together. */
#MachineSelect .menu,
#WearAssemblySelect .menu {
  max-height: none !important;
}

/* Checkbox block */
#configuredParts {
  margin-top: 18px;
}

#configuredParts + label,
#configuredParts ~ label,
#createOrder .custom-control-label {
  line-height: 1.35;
}

/* Primary CTA */
#orderForm .btn.btn-primary {
  width: 100%;
  min-height: 48px;
  text-transform: uppercase;
}

/* ===== STICKY RIGHT COLUMN =====
   On large screens the right column (staging tray on top
   + Frequently Ordered below) follows the user as they
   scroll the long left column.

   Two things to be careful about:
     1. align-self: flex-start so the column doesn't stretch
        to match the left column's height — sticky elements
        only work when shorter than their scroll container.
     2. `top` offset is large enough to clear the site's
        fixed header + breadcrumb row. Anything smaller and
        the panel pins behind them, which is what hid the
        staging tray earlier.

   No internal scrollbar (overflow-y: auto) on the column —
   that lets the panel be scrolled independently of the
   page, which lets the staging tray disappear off the top
   of the panel even though it should always be visible at
   the top. If the panel grows taller than the viewport,
   sticky simply stops working past the bottom edge — that's
   fine, the user can keep scrolling the page normally.

   Disabled on smaller screens (col-lg- collapses to
   stacked) so the panel doesn't pin awkwardly above the
   form steps. */
@media (min-width: 992px) {
  .create-order-row > .col-lg-5 {
    position: sticky;
    top: 130px;
    align-self: flex-start;
  }
  /* Stop the left column from stretching to match the
     (often-tall) right panel. Without this, the form card's
     grey form-step boxes balloon to fill all the empty
     vertical space below the dropdowns when the user hasn't
     selected a machine yet (no Step 3 to push the form down).
     align-self: flex-start makes the left column take its
     natural content height — empty space below it is just
     page background, not stretched form chrome. */
  .create-order-row > .col-lg-7 {
    align-self: flex-start;
  }
}

.insights-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 8px;
  margin-bottom: 6px;
  border-bottom: 1px dashed #d8d8d8;
}

.insights-panel-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

/* Header right-side group: YTD/All Time toggle + search input.
   Sits to the right of the panel title; on narrow viewports the
   group wraps to a new line below the title. The two children
   (toggle + search) can themselves wrap relative to each other
   if the column is especially tight. */
.insights-panel-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.insights-panel-toggle {
  display: flex;
  gap: 4px;
}

.insights-panel-toggle .btn-sm {
  font-size: 11px;
  padding: 4px 12px;
  letter-spacing: 0.2px;
}

/* Force black text on the inactive (outline) toggle button.
   Bootstrap-Vue's btn-outline-dark inherits a near-white color in
   the global theme on this site, so the "All Time" label was
   showing as nearly invisible against white. Pin the colors
   explicitly so it reads cleanly in both states. */
.insights-panel-toggle .btn-outline-dark {
  color: #222 !important;
  background: #fff !important;
  border: 1px solid #555 !important;
  font-weight: 600 !important;
}

.insights-panel-toggle .btn-outline-dark:hover,
.insights-panel-toggle .btn-outline-dark:focus {
  color: #fff !important;
  background: #555 !important;
  border-color: #555 !important;
}

.insights-panel-toggle .btn-dark {
  color: #fff !important;
  font-weight: 600 !important;
}

/* ===== HISTORY SEARCH INPUT =====
   Pill-shaped input with magnifying-glass icon on the left and an
   × clear button on the right (only visible when there's text).
   Width grows to fill remaining space on the controls row, with a
   sensible min so it stays usable when the column is tight. */
.insights-panel-search {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1 1 130px;
  min-width: 130px;
  max-width: 200px;
  padding: 0 8px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 14px;
  height: 26px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.insights-panel-search:focus-within {
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.12);
}

.insights-search-icon {
  flex-shrink: 0;
  color: #888;
}

.insights-panel-search:focus-within .insights-search-icon {
  color: #c0392b;
}

.insights-search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  color: #222;
  padding: 0;
}

.insights-search-input::placeholder {
  color: #aaa;
  font-style: italic;
}

.insights-search-clear {
  flex-shrink: 0;
  background: transparent;
  border: none;
  padding: 0;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  color: #888;
  cursor: pointer;
  line-height: 1;
}

.insights-search-clear:hover {
  background: #c0392b;
  color: #fff;
}

/* Inline "Clear search" link inside the no-results empty state. */
.insights-empty-clear {
  display: inline-block;
  margin-left: 6px;
  color: #c0392b;
  text-decoration: underline;
  cursor: pointer;
  font-style: normal;
}

.insights-empty-clear:hover {
  color: #a8321f;
  text-decoration: underline;
}

.insights-panel-sub {
  margin: 0 0 14px;
  font-size: 12px;
  color: #777;
  line-height: 1.4;
}

.insights-panel-sub strong {
  color: #444;
}

.insights-empty {
  padding: 30px 12px;
  text-align: center;
  background: #fafafa;
  border: 1px dashed #e0e0e0;
  border-radius: 6px;
}

.insights-empty-text {
  font-size: 13px;
  color: #888;
  font-style: italic;
  line-height: 1.5;
}

/* ===== SELECTED TRAY =====
   Sits at the top of the panel (above the part list) when the user
   has anything queued. Soft yellow-ish accent so it reads as
   "this is your queue, not the catalog." Each chip is a pill with
   the part code and an × to remove. Clear-all link sits in the
   header so users can reset without a per-chip dance. */
/* Staging tray — the user's working order. Earns its
   visual weight: a 4px red left accent (matches the
   "Order #N" pattern used on the cart page), a soft
   shadow to lift it off the panel, and a dedicated title
   row above the count line so it never reads as
   secondary content. */
.insights-selected-tray {
  margin-bottom: 16px;
  padding: 14px 14px 12px 14px;
  background: #fff;
  border: 1px solid #e7d6a6;
  border-left: 4px solid #c0392b;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(192, 57, 43, 0.08);
  position: relative;
}

.insights-selected-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e6c97a;
}

.insights-selected-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #c0392b;
  color: #fff;
  border-radius: 50%;
  flex-shrink: 0;
}

.insights-selected-title-text {
  font-size: 14px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
}

.insights-selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.insights-selected-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #8a6d2a;
}

.insights-selected-clear {
  background: transparent;
  border: none;
  padding: 0;
  font-size: 11px;
  color: #888;
  text-decoration: underline;
  cursor: pointer;
}

.insights-selected-clear:hover {
  color: #c0392b;
}

/* ===== QUEUE ROWS =====
   Replaces chip-based rendering. Each row is a compact mini-cart
   line: part code → qty input → × remove. Number input width is
   tight (around 60px) so it sits inline with the other elements
   without dominating. */
.insights-queue-rows {
  list-style: none;
  margin: 0;
  padding: 0;
}

.insights-queue-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f5e9c5;
}

.insights-queue-row:last-child {
  border-bottom: none;
}

.insights-queue-row-code {
  flex: 1 1 auto;
  font-size: 12px;
  font-weight: 700;
  color: #5a4716;
  letter-spacing: 0.2px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insights-queue-row-qty {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.insights-qty-input {
  width: 56px;
  padding: 3px 6px;
  font-size: 12px;
  font-weight: 600;
  color: #222;
  text-align: center;
  border: 1px solid #d4c486;
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.insights-qty-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}

/* Hide the spinner controls in webkit so the input looks clean —
   the user can still type any positive integer. */
.insights-qty-input::-webkit-inner-spin-button,
.insights-qty-input::-webkit-outer-spin-button {
  opacity: 1;
}

.insights-qty-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8a6d2a;
}

.insights-queue-row-remove {
  flex-shrink: 0;
  background: transparent;
  border: none;
  width: 22px;
  height: 22px;
  padding: 0;
  font-size: 16px;
  font-weight: 700;
  color: #888;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.insights-queue-row-remove:hover {
  background: #c0392b;
  color: #fff;
}

/* ===== ORDER GROUPS WITHIN TRAY =====
   Each PO becomes its own little card inside the tray, with an
   "Order #N · PO: 12345 · M parts" header and chips beneath. The
   visual weight of the group header makes the order grouping legible
   without being noisy. */
.insights-order-group {
  margin-top: 10px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #ecdfb3;
  border-radius: 4px;
}

.insights-order-group:first-of-type {
  margin-top: 4px;
}

.insights-order-group-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
  padding-bottom: 5px;
  border-bottom: 1px dashed #f0e3b5;
}

.insights-order-group-num {
  font-size: 12px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.insights-order-group-meta {
  font-size: 11px;
  color: #8a6d2a;
}

.insights-order-group-meta strong {
  color: #5a4716;
}

/* ===== CONTINUE BUTTON META =====
   Sub-line under the Continue button shown only when there's more
   than one order — otherwise the breakdown is implicit. */
.insights-continue-meta {
  margin-top: 6px;
  text-align: center;
  font-size: 11px;
  color: #888;
  font-style: italic;
}

/* Sub-meta when the cart already matches the staging tray —
   the button text says "Go to Cart" and this confirms why
   without yelling. Subtle green to convey "synced / no
   action needed". */
.insights-continue-meta-synced {
  color: #2d7a3a;
}

/* Sync-status line shown beneath the staging tray after the
   Continue button was removed. Communicates whether the cart
   reflects the current queue without taking up a full button.
   Synced state uses the brand green (already used elsewhere
   for "all good" signals); pending state uses a muted gray
   so it doesn't grab attention during the brief save window. */
/* ===========================================================
   CREATE ORDER FORM — left side of /orders/create
   ===========================================================
   Two-step layout: Order Details (Company + PO) and Find Your
   Part by Machine (machine select + wear filter). Plus a
   tinted help panel for users whose machine isn't listed yet
   (new equipment, not-yet-configured), which routes them to
   the parts team rather than letting them get stuck. */

.create-order-form {
  /* Card visual: lift the form off the page with a thin red
     top accent, white background, soft shadow, and rounded
     corners. The red top accent is a real border-top (see the
     #orderForm rule below) so it matches the cart panel exactly.
     Background of the section behind the card is left as
     the page default; the card creates contrast on its own
     via shadow rather than a colored backdrop, which keeps
     the surrounding chrome neutral.

     The form element also carries Bootstrap's `.row`
     class, so we override the negative margins it brings
     in — otherwise the row's children would sit 15px
     outside the card's padding and break the visual.

     Note: this must NOT use `overflow: hidden` — that was
     clipping the Machine dropdown menu (Semantic UI .menu is
     position:absolute and got cut off at the card edge). The
     red border-top accent follows the card's border-radius on
     its own, so no clipping boundary is needed. */
  /* Normal in-flow card: it GROWS with the results so the PAGE scrolls and the
     footer trails below it (the user wants to scroll the results list, with the
     footer following — it doesn't need to stay on-screen). This was a
     fixed/sticky panel; reverted because pinning a panel taller than the
     viewport fought scrolling and the results couldn't be reached. z-index
     keeps the red top accent above page chrome but below the floating widgets
     (z 8500). */
  position: relative;
  z-index: 2;
  /* This card is a Bootstrap .row (display:flex; flex-wrap:wrap). As a fixed
     panel taller than its content, the default align-content/align-items
     (stretch) spread the wrapped rows apart — the title and "Completed Steps"
     rows ballooned to 230/217px, pushing the parts list off the bottom of the
     screen. Pack everything to the top instead so rows keep their natural
     height and the list stays on-screen. (Nested rows inside the form-steps,
     e.g. the side-by-side dropdowns, have their own flex context and are
     unaffected.) Panel height is capped against the footer in App.vue's
     constrainWidgetStack so the list area scrolls instead of overflowing. */
  align-content: flex-start;
  align-items: flex-start;
  /* No overflow on the panel itself: the header (title, Completed Steps, the
     assembly dropdowns) must stay STATIC/pinned, and a panel-level scroll
     would scroll it away (and clip the Semantic dropdown menu). Only the
     results list scrolls (see .inline-wear-list) — clamped to the footer by
     App.vue's constrainWidgetStack, re-run on a MutationObserver when results
     render. */
  background: #ffffff;
  border-radius: 8px;
  padding: 28px 28px 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04),
              0 8px 24px rgba(0, 0, 0, 0.08);
  margin-left: 0;
  margin-right: 0;
}

.create-order-form > [class*="col-"] {
  padding-left: 0;
  padding-right: 0;
}

/* Brand-red top accent. Implemented as a real 3px top BORDER (not a
   ::before bar) so it renders IDENTICALLY to the floating cart / Top 5
   panels (border-top: 3px solid #c0392b on a 6px-radius card). The old
   ::before sat 1px below the card's grey top border and rounded at 8px
   (vs the card's 6px), so the red line read as thinner/lower than the
   cart's. Targeting #orderForm (id) + !important beats the panel's faint
   1px grey top border. Mirrors the fix in Orders/Create.vue. */
#orderForm {
  border-top: 3px solid #c0392b !important;
}

/* Align the form card with the breadcrumb. steele's "standard page
   spacing" rule forces `#createOrder > .container { padding: 30px 24px 15px
   !important }`, which pushed the card's left edge 24px inboard of the
   "Home" breadcrumb. Override the HORIZONTAL padding with our own
   !important — this scoped rule's data-v attribute outranks steele's, so it
   wins. Vertical padding is left to steele. Mirrors Orders/Create.vue. */
#createOrder > .container {
  margin-top: 20px;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* Bootstrap's .row applies margin-left/right: -15px to
   outdent within a padded container. With the container
   padding zeroed (above), those negative margins would push
   the columns past the page edge. Neutralize them and
   neutralize the columns' compensating padding so children
   sit flush with the breadcrumb. The right column gets a
   small left-padding back to preserve the gutter between
   form and staging tray. */
#createOrder .create-order-row {
  margin-left: 0;
  margin-right: 0;
}

#createOrder .create-order-row > [class*="col-"] {
  padding-left: 0;
  padding-right: 0;
}

#createOrder .create-order-row > .col-lg-5 {
  padding-left: 24px;
}

@media (max-width: 991.98px) {
  /* On stacked layout the staging tray drops below the
     form — restore default padding so it doesn't bleed
     into the form card visually. */
  #createOrder .create-order-row > .col-lg-5 {
    padding-left: 0;
    margin-top: 16px;
  }
}

.create-order-title {
  margin-bottom: 6px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.create-order-subtitle {
  margin-bottom: 24px;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.new-equipment-link {
  color: #c0392b;
  font-weight: 500;
  text-decoration: underline;
}

.new-equipment-link:hover {
  color: #a8321f;
}

/* ----- Numbered step container ----- */
.form-step {
  margin-bottom: 20px;
  padding: 18px 20px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
}

/* ===== COMPLETED STEPS NAV ROW =====
   Compact row of clickable numbered circles for finished/started steps,
   shown above the one expanded step. Works in light + dark mode. */
.completed-steps-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}
.completed-steps-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a93a0;
  margin-right: 2px;
}
.completed-step-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.1s ease, box-shadow 0.1s ease, background-color 0.1s ease;
}
.completed-step-circle.is-done {
  background: #27ae60; /* green = completed (matches the in-step circle) */
}
.completed-step-circle.is-pending {
  /* red = this step still needs a selection; flips to green (is-done) once made */
  background: #c0392b;
}
.completed-step-circle:hover {
  transform: scale(1.12);
  box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.3);
}
/* Wear-parts toggle parked at the right end of the steps row. */
.steps-wear-toggle {
  margin-left: auto;
}

.form-step-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

/* Variant for headers that carry a right-aligned action
   (currently: Step 3 with the Show-wear-parts-only toggle).
   The header-main wrapper holds the step num + title on
   the left; the action sits flush right via
   justify-content: space-between. flex-wrap lets the
   action drop to a new row on very narrow viewports
   instead of overflowing the card. */
.form-step-header-with-action {
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: 8px;
}

.form-step-header-main {
  display: flex;
  align-items: center;
  gap: 12px;
  /* min-width: 0 + flex-shrink: 1 lets the title truncate
     gracefully if the header gets squeezed instead of
     pushing the action off the right edge. */
  min-width: 0;
  flex: 1 1 auto;
}

/* Step number circle. Default state is muted gray —
   "this step exists, but it isn't done yet." When the
   step's data is captured (company picked, machine picked,
   assembly picked) it flips to green via the `.is-complete`
   modifier, giving the user a glanceable progress
   indicator without adding extra UI. */
.form-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #adb5bd;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  transition: background-color 0.2s ease;
}

.form-step-num.is-complete {
  background: #27ae60;
}

.form-step-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #222;
  line-height: 28px;
}
/* ===== STEP-3 PART FILTER ===== (replaced the wear toggle in this header) */
.part-filter-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: 0 1 320px;
  min-width: 180px;
}
.part-filter-icon {
  position: absolute;
  left: 9px;
  font-size: 12px;
  opacity: 0.55;
  pointer-events: none;
}
.part-filter-input {
  width: 100%;
  padding: 7px 26px 7px 44px;
  border: 1px solid #d4d8dd;
  border-radius: 6px;
  font-size: 13px;
  color: #16181b;
  background: #fff;
}
/* Beat any global/theme `input` rule (the field is dark-themed by one) so the
   placeholder always clears the 🔍 icon. High specificity + !important. */
.part-filter-wrap .part-filter-input,
.part-filter-wrap.part-filter-wrap-block .part-filter-input {
  padding-left: 44px !important;
  text-indent: 0;
}
.part-filter-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}
.part-filter-clear {
  position: absolute;
  right: 6px;
  border: none;
  background: transparent;
  color: #8a93a0;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}
.part-filter-clear:hover {
  color: #c0392b;
}
/* Block placement of the filter below the assembly/sub-assembly row. */
.part-filter-wrap-block {
  display: flex;
  width: 100%;
  max-width: 360px;
  margin: 0 0 14px;
}
/* Filter row mirrors the assembly/sub-assembly picker row above (same flex columns
   + gap), so col 1 (the part filter) matches the "Choose an Assembly" field width and
   col 2 (the discount toggle) left-aligns under "Jump to Sub-Assembly". Wraps to a
   stack on narrow cards. */
.part-filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  margin: 0 0 14px;
}
.part-filter-row > .part-filter-wrap-block {
  flex: 1 1 240px;      /* SAME sizing as .inline-wear-picker → tracks the assembly field */
  min-width: 0;
  max-width: none;      /* drop the 360px cap so it matches the assembly field at any width */
  margin: 0;
}
.part-filter-row > .part-filter-discount-col {
  flex: 1 1 240px;      /* 2nd column → aligns under the sub-assembly field */
  min-width: 0;
  display: flex;
  align-items: center;
}
.discount-only-toggle {
  margin: 0;
  transform: scale(0.9);          /* a touch smaller per request */
  transform-origin: left center;
}
.discount-only-label {
  font-size: 13px;
  font-weight: 600;
  color: #16181b;
  cursor: pointer;
}
/* Dark mode: the label is near-black (#16181b) → dark-on-dark on the Search page's dark
   surface. Lighten it. Scoped rule → compiles to html.dark-mode .discount-only-label[data-v],
   which matches because the span is in this component's template. */
html.dark-mode .discount-only-label { color: #e3e7eb; }

.form-step-body label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #444;
  text-transform: none;
  letter-spacing: 0;
}

.form-step-body input[type="text"] {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
}

.form-step-body input[type="text"]:focus {
  border-color: #c0392b;
  outline: none;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}

/* Helper text under inputs: small, muted, italic. Keeps
   guidance close to the input it applies to without
   competing with the label. */
.form-helper-text {
  margin-top: 6px;
  font-size: 12px;
  color: #888;
  line-height: 1.4;
}

.form-helper-text a {
  color: #c0392b;
  text-decoration: underline;
}

/* ----- Wear-parts toggle ----- */
.wear-parts-toggle {
  /* Override Bootstrap-Vue's default vertical alignment so
     the label and helper text stack neatly to the right of
     the checkbox rather than running on a single line. */
}

/* Variant when the toggle lives inside a step header rather
   than its own row. Tightens the label sizing so it sits
   comfortably next to the step title without dominating
   the row, and removes any inherited block margins from
   the previous .col-12 mt-3 wrapper. */
.wear-parts-toggle-header {
  margin: 0;
  flex-shrink: 0;
}

.wear-parts-toggle-header .wear-parts-label {
  font-size: 13px;
}

.wear-parts-toggle-header .wear-parts-helper {
  font-size: 11px;
}

.wear-parts-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #222;
  line-height: 1.3;
}

.wear-parts-helper {
  display: block;
  font-size: 12px;
  color: #888;
  font-style: italic;
  font-weight: 400;
  margin-top: 2px;
}

/* ----- New equipment help panel -----
   Visually distinct from the numbered steps so users
   intuit it as a different type of element ("not part of
   the form, but a way out"). Soft yellow tint conveys
   "info / heads up" without being alarming. */
.new-equipment-panel {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #fff8e6;
  border: 1px solid #f3deaa;
  border-radius: 6px;
  padding: 16px 18px;
  margin-bottom: 20px;
}

.new-equipment-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  color: #b8860b;
  margin-top: 2px;
}

.new-equipment-icon svg {
  width: 100%;
  height: 100%;
}

.new-equipment-content {
  flex: 1;
}

.new-equipment-content strong {
  display: block;
  font-size: 14px;
  color: #222;
  margin-bottom: 4px;
}

.new-equipment-content p {
  margin: 0 0 12px 0;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.new-equipment-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.new-equipment-actions .btn-sm {
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
}

/* Force black text on both action buttons regardless of
   Bootstrap variant inheritance. The btn-outline-primary
   class otherwise pulls red text from the brand red, which
   gets lost against the yellow tinted panel background. */
.new-equipment-actions .btn-outline-primary,
.new-equipment-actions .btn-outline-secondary {
  color: #222 !important;
  border-color: #888 !important;
  background: #fff !important;
}

.new-equipment-actions .btn-outline-primary:hover,
.new-equipment-actions .btn-outline-secondary:hover {
  color: #fff !important;
  background: #c0392b !important;
  border-color: #c0392b !important;
}

/* ----- Submit row ----- */
.form-submit-row {
  margin-top: 8px;
}

.proceed-btn {
  font-size: 15px;
  font-weight: 600;
  padding: 12px 16px;
  letter-spacing: 0.3px;
  border-radius: 4px;
}

.proceed-btn:disabled {
  background: #d0d0d0 !important;
  border-color: #d0d0d0 !important;
  color: #888 !important;
  cursor: not-allowed;
}

.insights-sync-status {
  margin-top: 12px;
  padding: 8px 12px;
  text-align: center;
  font-size: 12px;
  color: #2d7a3a;
  background: #f1f9f3;
  border: 1px solid #d4e9d8;
  border-radius: 4px;
  font-weight: 500;
}

.insights-sync-dot {
  display: inline-block;
  margin-right: 6px;
  color: #2d7a3a;
  font-size: 10px;
  vertical-align: middle;
}

.insights-sync-status-pending {
  color: #888;
  background: #f7f7f7;
  border-color: #e5e5e5;
  font-style: italic;
}

.insights-part-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.insights-part-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.insights-part-item:last-child {
  border-bottom: none;
}

.insights-part-main {
  flex: 1 1 auto;
  min-width: 0;
}

.insights-part-code {
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.2px;
  margin-bottom: 2px;
}

.insights-part-desc {
  font-size: 13px;
  color: #333;
  line-height: 1.35;
  margin-bottom: 4px;
}

.insights-part-context {
  color: #888;
  font-size: 12px;
}

.insights-part-meta {
  font-size: 11px;
  color: #888;
  font-style: italic;
}

.insights-part-meta strong {
  color: #555;
  font-style: normal;
}

/* Compact add-to-cart action — icon + label, brand red, link-styled
   so it doesn't compete visually with the page's primary CTA button. */
.insights-part-add {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #c0392b;
  background: #fff;
  border: 1.5px solid #c0392b;
  border-radius: 4px;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
  cursor: pointer;
}

.insights-part-add:hover {
  background: #c0392b;
  color: #fff;
  text-decoration: none;
}

.insights-part-add.disabled,
.insights-part-add.disabled:hover {
  opacity: 0.5;
  pointer-events: none;
  background: #fff;
  color: #c0392b;
}

/* Selected state — Add button flips to the filled treatment with a
   checkmark icon to signal "this part is in your queue." Click
   removes it from the queue. */
.insights-part-add.is-selected {
  background: #c0392b;
  color: #fff;
  border-color: #c0392b;
}

.insights-part-add.is-selected:hover {
  background: #a8321f;
  color: #fff;
  border-color: #a8321f;
}

.insights-part-add svg {
  flex-shrink: 0;
}

/* ===== CONTINUE BUTTON =====
   Appears at the bottom of the panel when at least one part is
   queued. Full-width primary red mirroring the form's "Proceed
   with Order" button on the left so the two flows feel parallel:
   left = manual machine browse, right = quick-add from history. */
.insights-continue-wrap {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f0f0f0;
}

.insights-continue-btn {
  display: block;
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* See more button — link-style centered action below the part list.
   Sits flush at the bottom of the panel so the list visually grows
   into the same space rather than displacing other content. */
.insights-see-more-wrap {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px dashed #e5e5e5;
  text-align: center;
}

.insights-see-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: transparent;
  border: 1px solid #ccc;
  border-radius: 4px;
  color: #444;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.insights-see-more-btn:hover {
  background: #f3f3f3;
  border-color: #999;
  color: #222;
}

.insights-see-more-count {
  font-weight: 400;
  color: #888;
  text-transform: none;
  letter-spacing: 0;
}

/* Previous orders card */
#prevOrders {
  height: 100%;
  align-content: start;
}

#prevOrders .po-heading {
  font-size: 1.5rem;
  line-height: 1.2;
}

#prevOrders .btn.btn-dark {
  min-width: 160px;
  text-transform: uppercase;
}

/* Emergency CTA spacing */
#createOrder #emergencyCTA,
#createOrder .red-cta,
#createOrder .enterPO {
  margin-top: 8px;
}

/* Small helper text under company selector */
.cart-reset-span {
  cursor: pointer;
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 991.98px) {
  #createOrder > .container {
    margin-top: 16px;
  }

  #updateBanner {
    min-height: 72px;
    margin-top: 10px;
  }

  #configuredParts {
    margin-top: 10px;
  }

  #prevOrders .btn.btn-dark {
    width: 100%;
  }

  /* Stack the insights panel below the form on smaller screens
     and let it consume the full width — keeps each item readable. */
  .insights-panel {
    margin-top: 16px;
  }

  .insights-part-add-label {
    display: none;
  }

  .insights-part-add {
    padding: 6px 10px;
  }
}

/* ===== INLINE WEAR PARTS PANEL =====
   Sits in the right column above .insights-panel. Designed
   to feel native to the existing card stack — same border-
   radius, same shadow depth, same heading scale. Brand-red
   top accent distinguishes it from the cream Frequently
   Ordered card below. */
.inline-wear-panel {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  padding: 16px 16px 14px;
  margin-bottom: 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.inline-wear-header {
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e3e3e3;
}

.inline-wear-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #c0392b;
  margin: 0 0 4px 0;
}

.inline-wear-discount-note {
  font-size: 11px;
  color: #2e7d32;
  margin: 0;
  font-style: italic;
}

.inline-wear-discount-note strong {
  font-weight: 700;
}

/* Assembly picker — Semantic UI dropdown to match the
   Machine dropdown so the "Don't see your assembly?"
   help block can live inside the .menu. The native
   <select> implementation that previously lived here
   couldn't host arbitrary HTML inside <option>, which
   forced the help block to sit below the picker. The
   Semantic UI dropdown requires manual sync points
   (init after options load, set selected on programmatic
   value change, clear on reset) — those are handled in
   loadInlineWearAssemblyOptions and resetInlineWearState.

   The .inline-wear-picker-* native-select rules below are
   retained because the sub-assembly picker is still a
   native <select> (it's a filter, not a primary picker —
   doesn't need search or in-dropdown footer). */
/* The two pickers ("Choose an Assembly" + "Jump to Sub-Assembly") share
   one row as two equal columns (matching the approved design). They wrap
   to stacked rows on narrow widths. The sub-assembly column only renders
   once an assembly is picked; until then the assembly picker fills the
   row on its own. */
.inline-wear-picker-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 12px;
}
.inline-wear-picker-row > .inline-wear-picker {
  flex: 1 1 240px;
  min-width: 0;
  margin-bottom: 0;
}
.inline-wear-picker {
  margin-bottom: 12px;
}

.inline-wear-picker-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  margin: 0 0 4px 0;
}

.inline-wear-picker-select {
  display: block;
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  color: #222;
  background: #fff;
  border: 1px solid #d4d4d4;
  border-radius: 4px;
  appearance: auto;
  cursor: pointer;
}

.inline-wear-picker-select:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}

.inline-wear-picker-select:disabled {
  background: #f7f7f7;
  cursor: wait;
}

/* Tighten the Semantic UI assembly dropdown's font and
   min-height to fit this panel's compact density —
   Semantic UI's defaults are sized for full-width forms,
   which look oversized next to the sibling sub-assembly
   <select> below. */
.inline-wear-assembly-dropdown.ui.dropdown {
  font-size: 13px !important;
  min-height: 38px;
}

/* Widen the Machine + Assembly dropdown MENUS so long values aren't clipped.
   Semantic UI keeps each .item at white-space:nowrap (we WANT that — values
   shouldn't wrap) but pins the menu to the picker's narrow width, cutting off
   long names like "VACUUM CHAMBER / FRONT BARREL / HINGE…". Size the menu to its
   content instead (capped to the viewport). The help footer keeps its own
   white-space:normal, so only IT wraps. */
#MachineSelect.ui.dropdown > .menu,
#WearAssemblySelect.ui.dropdown > .menu {
  width: max-content !important;
  min-width: 100% !important;
  max-width: min(92vw, 520px) !important;
}
#MachineSelect.ui.dropdown > .menu > .item,
#WearAssemblySelect.ui.dropdown > .menu > .item {
  white-space: nowrap;   /* values stay on one line (no wrapping) */
}
/* At-a-glance "has a drawing?" indicator on each Assembly option. The item reserves
   right padding and the picture glyph is pinned to the far right so the icons line up
   down the list regardless of name length. Bright = has a drawing; faded grey = none.
   (data-text on the item keeps the SELECTED display clean — just the name.) */
#WearAssemblySelect.ui.dropdown > .menu > .item {
  position: relative;
  padding-right: 34px !important;
}
#WearAssemblySelect .wear-asm-drawing-ico {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  line-height: 1;
}
#WearAssemblySelect .wear-asm-drawing-ico.no-drawing {
  opacity: 0.28;
  filter: grayscale(1);
}

/* Empty / loading states — soft grey block, centered text. */
.inline-wear-empty {
  padding: 16px 12px;
  font-size: 12px;
  color: #F00;
  text-align: center;
  background: #fafafa;
  border-radius: 4px;
}

/* ===== ACCORDION =====
   Each sub-assembly is a card with a clickable header.
   Default-expanded so the parts are immediately visible. */
.inline-wear-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* The list flows naturally — the whole card grows with it and the PAGE
     scrolls (the card is in normal flow now), with the footer trailing below.
     No inner max-height/scroll. */
  max-height: none;
  overflow-y: visible;
  padding-right: 2px;
}

/* Wayfinding banner — sits between the first (expanded)
   sub-assembly card and the rest (collapsed). Keeps
   the user from missing that there are more categories
   below the one they're seeing. Subtle but visible:
   muted background, small text, downward arrow to
   reinforce the spatial relationship. */
.inline-wear-more-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #fff8e1;
  border: 1px dashed #e0c97a;
  border-radius: 4px;
  font-size: 12px;
  color: #6b5614;
  line-height: 1.4;
}

.inline-wear-more-hint strong {
  color: #c0392b;
  font-weight: 700;
}

/* ===== OFF-ACCOUNT HEADS-UP BANNER =====
   Non-blocking amber notice (same family as
   .inline-wear-more-hint) shown near the top of the form when
   the searched part's machine isn't on the account. Row layout:
   round "!" badge, message (flexes to fill), dismiss ×. */
.header-offaccount-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #fff8e1;
  border: 1px solid #e0c97a;
  border-left: 4px solid #e0a800;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.45;
  color: #6b5614;
}

.header-offaccount-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  background: #e0a800;
  color: #fff;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
}

.header-offaccount-text {
  flex: 1 1 auto;
  min-width: 0;
}

.header-offaccount-text strong {
  color: #5a4710;
  font-weight: 700;
}

.header-offaccount-text a {
  color: #c0392b;
  font-weight: 600;
  text-decoration: underline;
}

.header-offaccount-close {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: #6b5614;
  font-size: 20px;
  line-height: 1;
  padding: 0 2px;
  cursor: pointer;
  opacity: 0.7;
}

.header-offaccount-close:hover {
  opacity: 1;
}

.inline-wear-card {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 2px solid #c0392b;
  border-radius: 4px;
  overflow: hidden;
}

.inline-wear-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: #fafafa;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
}

.inline-wear-card-header:hover {
  background: #f3f3f3;
}

.inline-wear-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  background: #c0392b;
  color: #fff;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
}

.inline-wear-card-name {
  flex: 1 1 auto;
  font-size: 12px;
  font-weight: 700;
  color: #2b2b2b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inline-wear-card-count {
  flex-shrink: 0;
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Parts list inside each card. Each row stacks code+chip,
   description, and price vertically; ADD button is
   right-aligned via flex on the .inline-wear-part container. */
.inline-wear-parts {
  list-style: none;
  margin: 0;
  padding: 0;
}

.inline-wear-part {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-top: 1px solid #f5f5f5;
}

.inline-wear-part:first-child {
  border-top: none;
}

/* ===== PART-ROW THUMBNAIL =====
   Small square product/diagram image on the LEFT of each wear-part
   row, sourced from the part's sub-assembly picture (assemblyThumbs).
   Mirrors the Top-5 widget's thumbnail framing: white tile, thin
   neutral border, soft radius, image contained (never cropped). A
   neutral cube glyph stands in when the assembly has no image so the
   rows stay aligned. */
.inline-wear-part-thumb {
  flex: 0 0 auto;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  overflow: hidden;
}
.inline-wear-part-thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}
.inline-wear-part-thumb-ph {
  width: 26px;
  height: 26px;
  fill: #c8ccd0;
}
/* When the thumbnail has a real image, it's clickable and opens the
   same zoom/full-view modal the locked assembly image uses. */
.inline-wear-part-thumb.is-clickable {
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.inline-wear-part-thumb.is-clickable:hover {
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}
/* OCR found the part's balloon -> the thumb is a zoomed crop of the drawing
   (background set inline by partThumbCropStyle). Drop the padding so the crop
   fills the tile; the existing overflow:hidden + radius keep it tidy. */
.inline-wear-part-thumb.is-crop {
  padding: 0;
  background-color: #fff;
}

/* ===== HEADER-SEARCH SURFACED PART HIGHLIGHT =====
   Applied to the row whose code matches the part the user
   picked from the floating header search (see
   isHeaderHighlightPart). Soft brand tint + a bold left accent
   bar make the surfaced part unmistakable without shouting. A
   gentle one-shot pulse draws the eye on arrival; the class
   itself auto-clears after ~4s. !important on the background
   beats the row's plain white so the tint actually shows. */
@keyframes inlineWearHighlightPulse {
  0%   { background: #fde2dd; }
  100% { background: #fff4f1; }
}

.inline-wear-part.is-header-highlight {
  background: #fff4f1 !important;
  border-left: 3px solid #c0392b;
  /* Offset the 3px left bar so text doesn't shift relative to
     non-highlighted rows. */
  padding-left: 9px;
  animation: inlineWearHighlightPulse 0.9s ease-out 1;
}
/* The highlight tint is always light, so force dark text on the row's content —
   otherwise the dark/mid theme's light text is unreadable on the pink tint. The
   3-class chain outweighs the theme's `html.<mode> .desc` rules (2 classes + an
   element) without needing to know the theme class. Code stays brand red. */
.inline-wear-part.is-header-highlight .inline-wear-part-desc,
.inline-wear-part.is-header-highlight .inline-wear-part-price,
.inline-wear-part.is-header-highlight .inline-wear-part-qpm,
.inline-wear-part.is-header-highlight .inline-wear-part-item-label,
.inline-wear-part.is-header-highlight .inline-wear-part-item-val {
  color: #2a2d31 !important;
}
/* "Click here" link in the filter banner that clears the active filter. */
.inline-wear-more-hint-clear {
  color: #c0392b;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}
.inline-wear-more-hint-clear:hover {
  color: #a93222;
}

.inline-wear-part-main {
  flex: 1 1 auto;
  min-width: 0;
}

.inline-wear-part-code-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 2px;
}

.inline-wear-part-code {
  font-size: 12px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
}

.inline-wear-discount-chip {
  display: inline-block;
  padding: 1px 6px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #fff;
  background: #2e7d32;
  border-radius: 8px;
  line-height: 1.4;
}

.inline-wear-part-desc {
  font-size: 11px;
  color: #555;
  line-height: 1.3;
  margin-bottom: 2px;
  word-break: break-word;
}

.inline-wear-part-price {
  font-size: 11px;
  font-weight: 600;
  color: #2b2b2b;
}

.inline-wear-part-qpm {
  font-weight: 500;
  color: #888;
}

/* Parts.Item callout box — compact labeled value sitting just left of the
   Add button (flex-shrink:0 so it never squeezes the Add pill). */
.inline-wear-part-item {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 46px;
  padding: 3px 8px;
  background: #f7f8f9;
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  line-height: 1.1;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.inline-wear-part-item:hover {
  border-color: #c0392b;
  background: #fdecea;
}
.inline-wear-part-item-label {
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #9098a0;
}
.inline-wear-part-item-val {
  font-size: 13px;
  font-weight: 700;
  color: #2b2b2b;
}
/* Admin click-to-edit on the Item # box: the value becomes a seamless inline
   input (no border/bg of its own — the box provides the look). */
.inline-wear-part-item.is-admin-edit { cursor: pointer; }
.inline-wear-part-item.is-editing { border-color: #c0392b; background: #fdecea; }
.item-val-input {
  width: 58px;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #2b2b2b;
  text-transform: uppercase;
  padding: 0;
  outline: none;
}
.item-val-input::placeholder { color: #c9a8a8; font-weight: 600; text-transform: none; }

/* ADD button — pill style, brand-red. is-selected toggles
   to a filled "Edit" pill so users see at a glance which
   parts are already in their queue. */
.inline-wear-part-add {
  /* Cart-icon button matching the Top-5 widget's .dashboard-add-btn (request:
     "same cart that Top Parts has"): circular white tile, light-blue icon that
     fills brand red on hover — consistent across all color modes. */
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #fff;
  color: #7ec8e3;
  border: 1px solid #e3e3e3;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  cursor: pointer;
}
.inline-wear-part-add .cart-icon-svg {
  width: 15px;
  height: 15px;
}

.inline-wear-part-add:hover {
  background: #c0392b;
  color: #fff;
  border-color: #c0392b;
  text-decoration: none;
}

.inline-wear-part-add.is-selected {
  color: #2a2a2a;
}

.inline-wear-part-add.is-selected:hover {
  background: #2a2a2a;
  color: #fff;
  border-color: #2a2a2a;
}

.inline-wear-part-add.disabled {
  opacity: 0.45;
  pointer-events: none;
}

/* Admin: add a missing item # — cart-colored "+" button + inline editor. */
.inline-wear-additem-wrap { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 4px; }
.inline-wear-part-additem {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 50%;
  background: #fff; color: #7ec8e3; border: 1px dashed #7ec8e3;
  font-size: 20px; font-weight: 700; line-height: 1; cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.inline-wear-part-additem:hover { background: #c0392b; color: #fff; border-color: #c0392b; }
/* Has-item box + a Master edit pencil (edit/clear the item #). */
.inline-wear-item-box-wrap { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 4px; }
.inline-wear-item-edit {
  width: 22px; height: 22px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: #fff; color: #7ec8e3; border: 1px dashed #7ec8e3;
  font-size: 12px; line-height: 1; cursor: pointer; padding: 0;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.inline-wear-item-edit:hover { background: #c0392b; color: #fff; border-color: #c0392b; }
.additem-input {
  width: 130px; padding: 4px 6px; font-size: 12px; font-weight: 700;
  text-transform: uppercase; color: #16181b; background: #fff;
  border: 1px solid #7ec8e3; border-radius: 5px;
}
.additem-save, .additem-cancel {
  width: 24px; height: 24px; border-radius: 50%; border: none; cursor: pointer;
  font-size: 12px; line-height: 1; padding: 0; color: #fff;
}
.additem-save { background: #2e7d32; }
.additem-save:disabled { background: #9aa7b0; cursor: default; }
.additem-cancel { background: #6c757d; }
.additem-error { color: #c0392b; font-weight: 700; cursor: help; padding: 0 2px; }

/* ===== HEADER-SEARCH RESULT BANNER =====
   Sits at the very top of the right-column .insights-panel,
   above the staging tray (.insights-selected-tray) and the
   Frequently Ordered section. Renders only when the user
   arrived here via a part-suggestion click in the floating
   header search bar (Head.vue → selectOption → router.push).

   Visual language is intentionally close to
   .insights-selected-tray — red left border on a white card
   with a soft red shadow — but the accent color and icon are
   tuned to read as "search context" rather than "queued
   parts". Same vertical rhythm (margin-bottom: 16px) so the
   stack reads cleanly when both the banner and the staging
   tray are visible.

   Layout: [icon][body grows][× clear button], all in a
   single row. Body is the column that absorbs extra width;
   icon and clear button stay fixed-size on the ends. */
.header-search-result {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 12px 12px 14px;
  background: #fff;
  border: 1px solid #e7d6a6;
  border-left: 4px solid #c0392b;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(192, 57, 43, 0.08);
  position: relative;
}

.header-search-result-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #c0392b;
  color: #fff;
  border-radius: 50%;
  flex-shrink: 0;
  /* Nudge down to optically center against the title row,
     which sits a hair below the box's top padding. */
  margin-top: 1px;
}

.header-search-result-body {
  flex: 1 1 auto;
  min-width: 0; /* allows long part codes to ellipsize */
}

.header-search-result-title {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  line-height: 1.3;
}

.header-search-result-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #c0392b;
}

.header-search-result-code {
  font-size: 14px;
  font-weight: 700;
  color: #222;
  word-break: break-word;
}

.header-search-result-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #6b6b6b;
  line-height: 1.35;
  word-break: break-word;
}

/* Match the × button's visual weight to the staging tray's
   row-remove buttons so the page reads as one coherent set
   of dismissible items. */
.header-search-result-clear {
  border: none;
  background: transparent;
  color: #999;
  font-size: 22px;
  line-height: 1;
  padding: 0 4px;
  cursor: pointer;
  flex-shrink: 0;
  /* Pull the × up to optically align with the title row
     (button has its own line-box and the default flex
     baseline puts it slightly low). */
  margin-top: -2px;
  transition: color 120ms ease;
}

.header-search-result-clear:hover,
.header-search-result-clear:focus {
  color: #c0392b;
  outline: none;
}

/* ===== DEFENSIVE OVERRIDE — .insights-panel WRAPPER NEUTRALIZER =====
   The bare <div class="insights-panel"> wrapper that USED to wrap the
   right-column content (Your Cart + Frequently Ordered) has been
   removed from the template above. This rule exists as belt-and-
   suspenders insurance — if for any reason an .insights-panel element
   ends up in the DOM (stale build cache holding old compile, alternate
   copy of this file in a separate dist, browser cache, etc.), the
   visual card styling will be force-neutralized here so the outer
   "double card" effect can't reappear.

   Scoped to #createOrder so this can't affect Account/Index.vue or
   anywhere else that might use a similar class name in the future.
   Uses !important on every property to defeat any rule with equal or
   higher specificity that might otherwise win the cascade — including
   scoped-CSS [data-v-XXXX] attribute selectors.

   The .insights-panel-* descendant selectors (-header, -title,
   -controls, -toggle, -search, -sub) are NOT affected — those are
   different selectors entirely and continue to style Frequently
   Ordered's internal structure. */
#createOrder .insights-panel {
  background: transparent !important;
  border: 0 !important;
  border-top: 0 !important;
  border-right: 0 !important;
  border-bottom: 0 !important;
  border-left: 0 !important;
  border-radius: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  box-shadow: none !important;
}

/* Frequently Ordered card frame FQ-MARKER-2026-05-20 */
.frequently-ordered-card {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  padding: 18px 20px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-top: 16px;
}

</style>

<!-- ===== PO EXPLAINER MODAL =====
     b-modal teleports its DOM to document.body, which means scoped
     styles in this component can't reach it. This non-scoped block
     is intentionally global, but namespaced via .po-explainer-modal
     so it only affects this one modal — not every b-modal on the
     site. Keeps the explainer body readable, the input full-width,
     and the action buttons consistent with the rest of the page. -->
<style>
.po-explainer-modal .modal-body {
  padding: 18px 20px;
}

.po-explainer-modal .po-explainer-text {
  margin: 0 0 18px;
  font-size: 13px;
  line-height: 1.55;
  color: #444;
}

.po-explainer-modal .po-modal-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  margin: 0 0 6px;
}

.po-explainer-modal .po-modal-input {
  display: block;
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #222;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.po-explainer-modal .po-modal-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.12);
}

.po-explainer-modal .modal-footer .btn {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 12px;
  padding: 8px 18px;
}

/* ===== PO PICKER MODAL =====
   Shown when the queue already has Orders. Each option is a
   stacked button — Order #N + PO info on top, count beneath.
   "New PO Number" option uses a dashed border to differentiate
   from existing-order picks. */
.po-picker-modal .modal-body {
  padding: 18px 20px;
}

.po-picker-modal .po-picker-text {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.5;
  color: #555;
}

/* Edit-mode hint: shown above the explainer text to remind the
   user where the part currently lives. Soft yellow chip to match
   the tray's order-group accent. */
.po-picker-modal .po-picker-current {
  margin: 0 0 12px;
  padding: 8px 12px;
  background: #fffaf0;
  border: 1px solid #f3e3b8;
  border-radius: 4px;
  font-size: 12px;
  color: #5a4716;
}

.po-picker-modal .po-picker-current strong {
  color: #c0392b;
}

.po-picker-modal .po-picker-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.po-picker-modal .po-picker-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  padding: 12px 16px;
  background: #fff;
  border: 1.5px solid #c0392b;
  border-radius: 6px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.po-picker-modal .po-picker-option:hover {
  background: #c0392b;
  color: #fff;
}

.po-picker-modal .po-picker-option-num {
  font-size: 14px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.2px;
  text-transform: uppercase;
}

.po-picker-modal .po-picker-option:hover .po-picker-option-num {
  color: #fff;
}

.po-picker-modal .po-picker-option-meta {
  font-size: 12px;
  color: #777;
}

.po-picker-modal .po-picker-option:hover .po-picker-option-meta,
.po-picker-modal .po-picker-option:hover .po-picker-option-meta strong {
  color: #fff;
}

.po-picker-modal .po-picker-option-meta strong {
  color: #444;
}

.po-picker-modal .po-picker-option-new {
  border-style: dashed;
  border-color: #888;
}

.po-picker-modal .po-picker-option-new .po-picker-option-num {
  color: #444;
}

.po-picker-modal .po-picker-option-new:hover {
  background: #444;
  border-color: #444;
}

.po-picker-modal .po-picker-option-new:hover .po-picker-option-num {
  color: #fff;
}

/* Remove option — destructive, red theme to match the cart's
   destructive actions elsewhere on the site. Visually separated
   from the safe options above by the dashed border on the New PO
   option that precedes it. */
.po-picker-modal .po-picker-option-remove {
  border-color: #c0392b;
  background: #fff;
  margin-top: 4px;
}

.po-picker-modal .po-picker-option-remove .po-picker-option-num {
  color: #c0392b;
}

.po-picker-modal .po-picker-option-remove:hover {
  background: #c0392b;
  border-color: #c0392b;
}

.po-picker-modal .po-picker-option-remove:hover .po-picker-option-num,
.po-picker-modal .po-picker-option-remove:hover .po-picker-option-meta {
  color: #fff;
}

.po-picker-modal .modal-footer .btn {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 12px;
  padding: 8px 18px;
}

/* ===== MACHINE IMAGE + ZOOM MODAL =====
   Image is constrained to its column width so it stays compact in
   the Step 2 panel. Modal CSS is ported verbatim from
   ShopWearParts.vue so the zoom UX is consistent. */
.machine-image-wrap {
  display: flex;
  justify-content: center;
  margin-top: 4px;
  /* In-place hover-zoom needs overflow:hidden so the scaled image
     stays clipped to the wrap (otherwise it would bleed out and
     visually "jump" over neighboring layout). position:relative
     is defensive — keeps the wrap as a positioning context if any
     overlays (loaders, badges) are added later. */
  overflow: hidden;
  position: relative;
}

.machine-preview-img {
  width: 100%;
  max-width: 360px;
  height: auto;
  max-height: 240px;
  cursor: zoom-in;
  object-fit: contain;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
  background: #fff;
  /* In-place zoom: short transition makes hover-in / hover-out
     feel polished without lagging when the cursor moves quickly
     within the image. transform-origin is set inline by
     inPlaceZoomMove() — defaults to center via the data property. */
  transition: transform 0.1s ease-out;
  will-change: transform;
}

/* Hover state — scale up the image; transform-origin (inline,
   driven by mousemove) decides which area becomes the focal point.
   2x balances "shows enough detail" with "still see enough of the
   image at once". Bump higher if customers find it too modest;
   the scale here is the only number to change. */
.machine-image-wrap:hover .machine-preview-img,
.machine-image-wrap.is-zoom-locked .machine-preview-img {
  transform: scale(4);
}

/* ===== Clickable balloon hotspots over the docked diagram =====
   Layer is absolutely positioned to the img's box (calloutLayerStyle) and
   mirrors the img's zoom transform so hotspots track hover/lock zoom. It's
   pointer-events:none; only the hotspots opt back in, so hover-zoom on the
   rest of the image is unaffected. */
.callout-hotspot-layer {
  position: absolute;
  pointer-events: none;
  z-index: 2;
  transition: transform 0.1s ease-out;
  will-change: transform;
}
/* Mirror the img's hover-zoom (scale 4) when NOT locked; the locked scale is
   applied inline (calloutLayerStyle) to match lockedZoom. */
.machine-image-wrap:hover .callout-hotspot-layer:not(.is-zoom-locked) {
  transform: scale(4);
}
.callout-hotspot {
  position: absolute;
  margin: 0;
  padding: 0;
  /* Barely-there ring so the balloons read as clickable without competing
     with the printed number; it strengthens on hover. */
  border: 1px solid rgba(192, 57, 43, 0.14);
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  pointer-events: auto;
  -webkit-appearance: none;
  appearance: none;
  transition: border-color 0.12s ease, background 0.12s ease;
}
.callout-hotspot:hover {
  border-color: #c0392b;
  background: rgba(192, 57, 43, 0.16);
}

/* ===== Master-only balloon editor (runs in the zoom MODAL) ===== */
/* Crosshair over the modal image while editing (click adds a bubble). */
.diagram-zoom-wrap.is-balloon-editing { cursor: crosshair; }

/* Let the modal control bar wrap once the edit controls appear. */
/* Modal control bar: editing aids on one line, then a SINGLE row of buttons. */
.custom-image-controls { flex-direction: column; align-items: stretch; justify-content: center; }
.custom-image-controls .cic-editrow {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px; width: 100%;
}
.custom-image-controls .cic-btnrow {
  display: flex; flex-direction: row; flex-wrap: nowrap;
  align-items: center; justify-content: center; gap: 8px;
}
.custom-image-controls .cic-btnrow .btn {
  flex: 0 0 auto; width: auto; white-space: nowrap;
}
.custom-image-controls .bee-hint {
  flex: 1 1 auto;
  min-width: 120px;
  font-size: 11px;
  line-height: 1.25;
  color: #555;
  text-align: left;
}
.custom-image-controls .bee-sel { display: inline-flex; align-items: center; gap: 5px; }
.custom-image-controls .bee-code-input {
  width: 120px;
  padding: 5px 8px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  color: #1a1c1f;
  background: #fff;
  border: 2px solid #c0392b;
  border-radius: 5px;
}
.custom-image-controls .bee-del {
  width: 26px; height: 26px;
  font-size: 13px; color: #fff;
  background: #6c757d; border: none; border-radius: 50%; cursor: pointer; padding: 0;
}
.custom-image-controls .bee-del:hover { background: #c0392b; }
.custom-image-controls .bee-error { flex: 1 1 100%; font-size: 12px; color: #c0392b; }

/* Balloon markers in the modal edit layer.
   OCR balloons: read-only (faint blue) with a hide toggle. */
.callout-edit-ocr {
  position: absolute;
  display: flex; align-items: center; justify-content: center;
  border: 1.5px dashed rgba(41, 128, 185, 0.75);
  border-radius: 50%;
  background: rgba(41, 128, 185, 0.1);
  pointer-events: auto;
}
.callout-edit-ocr.is-hidden {
  border-color: rgba(120, 124, 128, 0.6);
  background: rgba(120, 124, 128, 0.12);
  opacity: 0.5;
}
.callout-edit-ocr .cee-code { font-size: 10px; font-weight: 700; color: #1f6391; pointer-events: none; }
.callout-edit-ocr.is-hidden .cee-code { text-decoration: line-through; color: #777; }
.callout-edit-ocr .cee-hide {
  position: absolute; top: -9px; right: -9px;
  width: 18px; height: 18px; font-size: 11px; line-height: 1;
  color: #fff; background: #6c757d; border: 1px solid #fff; border-radius: 50%; cursor: pointer; padding: 0;
}
.callout-edit-ocr .cee-hide:hover { background: #c0392b; }

/* Admin balloons — editable (draggable / selectable / resizable). */
.callout-edit-admin {
  position: absolute;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #c0392b;
  border-radius: 50%;
  background: rgba(192, 57, 43, 0.18);
  cursor: move;
  pointer-events: auto;
}
.callout-edit-admin.is-selected {
  border-color: #2e7d32;
  background: rgba(46, 125, 50, 0.22);
  box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.45);
}
.callout-edit-admin .cea-code { font-size: 10px; font-weight: 800; color: #c0392b; pointer-events: none; }
.callout-edit-admin.is-selected .cea-code { color: #226024; }
.callout-edit-admin .cea-resize {
  position: absolute; bottom: -10px; right: -10px;
  width: 20px; height: 20px; font-size: 12px; line-height: 1;
  color: #fff; background: #2e7d32; border: 1px solid #fff; border-radius: 50%; cursor: nwse-resize; padding: 0;
  display: flex; align-items: center; justify-content: center;
}

/* When the locked image is teleported INTO the floating stack (portal), it
   docks as a normal panel at the TOP of the stack — neutralize the fixed-
   overlay positioning so it flows in the stack above the cart. The base
   .is-zoom-locked rule (fixed + --lock-* coords) still applies anywhere the
   wrap is NOT inside the stack. */
.floating-widget-stack .machine-image-wrap.is-zoom-locked {
  position: relative !important;
  top: auto !important;
  left: auto !important;
  width: 100% !important;
  height: 320px;
  /* CRITICAL: the stack is a flex column with a capped max-height. Without
     flex-shrink:0 the docked panel gets squeezed toward its near-zero
     min-content (the toolbar + zoom buttons are position:absolute, so they
     add no height) and collapses to just its 3px red top border — the
     "red line" bug. Pin it to a non-shrinking 320px panel; the stack
     scrolls (overflow-y:auto) when the cart + Top 5 + image don't all fit. */
  flex: 0 0 auto;
  z-index: auto !important;
  margin-top: 0;
}
.floating-widget-stack .machine-image-wrap.is-zoom-locked .machine-preview-img {
  max-height: 320px;
}

/* When locked (single-click on image, see onImageClick), the
   wrap switches to sticky positioning so the zoomed view stays
   visible at the top of the viewport as the user scrolls down
   through the parts list. Sticky preserves the wrap's original
   document-flow position too — unlocking simply restores the
   natural layout with no jump. z-index keeps the wrap above
   subsequent form sections that scroll past underneath. */
.machine-image-wrap.is-zoom-locked {
  /* position: fixed (not sticky) so the locked panel can sit
     over the right column's .insights-panel area regardless
     of where the wrap lives in the document. Sticky would
     constrain to the wrap's natural ancestor; fixed gives
     us free placement based on the inline geometry vars below. */
  position: fixed;
  /* All three coordinates are set inline by onImageClick via
     CSS custom properties, captured from .insights-panel's
     bounding rect at lock-activation time:
       --lock-top    = panel's top edge in viewport
       --lock-left   = panel's left edge in viewport
       --lock-width  = panel's width
     This makes the locked panel adopt the right column's exact
     geometry — same position, same width as Frequently Ordered.
     Fallbacks (10px / auto / auto) keep the panel visible
     somewhere reasonable if the measurement fails. */
  top: var(--lock-top, 10px);
  left: var(--lock-left, auto);
  width: var(--lock-width, auto);
  /* Float ABOVE the global floating cart/Top-5 stack (z 8500) and the
     pill rail (z 9000) — the locked image pins into the right column at
     the cart's position, so a lower z-index hid it behind the cart
     ("the image disappears on click"). 9500 keeps it on top. */
  z-index: 9500;
  /* Heavy visual treatment so the locked panel reads as a
     distinct floating element. 3px brand-red border on the
     wrap (img border would be clipped by overflow:hidden),
     opaque white background to mask any transparent regions
     in the source PNG, drop shadow for elevation. */
  background: #fff;
  /* Match the content cards EXACTLY: a 3px brand-red line on TOP only, an
     8px radius, and a drop shadow for elevation — NO full neutral side/bottom
     border. A full 1px side border traced the rounded corner and let the
     black page show through the corner triangle (worst in dark/mid mode);
     dropping it gives the same clean, soft corner the left content card
     (.create-order-form) and cart panel have. overflow:hidden on the base
     .machine-image-wrap clips the zoomed image to the rounded corners. */
  border-top: 3px solid #c0392b;
  border-radius: 8px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
  /* Margin-top stripped — position:fixed removes the wrap
     from document flow, but explicitly zeroing keeps the
     rendered box flush with the inline top coordinate. */
  margin-top: 0;
}

/* Locked-state image styling.
   - Crosshair cursor signals "anchored at this point — click to
     release the lock".
   - max-width: 100% lets the wrap fill the available column
     width when locked (so the visible window into the zoomed
     image is wide).
   - max-height is computed adaptively as
     calc(100vh - var(--lock-top, 100px) - 40px):
       * 100vh = total viewport height
       * minus var(--lock-top) = subtract the top offset where
         the wrap is pinned (set inline by onImageClick from
         the measured anchor element's viewport bottom)
       * minus 40px = breathing room at the very bottom of the
         viewport so the locked image isn't flush against the
         viewport's bottom edge
     Without the calc, a fixed 70vh combined with a non-trivial
     top offset would push the image's bottom below the viewport
     and cause clipping. The calc approach guarantees the image
     always fits inside the visible window regardless of how
     far down the anchor pins it.
   - object-fit: contain is inherited from the base rule. */
.machine-image-wrap.is-zoom-locked .machine-preview-img {
  max-width: 100%;
  max-height: calc(100vh - var(--lock-top, 100px) - 40px);
  /* cursor: grab (open hand) signals "this can be dragged" —
     the universal hand-grab convention. Replaces the previous
     'cursor: move' which rendered as 4-way arrows and tested
     poorly in user feedback. While dragging in either mode
     the cursor flips to 'grabbing' (closed fist) via the
     mode-specific class rules below. */
  cursor: grab;
  /* Block text selection during drag — without this, dragging
     across the image often selects whatever text is behind /
     adjacent and creates a distracting blue highlight that
     follows the cursor. Applied to both wrap and img to cover
     both pointer-event targets. */
  user-select: none;
  -webkit-user-select: none;
}
.machine-image-wrap.is-zoom-locked {
  user-select: none;
  -webkit-user-select: none;
}

/* Both drag modes (panning inside OR moving panel) show the
   closed-fist 'grabbing' cursor. The two modes are
   functionally distinct but visually share one drag-state
   cursor — mirrors the OS-native dragging cue users expect.
   Applied on the wrap and img so the cursor stays consistent
   regardless of which element the cursor is over. */
.machine-image-wrap.is-zoom-locked.is-panning-internal,
.machine-image-wrap.is-zoom-locked.is-panning-internal .machine-preview-img,
.machine-image-wrap.is-zoom-locked.is-dragging,
.machine-image-wrap.is-zoom-locked.is-dragging .machine-preview-img {
  cursor: grabbing;
}

/* ===== LOCKED-PANEL CLOSE BUTTON =====
   Small circular × in the panel's top-right corner. Visible
   only when the panel is locked (template-gated via v-if).
   Sized for easy click target without dominating the panel
   visually. Brand-red border matches the wrap border for
   visual cohesion. */
/* Toolbar holding the close (×) and collapse (→) buttons in the locked
   panel's top-right corner. Flex row anchored to the right edge, so the
   collapse arrow sits to the RIGHT of the ×. */
.locked-toolbar {
  /* Positioned flex toolbar in the panel's top-right corner. The two
     buttons are NORMAL flex children (not absolute), so they always sit
     side by side with the gap. row-reverse puts the × at the corner
     (conventional close position) with the collapse → to its left.
     z-index lifts the toolbar above the 4x-scaled image. */
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 6px;
  z-index: 20;
}
/* Scoped under .locked-toolbar so these out-specify the OLD global
   `.locked-close-btn { position:absolute }` rules that Orders/Create.vue and
   RFQ/Create.vue still ship (unscoped) — otherwise that leftover forces this
   page's × back to absolute and it overlaps the →. */
.locked-toolbar .locked-close-btn,
.locked-toolbar .locked-collapse-btn {
  position: static;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #c0392b;
  /* Red background + white glyph so each reads as a clear red dot in the
     panel corner. */
  background: #c0392b;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  padding: 0;
  /* Override the wrap's grab cursor — these are click targets, not drag
     handles. */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.locked-collapse-btn svg {
  width: 18px;
  height: 18px;
}
/* Hover inverts to white-on-red — visual confirmation that the cursor is
   on a clickable target. */
.locked-toolbar .locked-close-btn:hover,
.locked-toolbar .locked-collapse-btn:hover {
  background: #fff;
  color: #c0392b;
}
/* −/+ zoom controls, bottom-center of the docked image. */
.locked-zoom-controls {
  position: absolute;
  bottom: 6px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  z-index: 20;
}
.locked-zoom-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #c0392b;
  background: #c0392b;
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.locked-zoom-btn:hover {
  background: #fff;
  color: #c0392b;
}
/* Parked as the side pill → hide the in-page locked overlay (the pill in
   the rail stands in for it until the user clicks to restore). */
.machine-image-wrap.lock-collapsed {
  display: none !important;
}

/* Loading-state wrappers for image fetches and the parts
   accordion. Shared visual language: small spinner + label,
   centered, gray text — matches the muted nature of "loading"
   states without competing with primary content. */
.machine-image-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 60px;
  width: 100%;
  max-width: 360px;
  color: #666;
  font-size: 13px;
}

.inline-wear-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 12px;
  color: #666;
  font-size: 14px;
}

.custom-image-modal {
  position: fixed;
  z-index: 99999;
  inset: 0;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.custom-image-modal-inner {
  background: #fff;
  width: 95%;
  max-width: 1400px;
  height: 90vh;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.custom-image-close {
  position: absolute;
  top: 10px;
  right: 14px;
  z-index: 2;
  border: none;
  background: transparent;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
}

.diagram-zoom-wrap {
  flex: 1;
  overflow: hidden;
  position: relative;
  cursor: grab;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8f8f8;
}

.diagram-zoom-wrap:active {
  cursor: grabbing;
}

.imagepreview {
  max-width: 95%;
  max-height: 95%;
  user-select: none;
  -webkit-user-drag: none;
  transition: transform 0.05s linear;
}

.custom-image-controls {
  padding: 12px 16px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  border-top: 1px solid #ddd;
  background: #fff;
}

/* ===== MACHINE DROPDOWN FOOTER ("Don't see your machine?") =====
   Sits inside the Semantic UI dropdown's .menu, after the
   data items. The dropdown library would normally treat
   any clicked element inside .menu as a value-pick;
   `data-no-select` plus `@click.stop` on the inner
   buttons keeps that behavior contained and lets our own
   handlers run. */
.dropdown-footer-divider {
  border-top: 1px solid #e6e6e6;
  margin: 4px 0 0;
}

.dropdown-footer-help {
  /* Lift the visual weight slightly so this block reads as
     "support content" rather than another option. The Semantic
     UI items above are flat-white; this gets a tinted
     background to draw the eye. */
  background: #fffbe8;
  padding: 14px 18px;
  cursor: default;
  /* Semantic UI dropdown items default to white-space:nowrap, which the
     narrower assembly picker inherited — clipping the help text ("…isn't on
     this mac[hine]"). Force normal wrapping so the blurb flows onto multiple
     lines instead of being cut off. */
  white-space: normal;
  overflow-wrap: anywhere;
}

.dropdown-footer-help:hover {
  /* Override Semantic UI's .menu > * hover state so the
     footer doesn't look "selectable." */
  background: #fffbe8 !important;
}

.dropdown-footer-content {
  font-size: 13px;
  color: #444;
}

.dropdown-footer-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 14px;
  color: #222;
}

.dropdown-footer-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #c0392b;
  color: #fff;
  border-radius: 50%;
  font-weight: 700;
  font-size: 13px;
  line-height: 1;
  flex-shrink: 0;
}

.dropdown-footer-blurb {
  margin: 0 0 10px;
  line-height: 1.4;
  color: #555;
}

.dropdown-footer-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.dropdown-footer-action {
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  background-color: #c0392b !important;
  color: #fff !important;
  border-color: #c0392b !important;
}
.dropdown-footer-action:hover,
.dropdown-footer-action:focus {
  background-color: #a02d20 !important;
  border-color: #a02d20 !important;
  color: #fff !important;
}

/* ===== PARTS INQUIRY MODAL =====
   Internal layout — the modal frame itself is Bootstrap-Vue
   default styling. */
.parts-inquiry-intro {
  margin: 0 0 14px;
  font-size: 13px;
  color: #555;
}

.parts-inquiry-form .form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.parts-inquiry-form .form-row > [class*="col-"] {
  flex: 1 1 0;
  min-width: 200px;
  padding: 0;
}

.parts-inquiry-form label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.parts-inquiry-form .form-control,
.parts-inquiry-form input[type="file"] {
  width: 100%;
  font-size: 14px;
}

/* ===== ADD TO CART MODAL (Search page combined Order/Quote modal) ===== */
.add-to-cart-modal .modal-dialog {
  max-width: 480px;
}
.add-to-cart-body {
  padding: 0.25rem 0.25rem 0;
}
.add-to-cart-lede {
  font-size: 0.95rem;
  margin-bottom: 1.25rem;
  color: #333;
}
.add-to-cart-partcode {
  color: #c8102e;
  font-weight: 700;
}
.add-to-cart-section-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #555;
  margin-bottom: 0.5rem;
  margin-top: 1rem;
}
.add-to-cart-destination {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}
.add-to-cart-dest-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 0.5rem;
  border: 2px solid #d6d6d6;
  border-radius: 6px;
  background: #fff;
  color: #222;
  cursor: pointer;
  transition: all 0.12s ease-in-out;
  text-align: center;
  min-height: 70px;
}
.add-to-cart-dest-btn:hover {
  border-color: #b3b3b3;
}
.add-to-cart-dest-btn.is-active {
  background: #c8102e;
  border-color: #c8102e;
  color: #fff;
}
.add-to-cart-dest-title {
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
/* "✓ in cart" badge on a destination tile when the part is already there. */
.add-to-cart-dest-badge {
  display: inline-block;
  margin-left: 6px;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  text-transform: none;
  color: #1f6e23;
  background: #e6f4e6;
  border: 1px solid #bfe0bf;
  border-radius: 9px;
  padding: 1px 6px;
  vertical-align: middle;
}
.add-to-cart-dest-sub {
  font-size: 0.75rem;
  font-weight: 400;
  margin-top: 0.15rem;
  opacity: 0.85;
}
.add-to-cart-helper {
  font-size: 0.8rem;
  color: #777;
  margin-top: 0.4rem;
  font-style: italic;
}
.add-to-cart-qty-input {
  max-width: 100px;
}
.add-to-cart-remove {
  margin-top: 1.25rem;
  text-align: center;
  font-size: 0.85rem;
}
.add-to-cart-remove a {
  color: #c8102e;
  text-decoration: none;
}
.add-to-cart-remove a:hover {
  text-decoration: underline;
}

/* ===== PO PICKER (existing-group buttons inside the modal) ===== */
.add-to-cart-po-picker {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}
.add-to-cart-po-pick-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.7rem 1rem;
  border: 2px solid #d6d6d6;
  border-radius: 6px;
  background: #fff;
  color: #222;
  cursor: pointer;
  transition: all 0.12s ease-in-out;
  text-align: left;
  width: 100%;
}
.add-to-cart-po-pick-btn:hover {
  border-color: #b3b3b3;
  background: #fafafa;
}
.add-to-cart-po-pick-btn.is-active {
  border-color: #c8102e;
  background: #fdf0f2;
}
.add-to-cart-po-pick-btn-num {
  font-weight: 700;
  font-size: 0.95rem;
  color: #222;
}
.add-to-cart-po-pick-btn-meta {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.2rem;
}
.add-to-cart-po-pick-btn.is-active .add-to-cart-po-pick-btn-meta {
  color: #555;
}
/* "+ New PO Number" button — visually distinct from existing-PO
   options so users can see at a glance that it's a different
   action (open input vs select one of the existing). */
.add-to-cart-po-pick-btn-new {
  border-style: dashed;
  align-items: center;
}
.add-to-cart-po-pick-btn-new .add-to-cart-po-pick-btn-num {
  color: #c8102e;
}
.add-to-cart-po-pick-btn-new.is-active {
  border-style: solid;
}

/* ===== STAGING TRAY — QUOTE SECTION ===== */
.insights-selected-title-quote {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e6e6e6;
}
.insights-quote-group .insights-order-group-num {
  color: #555;
}

/* ==========================================================
   STAGING TRAY WRAPPER — matches the "Your Cart" presentation
   on Index_-_Account.vue. Wraps the existing
   .insights-selected-tray inside a card with a header
   ("YOUR CART") and gives the bottom-of-tray Continue
   button consistent styling across the three cart-driven
   pages (Search, Create-Order, Create-RFQ).
   ========================================================== */
.staging-tray-wrapper {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 14px 14px 12px;
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}
.staging-tray-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}
.staging-tray-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #c0392b;
}
.staging-tray-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #c0392b;
}
.staging-tray-body {
  flex: 1;
}
.staging-continue-btn {
  width: 100%;
  margin-top: 10px;
  padding: 9px 12px;
  background: #c0392b;
  color: #fff;
  border: 0;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: background 0.15s ease;
}
.staging-continue-btn:hover {
  background: #a3301f;
}
.staging-continue-btn-quote {
  background: #444;
}
.staging-continue-btn-quote:hover {
  background: #222;
}
</style>